<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="backButtonRoute ? true : false"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
    :tabs="getTabs"
    :selectedIndex="selectedIndex"
    ref="gc-base-layout"
    class="touch-sales-order-layout sales-order-layout"
  >
    <slot> </slot>
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Sales Order'"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="
          baseView.mode == 'create'
            ? 'Create Sales Order'
            : 'Sales Order # ' + baseView.id
        "
        :secondaryLabel="
          baseView.mode == 'review' ? model.CustomerName : undefined
        "
        v-if="loaded"
      />
    </template>
    <div class="css-v2 transaction-screen" v-if="loaded">
      <div class="gc-header-toolbar">
        <gc-form-header-toolbar
          :toolbar-options="getHeaderToolbar"
          :data-model="loaded ? toolbarModel : {}"
          :itemSaveCallBack="checkHeaderFormValidation"
          :backRoute="backButtonRoute"
          :setLoading="showLoading"
          ref="gc-form-header-toolbar"
          :baseView="baseView"
        ></gc-form-header-toolbar>
      </div>

      <div class="gc-header-form" v-show="!gridExpand">
        <div class="header-form-container">
          <div class="header-form">
            <div class="view-card responsive-paddings">
              <gc-form
                :formOptions="{
                  gcLabelLocation: 'left',
                }"
                :formItems="getHeaderFormItems"
                :isNormal="true"
                :data-model="loaded ? model : {}"
                :deliveryAddressClicked="showDeliveryAddress"
                :formMode="baseView.mode"
                :fromFrom="'gc-header-form'"
                ref="gc-header-form"
                :valueChangeCallBack="headerFormValueChanged"
                :checkValid="isValid"
                :validationGroup="'gc-header-form'"
                :scrollingEnabled="false"
                :formContentReady="onHeaderFormContentReady"
                :formHeight="canEditHeaderDiscount ? 175 : 155"
              ></gc-form>
            </div>
          </div>
          <div class="header-comments">
            <div class="view-card responsive-paddings">
              <gc-form
                :formOptions="{
                  gcLabelLocation: 'left',
                }"
                :formItems="commentPopUpItems"
                :isNormal="true"
                :data-model="loaded ? orderCommentModel : {}"
                :deliveryAddressClicked="undefined"
                :formMode="baseView.mode"
                :fromFrom="'gc-header-comments'"
                ref="gc-header-comments"
                :valueChangeCallBack="headerFormValueChanged"
                :checkValid="isValid"
                :validationGroup="'gc-header-comments'"
                :scrollingEnabled="true"
                :formHeight="canEditHeaderDiscount ? 175 : 155"
              ></gc-form>
            </div>
          </div>
        </div>
      </div>

      <div class="gc-details-grid">
        <gc-filter-split-view
          :showLeftContainer="showLeftContainer"
          :showRightContainer="showRightContainer"
          :rightContainerWidth="250"
        >
          <template #leftContent>
            <dx-data-grid
              @toolbar-preparing="toolbarPreparing($event)"
              :columns="getColumns"
              :data-source="getDataSource"
              :height="getGridHeight"
              @cell-prepared="onCellPrepared"
              :ref="'dx-data-grid-comp'"
              :key-expr="'ID'"
              :key="soDetailsCurrentView"
              @content-ready="onContentReady"
              @editor-preparing="onEditorPreparing"
              :twoWayBindingEnabled="false"
              @option-changed="onOptionChanged"
              @editing-start="onEditingStart"
              :columnChooser="{
                enabled: false,
              }"
              :repaint-changes-only="true"
              @initialized="onInitialized"
            >
              <DxLoadPanel :show-indicator="false" :enabled="false" />
              <DxPaging :page-size="50" :enabled="false" />
              <DxPager :visible="false" />

              <DxScrolling
                :mode="'virtual'"
                :row-rendering-mode="'virtual'"
                :preload-enabled="true"
                :renderAsync="false"
                :useNative="false"
              />

              <DxEditing
                :allow-updating="true"
                :allow-deleting="false"
                mode="batch"
                :new-row-position="'last'"
                :use-icons="true"
                :changes="changes"
                :select-text-on-edit-start="true"
                :refreshMode="'reshape'"
              />

              <DxStateStoring
                :enabled="true"
                :storage-key="getPersistenceKey"
                type="localStorage"
              />

              <template #item-cell-template="{ data }">
                <div class="custom-auto-complete" @click="clickOnInfo(data)">
                  <span class="auto-complete-primary">
                    {{ data.data.Description }} </span
                  ><br />
                  <span class="record-secondary gc-grid-code">
                    {{ data.data.OriginItemCode }}
                  </span>
                </div>
              </template>

              <template #item-cell-template-code="{ data }">
                <div class="custom-auto-complete" @click="clickOnInfo(data)">
                  {{ data.data.OriginItemCode }}
                </div>
              </template>

              <template #item-cell-template-description="{ data }">
                <div class="custom-auto-complete" @click="clickOnInfo(data)">
                  {{ data.data.Description }}
                </div>
              </template>

              <template #sales-um-edit-cell-template="{ data: cellInfo }">
                <div
                  v-bind:class="
                    cellInfo.data.SLUMLength < 2 ? 'cell-disabled' : 'sales-um'
                  "
                >
                  <DxSelectBox
                    :value="cellInfo.value"
                    :items="cellInfo.data.SalesUMs"
                    @initialized="onSalesUMInitialized"
                    @value-changed="onSalesUMRowClick($event, cellInfo)"
                    @focus-in="onSalesUMFocusIn($event, cellInfo)"
                    @key-down="onSalesUMKeyDown($event, cellInfo)"
                    valueExpr="SalesUM"
                    displayExpr="SalesUM"
                    :drop-down-options="{ width: 250 }"
                    :readOnly="cellInfo.data.SLUMLength < 2"
                    :tabIndex="0"
                  >
                  </DxSelectBox>
                </div>
              </template>

              <template #available-cell-template="{ data }">
                <div>
                  <span
                    v-if="data.value < 0"
                    class="hyper-link"
                    @click="visibleStockLevel(data.data)"
                    >{{ data.text }}</span
                  >
                  <span
                    v-else
                    class="hyper-link color"
                    @click="visibleStockLevel(data.data)"
                  >
                    {{ data.text }}</span
                  >
                </div>
              </template>

              <template #menu-cell-template="{ data }">
                <DxDropDownButton
                  :items="[
                    { menuText: 'Line Comment', data: data },
                    { menuText: 'Item History', data: data },
                    {
                      menuText: 'GP Tool',
                      data: data,
                      visible: baseView.mode != 'review' && gpToolVisible,
                    },
                    {
                      menuText: 'Copy Line',
                      data: data,
                      visible: baseView.mode != 'review',
                    },
                    {
                      menuText: 'Price Breakdown',
                      data: data,
                      visible:
                        baseView.mode != 'create' &&
                        priceBreakdownVisible &&
                        data.data.SalesUMQty != 0,
                    },
                    {
                      menuText: 'Update Item',
                      data: data,
                      visible:
                        baseView.mode != 'review' &&
                        updateItemVisible &&
                        (data.data.ItemCode === DummyProductItemCode ||
                          data.data.OriginItemCode === DummyProductItemCode ||
                          data.data.IsDummyRecord === true),
                    },
                  ]"
                  keyExpr="menuText"
                  displayExpr="menuText"
                  :drop-down-options="{ width: 250 }"
                  class="gc-dropdown-icon-button"
                  :id="data.data.ID"
                  :icon="'overflow'"
                  styling-mode="text"
                  :show-arrow-icon="false"
                  :onItemClick="onSoDetailsLineContextMenuItemClick"
                  :tabIndex="-1"
                />
              </template>
            </dx-data-grid>
          </template>
          <template #rightContent>
            <div
              class="advance-filter-panel"
              :key="'fpanel-' + soDetailsCurrentView"
            >
              <DxToolbar class="split-view-toolbar">
                <dx-item
                  location="before"
                  widget="dxTabs"
                  :options="filterPanelTabOptions"
                  :selected-index.sync="sidebarTabSelectedIndex"
                ></dx-item>

                <DxItem
                  :options="filterToolbar.clear"
                  location="after"
                  locate-in-menu="auto"
                  widget="dxButton"
                  :visible="false"
                />

                <DxItem
                  :options="filterToolbar.close"
                  location="after"
                  widget="dxButton"
                />
              </DxToolbar>

              <div v-if="sidebarTabSelectedIndex == 0">
                <gc-filter-panel
                  v-if="sidebarTabSelectedIndex == 0"
                  :toolbarVisible="false"
                  :formItemData="filterFormItems"
                  :setGridPanelFilter="setGridPanelFilter"
                  :gridFoolter="showFooter"
                  :showLoading="showLoading"
                  :from="'transaction-screen'"
                  :isNormal="false"
                  :persistenceKey="getFilterPersistenceKey"
                  :key="'fp-' + soDetailsCurrentView + filterRefreshKey"
                  :overWriteClear="true"
                >
                </gc-filter-panel>
              </div>

              <div v-else>
                <gc-number-pad
                  :focusedCellData="focusedCellData"
                  :applyFocusClass="applyFocusClass"
                />
              </div>
            </div>
          </template>
        </gc-filter-split-view>
      </div>
      <GcFooter
        v-if="showFooter"
        v-bind:class="selectedIndex == 0 ? 't-so-foolter-h' : 't-so-foolter-d'"
      >
        <template #leftContent>
          <div
            class="gc-grid-footer-item left-content"
            v-for="item in footerLeftItems"
            :key="item.label"
          >
            <label class="gc-footer-label">{{ item.label }}</label>

            <label
              v-if="item.fieldType == 'custome-string'"
              class="gc-footer-value"
              >{{ item.value }}</label
            >
            <GcFooterItem
              :value="item.value"
              :format="item.format"
              :length="item.format == 'Decimal-16-4C' ? 8 : 4"
              v-else
            />
          </div>
        </template>
        <template #rightContent>
          <div
            class="gc-grid-footer-item"
            v-for="item in footerRightItems"
            :key="item.label"
          >
            <label class="gc-footer-label">{{ item.label }}</label>
            <GcFooterItem
              :value="item.value"
              :format="item.format"
              :length="item.format == 'Decimal-16-4C' ? 8 : 6"
            />
          </div>
        </template>
      </GcFooter>
    </div>

    <GcPopUpSelectRecord
      v-if="popupVisible"
      :popupVisible="popupVisible"
      :popupHiddenCallBack="popupHiddenCallBack"
      :popupConfig="popupConfig"
      :dataModel="selectPopupModel"
    />

    <!-- Delivery Address -->
    <gc-popup
      v-if="editDeliveryAddressVisible"
      :popup-visible="editDeliveryAddressVisible"
      popup-title="Deliver To"
      :back-button-function="closeDeliveryAddressPopup"
      :popup-height="375"
      :saveButtonConfig="deliveryAddressPopupConfig.save"
      :backButtonConfig="deliveryAddressPopupConfig.back"
      :wrapperAttr="{ class: 'css-v2 gc-popup' }"
    >
      <div class="css-v2 gc-popup-form">
        <gc-form
          :formOptions="{
            gcLabelLocation: 'left',
          }"
          :formItems="getDeliverToFormItems"
          :isNormal="true"
          :data-model="addressData"
          :formMode="baseView.mode"
          :fromFrom="'gc-header-form-d-a'"
          :formHeight="240"
        ></gc-form>
      </div>
    </gc-popup>

    <!-- Line Comment Popup  -->
    <DxPopup
      :visible.sync="lineCommentsPopupVisible"
      :drag-enabled="false"
      :hide-on-outside-click="false"
      :show-title="true"
      title="Line Comment"
      :width="550"
      :height="310"
      :showCloseButton="false"
      :wrapperAttr="{ class: 'css-v2 gc-popup' }"
    >
      <div class="row css-v2 gc-popup-form">
        <form>
          <DxForm
            :key="lineCommentModel.ID"
            :ref="'line-comments-form'"
            :customizeItem="customizeLineCommentItem"
            :form-data.sync="lineCommentModel"
            :items="lineCommentItems"
            :label-location="formsConfig.labelPosition"
          ></DxForm>
        </form>
      </div>

      <div class="footer">
        <DxButton
          text="Back"
          type="default"
          styling-mode="contained"
          @click="popupLineCommentBackButtonClick"
        />
      </div>
    </DxPopup>

    <!-- items history popup  -->
    <DxPopup
      :visible.sync="itemHistoryPopupVisible"
      :drag-enabled="false"
      :hide-on-outside-click="true"
      :show-title="true"
      title="Customer Item History"
      :showCloseButton="false"
      :height="'75%'"
      @shown="itemHistoryPopupShown"
      @hidden="itemHistoryPopupHidden"
      :wrapperAttr="{ class: 'css-v2 gc-popup' }"
    >
      <div class="css-v2 transaction-list">
        <dx-data-grid
          :data-source="customerItemHistoryDataSourceConfig"
          :height="itemHistoryDataGridHeight"
          :columns="customerItemHistoryColumns"
          @toolbar-preparing="itemHistoryToolbarPreparing($event)"
          ref="itemHistoryGrid"
          :allow-column-reordering="false"
          :column-chooser="{ enabled: false }"
          @content-ready="itemHistoryAddGridNoResults($event)"
        >
          <template #historyStatusTemplate="{ data }">
            <gc-status-chip :status="data.data.Status" />
          </template>

          <template #date-in-cell-template="{ data }">
            <gc-friendly-date :date="data.data.DateIn" />
          </template>

          <template #displayDescription="{}">
            <div class="informer">
              <GcHeaderContentDynamic
                :baseView="baseView"
                :primaryLabel="customerItemHistory['Description']"
                :secondaryLabel="customerItemHistory['OriginItemCode']"
                :viewMode="'popup'"
              />
            </div>
          </template>
        </dx-data-grid>
      </div>

      <div class="footer">
        <DxButton
          text="Back"
          type="default"
          styling-mode="contained"
          @click="itemHistoryPopupHidden"
        />
      </div>
    </DxPopup>

    <!-- GP Tool Popup  -->
    <DxPopup
      :visible.sync="gpToolPopupVisible"
      :drag-enabled="false"
      :hide-on-outside-click="false"
      :show-title="true"
      :title.sync="gpTool.title"
      :showCloseButton="false"
      :height="270"
      :wrapperAttr="{ class: 'css-v2 gc-popup' }"
    >
      <div class="gp-toop-popup">
        <div class="row popup-in-header">
          <!-- <DxToolbar class="view-toolbar">
          <DxItem #default location="left" locate-in-menu="auto">-->
          <GcHeaderContentDynamic
            :baseView="baseView"
            :primaryLabel="gpTool.data['Description']"
            :secondaryLabel="gpTool.data['ItemCode']"
            :viewMode="'popup'"
          />
          <!-- </DxItem>
          </DxToolbar>-->
        </div>

        <div class="row css-v2 gc-popup-form">
          <form>
            <DxForm
              :form-data.sync="gpTool.model"
              :read-only="false"
              :show-colon-after-label="false"
              :show-required-mark="false"
              :show-validation-summary="false"
              :label-location="formsConfig.labelPosition"
              :customizeItem="customizeItem"
              :items="getGPToolFormItems"
              :validation-group="'gp-tool-form'"
              :height="95"
            ></DxForm>
          </form>
        </div>
      </div>

      <div class="footer">
        <DxButton
          text="Apply"
          type="default"
          styling-mode="contained"
          @click="applyGPToolValue"
          class="save-btn"
        />
        <DxButton
          text="Back"
          type="normal"
          styling-mode="contained"
          @click="popupHiddenCallBack(false)"
        />
      </div>
    </DxPopup>

    <!-- Price Breakdown Popup -->
    <GcPopupPriceBreakdown
      :setVisible="priceBreakdownPopupVisible"
      :lineData="priceBreakdownLineData"
      :callBack="
        (val) => {
          priceBreakdownPopupVisible = val;
        }
      "
      v-if="priceBreakdownPopupVisible"
      @show-loading="showLoading"
    />

    <!-- product details popup 460 -->
    <DxPopup
      :visible.sync="productDetailsPopupVisible"
      :drag-enabled="false"
      :hide-on-outside-click="true"
      :show-title="true"
      :title.sync="productDetails.title"
      :showCloseButton="false"
      :height="productDetails.hasPromotion ? 470 : 370"
      @hidden="itemDetailsPopupHidden"
      :wrapperAttr="{ class: 'css-v2 gc-popup gc-popup-product-details' }"
    >
      <div class="product-details" v-show="productDetails.data != null">
        <div class="popup-in-header">
          <GcHeaderContentDynamic
            :baseView="baseView"
            :primaryLabel="productDetails.data['Description']"
            :secondaryLabel="productDetails.data['ItemCode']"
            :viewMode="'popup'"
          />
        </div>
        <div class="row details-row">
          <div class="col-6">
            <div
              v-show="productDetails.data.HasPrimaryImage == false"
              class="product-primary-image-img"
            >
              <div class="no-image">
                <span class="product-primary-image-label-secondary"
                  >No Image</span
                >
              </div>
            </div>
            <div
              v-show="productDetails.data.HasPrimaryImage == true"
              class="product-primary-image-img"
            >
              <img :src="productDetails.imageURL" width="auto" height="200" />
            </div>
          </div>
          <div class="col-6">
            <form>
              <DxForm
                ref="product-details-form"
                :form-data.sync="productDetails.model"
                :read-only="true"
                :show-colon-after-label="false"
                :show-required-mark="false"
                :show-validation-summary="false"
                :label-location="formsConfig.labelPosition"
                :customizeItem="customizeItem"
                :items="productDetailsItems"
                :height="productDetails.hasPromotion ? 320 : 220"
              ></DxForm>
            </form>
          </div>
        </div>
      </div>

      <div class="footer">
        <DxButton
          text="Back"
          type="default"
          styling-mode="contained"
          @click="itemDetailsPopupHidden"
        />
      </div>
    </DxPopup>

    <!-- Available Stock -->
    <GcStockLevels
      v-if="visibleStockLevels"
      :itemData="stockLevelItemData"
      :setVisible="visibleStockLevels"
      :callBack="stockLevelCallBack"
    />

    <!-- Account Status popup  -->
    <GcAccountStatusModal
      v-if="accountStatusPopupVisible"
      :parent-model="model"
      :show-modal.sync="accountStatusPopupVisible"
      @close-form="
        () => {
          this.accountStatusPopupVisible = !this.accountStatusPopupVisible;
        }
      "
      @show-loading="showLoading"
      :wrapperAttr="{ class: 'sales-order-account-status' }"
    />
    <gc-popup-sales-order-split
      v-if="baseView.mode === 'review' && baseView.id"
      :sales-order-code="baseView.id"
      :visible="splitOrderVisible"
      @update:visible="splitOrderVisible = $event"
      @saved="onSplitOrderSaved"
    />
    <gc-popup-sales-order-update-item
      v-if="updateItemPopupVisible"
      :visible="updateItemPopupVisible"
      :current-item-code="updateItemLineData.OriginItemCode"
      :current-description="updateItemLineData.Description"
      :line-comment="updateItemLineData.Comment"
      :row-key="updateItemLineData.ID"
      :row-data="updateItemLineData"
      :sales-order-type-code="model.SalesOrderTypeCode"
      :branch-code="model.BranchCode"
      :customer-code="toolbarModel.CustomerCode"
      :exchange-rate="model.ExchangeRate"
      :date-in="model.DateIn"
      :required-by-date="model.RequiredByDate"
      @update:visible="updateItemPopupVisible = $event"
      @saved="onUpdateItemSaved"
    />
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import DataGridMixin from "@/_mixins/data-grid.mixin";
import { CustomerService } from "../../_services/api/customers/customer.service";
import { SalesRepresentativeService } from "../../_services/api/sales/sales-rep.service";
import {
  getDecimalFormat,
  getFactorByCode,
  getGlobalField,
} from "../../_store/helpers";
import { DefaultCompanyBranchDivisionService } from "../../_services/api/sales/default-company-branch-dicision.service";
import { CompanyService } from "../../_services/api/accounts/company.service";
import { CompanyCurrencyService } from "../../_services/api/accounts/company-currency.service";
import { CommonStorage, FeatureStorage } from "../../_services/storage";
// import { BranchService } from "../../_services/api/sales/branch.service";
// import { DivisionService } from "../../_services/api/sales/division.service";
// import { SalesOrderTypeService } from "../../_services/api/sales/sales-order-type.service";
import validationEngine from "devextreme/ui/validation_engine";
import { mapGetters } from "vuex";
import { CustomerDeliveryAddressService } from "../../_services/api/customers/customer-delivery-address.service";
import { CustomerNoteService } from "../../_services/api/customers/customer-note.service";
import { SalesOrderService } from "../../_services/api/sales/sales-order.service";
import { SalesOrderV3Service } from "../../_services/api/sales/sales-order-v3.service";
import ArrayStore from "devextreme/data/array_store";
import appConfig from "../../app-config";
import salesFormulas from "../../_helpers/formulas/sales-formulas";
import { SalesItemHistoryService } from "../../_services/api/sales/sale-item-history.service";
import SettingsService from "../../_services/api/settings/graycorp/settings.service";
import { alert } from "devextreme/ui/dialog";
// import { SalesOrderNoteService } from "../../_services/api/sales/sales-order-note.service";
import { SalesOrderState } from "../../_models/sales-order";
import { SalesOrderProductDetails } from "../../_services/api/sales/sales-order-product-details.service";
import { PriceGroupService } from "../../_services/api/sales/price-group.service";
import ApiService from "../../_services/api/api.service";
import { ProductService } from "../../_services/api/products/product.service";
import { CustomerSalesContracts } from "../../_models/customer-sales-contracts";
import { CustomerSalesContractService } from "../../_services/api/accounts/customer-sales-contract.service";
import { CustomerFreightChargeService } from "../../_services/api/accounts/customer-freight-charge.service";
import { InvoiceService } from "../../_services/api/sales/invoice.service";
import ColumnWidth from "../../_helpers/data-grid/ColumnWidth";
import { SalesOrderBatchAllocationsService } from "../../_services/api/sales/sales-order-batch-allocations.service";
import { SalesOrderEDIService } from "../../_services/api/sales/sales-order-edi.service";
import { DateFromIsoWithoutZ } from "../../_helpers/prototypes/prototype-date";
import { custom } from "devextreme/ui/dialog";
import { formatDate } from "devextreme/localization";
import GcPopupPriceBreakdown from "../../popups/gc-popup-price-breakdown";

function logDev() {
  // eslint-disable-next-line no-console
  console.log("Sales Order >>> ", arguments);
}

const {
  calculateGrossProfitPcnt,
  calculateGrossProfitPcntRaw,
  calculateSalesUMPriceForGP,
  calculateNetLineTotal,
  calculateWetLineTotal,
  calculateTaxableLineTotal,
  calculateTaxLineTotal,
  calculateLineTotal,
  calculateSalesUMNetFXPrice,
  calculateStockUMPrice,
  calculateStockUMNetPrice,
  calculateStockUMFXPrice,
  calculateStockUMQty,
  calculateStockUMNetFXPrice,
  calculateDiscLineTotal,
  calculateDiscFXLineTotal,
  calculateNetFXLineTotal,
  calculateWetFXLineTotal,
  calculateTaxableFXLineTotal,
  calculateTaxFXLineTotal,
  calculateFXLineTotal,
  calculateSalesUMNetPriceFromSalesUMNetFXPrice,
  calculateSalesUMPriceFromSalesUMFXPrice,
  calculateCalculatedDiscPcnt,
  calculateSalesUMFXPriceFromSalesUMNetFXPrice,
  // calculateSalesUMFXPrice,
  calculateSalesUMNetFXPriceFromSalesUMFXPrice,
} = salesFormulas;

let soDetailsBaseRecord = {
  AddDiscPriceGroupCode: null,
  AddDiscPricingMethod: null,
  AddDiscPricingMethodValue: 0,
  AddDiscSalesUMPrice: 0,
  AddDiscStockUMPrice: 0,
  CalculatedDiscPcnt: 0,
  Comment: "",
  CustomerProduct: "",
  CustomerSalesContractCode: null,
  CustomerSalesContractLineNo: null,
  DiscFXLineTotal: 0,
  DiscLineTotal: 0,
  DiscPcnt: 0,
  ExTaxUnitCost: 0,
  FXLineTotal: 0,
  IsCommentVisible: false,
  IsPrimeCostItem: false,
  ItemCode: "",
  ItemType: "",
  KitVersion: 0,
  LineNo: 0,
  LineTotal: 0,
  NetFXLineTotal: 0,
  NetLineTotal: 0,
  PriceGroupCode: null,
  PriceGroupSalesUMPrice: 0,
  PriceGroupStockUMPrice: 0,
  PricingMethod: null,
  PricingMethodValue: null,
  RecordId: "00000000-0000-0000-0000-000000000000",
  RequiredByDate: null,
  SalesTaxPcnt: 0,
  SalesUM: "",
  SalesUMConversion: null,
  SalesUMCost: 0,
  SalesUMFXPrice: 0,
  SalesUMIsVariable: null,
  SalesUMManufacturedCost: 0,
  SalesUMNetFXPrice: 0,
  SalesUMNetPrice: 0,
  SalesUMPrice: 0,
  SalesUMPromotionNetPrice: 0,
  SalesUMQty: 0,
  SalesUMQtyFocused: false,
  SalesUMStandardCost: 0,
  SalesUMStandardPrice: 0,
  ShipDate: null,
  Status: "A",
  StockUM: null,
  StockUMFXPrice: 0,
  StockUMManufacturedCost: 0,
  StockUMNetFXPrice: 0,
  StockUMNetPrice: 0,
  StockUMPrice: 0,
  StockUMQty: 0,
  StockUMStandardCost: 0,
  StockUMStandardPrice: 0,
  TaxableFXLineTotal: 0,
  TaxableLineTotal: 0,
  TaxCode: null,
  TaxFXLineTotal: 0,
  TaxLineTotal: 0,
  WetFreightAdjustment: 0,
  WetFXFreightAdjustment: 0,
  WetFXLineTotal: 0,
  WetLineTotal: 0,
  WetPcnt: 0,
  HasPromotion: false,
  BestBeforeDate: null,
  ProductSizeCode: "",
  gpprcnt: 0,
  SalesUMSuppliedQty: 0,
  customerContractItemsForProduct: undefined,
  Rop: null,
  MarginOverrideWarning: false,
};

// Factor 478 sort-key name (normalised: whitespace stripped, upper-cased) -> product grid field.
// A `null` field means the key is recognised per Factor 478's spec but has no distinct real value
// on unadded catalog rows today (Bin Code / History 1-4 have no backing field at all; Stock UM Price /
// Net Price would otherwise duplicate Price since SalesUMFXPrice/StockUMPrice/SalesUMNetPrice are all
// hardcoded to 0 until a product becomes an order line) — treated as a no-op sort tier, not an error.
const factor478SortKeyFieldMap = {
  CATEGORY1: "ProductCategory1Code",
  PRODUCTCATEGORY1: "ProductCategory1Code",
  CATEGORY2: "ProductCategory2Code",
  PRODUCTCATEGORY2: "ProductCategory2Code",
  CATEGORY3: "ProductCategory3Code",
  PRODUCTCATEGORY3: "ProductCategory3Code",
  LINENO: "LineNo",
  PRODUCTTYPE: "ProductTypeCode",
  BINCODE: "BinCode",
  ITEMCODE: "ItemCode",
  DESCRIPTION: "Description",
  PRODUCTDESCRIPTION: "Description",
  SALESUM: "SalesUM",
  HISTORY1: null,
  HISTORY2: null,
  HISTORY3: null,
  HISTORY4: null,
  PRICE: "StandardSell",
  STOCKUMPRICE: null,
  NETPRICE: null,
};

const factor478NumericSortFields = ["LineNo", "StandardSell"];

// Update Item (2113/76040) — revised 2026-08-28 (technical-design.md §0) to
// lift-and-shift the despatch Order "Edit Item" popup's full-reprice
// behaviour. The setSaleItemPrice repricing call happens inside
// gc-popup-sales-order-update-item.vue itself, with NO field-freezing:
// Price, Discount %, Tax Code, WET%, costs and all line totals all follow
// the New Item's own pricing (BR-6/BR-7a/BR-8a). The previous BR-7/BR-8
// PROTECTED_PRICE_FIELDS snapshot/restore mechanism (which caused Bug 76123
// by falling out of sync with setSaleItemPrice) has been deleted outright —
// see that component for details. onUpdateItemSaved below only applies the
// popup's already-fully-priced result to the grid row.

// let getFilterExpressionForArray = (fieldName, dataField, arr) => {
//   let expression = "";
//   for (let index = 0; index < arr.length; index++) {
//     if (index == 0) {
//       expression = `${fieldName} eq '` + arr[index][`${dataField}`] + "'";
//     } else {
//       expression =
//         expression + ` or ${fieldName} eq '` + arr[index][`${dataField}`] + "'";
//     }
//   }
//   return expression;
// };

// let chunkArray = (array, size) => {
//   let result = [];
//   for (let i = 0; i < array.length; i += size) {
//     let chunk = array.slice(i, i + size);
//     result.push(chunk);
//   }
//   return result;
// };

export default {
  mixins: [RouteMixin, FormMixin, DataGridMixin],
  components: {
    GcPopupPriceBreakdown,
    GcPopupSalesOrderSplit: () =>
      import("@/popups/sales/gc-popup-sales-order-split.vue"),
    GcPopupSalesOrderUpdateItem: () =>
      import("@/popups/sales/gc-popup-sales-order-update-item.vue"),
  },
  data() {
    return {
      promotionItems: [],
      freightCharges: [],
      TransitDays: 0,
      linkedContractDates: null,
      splitOrderVisible: false,
      accountStatusPopupVisible: false,
      isExchangeReverting: false,
      gridExpand: false,
      // Keyed by row ID (FDT70523 fix) - was a single screen-wide timer, so
      // committing a Quantity edit on one line could cancel another line's
      // still-pending first-time price/tax lookup outright. See
      // setUpdatePriceData/updatePriceData below.
      priceLookupTimeouts: {},
      searchFocusRequested: false,
      priceUpdateTimeOut: undefined,
      discountTimeOut: undefined,
      loaded: false,
      setLoading: false,
      soAlertLoadedCustomerCode: undefined,
      toolbarModel: {
        CustomerCode: undefined,
        CustomStatus: undefined,
        Status: "N",
        FriendlyStatus: undefined,
        NextStatus: undefined,
      },
      model: {
        isEDIOrder: false,
        isPendingEDIOrder: false,
        CompanyCurrency: undefined,
        Code: null,

        // CustomerCode: null,
        // CustomerCode: "13003",
        CustomerName: null,
        DateIn: new Date(),
        RequiredByDate: null,
        ShipDate: null,
        OrderNo: null,

        DeliveryAddressCompanyName: null,
        DeliveryAddressCountry: null,
        DeliveryAddressPostcode: null,
        DeliveryAddressState: null,
        DeliveryAddressStreetLine1: null,
        DeliveryAddressStreetLine2: null,
        DeliveryAddressSuburb: null,
        CustomerDeliveryAddressLineNo: undefined,

        ConsignmentNo: "",
        CurrencyCode: "",
        ExchangeRate: 1,

        BranchCode: null,
        BranchDescription: null,
        DivisionCode: null,
        DivisionDescription: null,
        SalesOrderTypeCode: null,
        SalesOrderTypeDescription: null,
        SalesRepresentativeCode: null,
        SalesRepresentativeFullName: null,

        SalesOrderDetails: [],

        DateChanged: null,
        DateCreated: null,
        ChangedBy: null,
        CreatedBy: null,
        Notes: [],
        LineItemCode: undefined,
        FilterDate: undefined,
      },
      orderCommentModel: {},
      selectedIndex: 0,

      popupVisible: false,
      popupConfig: undefined,
      selectPopupModel: {
        filterBySalesRep: undefined,
      },
      isValid: false,
      editDeliveryAddressVisible: false,
      addressData: undefined,
      soDetailsCurrentView: "CustomerList",
      // soDetailsCurrentView: "ProductList",
      showFooter: true,
      footerItems: [],
      changes: [],
      reviewDataList: new ArrayStore({
        key: "ID",
        data: [],
      }),
      showLeftContainer: true,
      showRightContainer: false,

      filterToolbar: {
        close: {
          icon: "showpanel",
          onClick: () => {
            this.closeFilterPanel();
          },
          // visible: browserHelper.tablet,
        },
        clear: {
          text: "Clear",
          onClick: () => {
            this.clearAdvanceFilters();
          },
        },
      },
      sidebarTabSelectedIndex: 0,
      filterModel: {},
      selectedProductCategory1: [],
      filterCollection: [],
      focusedCellData: undefined,
      lineCommentsPopupVisible: false,

      lineCommentModel: {
        Comment: "",
        IsCommentVisible: false,
        ID: undefined,
        RowData: undefined,
      },

      itemHistoryPopupVisible: false,
      customerItemHistory: [],
      repaint: false,
      promoRequest: false,
      allowPriceChangeTriggerConfirmation: false,
      isProductAccountSplit: false,
      filterRefreshKey: 1,
      salesUMsLocalDictionary: {},
      selectedRowData: undefined,
      gpToolPopupVisible: false,
      priceBreakdownPopupVisible: false,
      priceBreakdownLineData: {},
      updateItemPopupVisible: false,
      updateItemLineData: {},
      gpTool: {
        title: "GP Tool",
        data: [],
        cellInfo: undefined,
        model: {
          quantity: null,
          standardSell: null,
          currentPrice: null,
          gp: null,
          newSalesUMNetPrice: null,
        },
        //  items:  getGPToolFormItems
      },
      scrollingOffSet: undefined,
      localCopyItems: [],
      productDetailsPopupVisible: false,
      productDetails: {
        title: "Product Details",
        data: {
          HasPrimaryImage: false,
        },
        hasPromotion: false,
        imageURL: null,
        model: {
          itemCode: null,
          apn: null,
          standardSell: null,
          customerSell: null,
          cartonQty: null,
          salesUM: null,
          promotionStartDate: null,
          promotionEndDate: null,
        },
      },
      StockModel: {
        AvailableStock: false,
      },
      stockLevelItemData: undefined,
      visibleStockLevels: false,
      forceRefresh: true,
      productDataSourceItems: new ArrayStore({
        key: "ID",
        data: [],
      }),
      customerDataSourceItems: new ArrayStore({
        key: "ID",
        data: [],
      }),
      itemHistoryDataGridHeight: "auto",
      headerFormHeight: 0,
      headerDxFormHeight: "auto",
      CustomerSalesContracts: new CustomerSalesContracts(),
      SalesContractDrawdownEnabled: false,
      IsCustomerEditable: true, // == false if any invoice is available for the SO
      SelectedQTYCell: undefined,
      isBatchAllocationVisible: false,
      HasLinkedWorkOrder: false, // to check if the sales order has linked work order
      DummyProductItemCode: null, // to check if the dummy product item code is set in the settings
    };
  },
  beforeDestroy() {
    document.removeEventListener(
      "keydown",
      this.gridKeyboardShortcutHandler,
      true,
    );
    this.releaseGridSearchBoxFocus();
    this.reviewDataList.clear();
    var views = [
      {
        value: "Review",
        text: "Review",
      },
      {
        value: "ProductList",
        text: "Product",
      },
      {
        value: "CustomerList",
        text: "Customer",
      },
    ];
    views.forEach((element) => {
      CommonStorage.clear("sales-order-" + element.value);
      CommonStorage.clear("sales-order-" + element.value + "-scroll-offset");
      CommonStorage.clear("filter-panel-sales-order-" + element.value);
    });
    CommonStorage.clear("sales-order-details");
    CommonStorage.clear("sales-order-details-scroll-offset");
    CommonStorage.clear("filter-panel-sales-order-details");
  },
  async created() {
    // this.loaded = true;

    this.$store.dispatch("ui/setVisibleCommentLabels", false);
    this.showLoading(true);

    if (CommonStorage.getItems(this.getFilterPersistenceKey + "-scroll-offset"))
      this.scrollingOffSet = JSON.parse(
        CommonStorage.getItems(this.getFilterPersistenceKey + "-scroll-offset"),
      );

    await SettingsService.getBySettingByApplicationFeatureCode(
      "SalesOrder",
    ).then((res) => {
      res.data.value.forEach((element) => {
        if (element.SettingName == "PriceChangeTriggerConfirmation") {
          if (element.ScopeCode == "Global") {
            if (element.SettingValue == "Y") {
              this.allowPriceChangeTriggerConfirmation = true;
            }
          }
        } else if (element.SettingName == "ProductAccountDisplayMode") {
          if (element.ScopeCode == "Global") {
            if (element.SettingValue == "Split") {
              this.isProductAccountSplit = true;
            }
          }
        } else if (element.SettingName === "DummyProductItemCode") {
          if (element.ScopeCode === "Global") {
            if (element.SettingValue) {
              this.DummyProductItemCode = element.SettingValue;
            }
          }
        }
      });
    });
    await this.initialized();

    // this.loadCustomerItems(undefined, 0);
  },
  async mounted() {
    this.gridKeyboardShortcutHandler =
      this.handleGridKeyboardShortcut.bind(this);
    // Capture phase on document: DevExtreme's own grid keyboard navigation
    // (F2 = start edit on the focused cell) calls stopPropagation() on the
    // bubble phase, so a bubble-phase listener here would never see F1/F2
    // once focus is inside the grid. Capturing ahead of it lets this
    // screen's override win. Bound to document (not this.$el) so it fires
    // regardless of where focus currently is — a capture-phase listener on
    // this.$el only sees events whose target is already inside its own
    // subtree, so it stayed silent until the user had clicked into the grid.
    document.addEventListener(
      "keydown",
      this.gridKeyboardShortcutHandler,
      true,
    );
  },

  computed: {
    getTabs() {
      var tabItems = [];

      if (this.baseView.mode !== "create") {
        tabItems = [
          {
            text: "Order",
            path: `/sales-order/${this.baseView.mode}/${this.baseView.id}`,
            applicationFeatureCode: "SalesOrder",
          },
          {
            text: "Batch Allocation",
            path: `/sales-order-batch-allocations/${this.baseView.mode}/${this.baseView.id}`,
            applicationFeatureCode: "SalesOrder",
            disabled:
              !this.isBatchAllocationVisible ||
              this.toolbarModel.Status === "P",
          },
          {
            text: "EDI",
            path: `/sales-order-edi/${this.baseView.id}`,
            applicationFeatureCode: "SalesOrderEDI",
            disabled: this.model.IsEDIOrder === "N",
          },
          {
            text: "Lot Assignment",
            path: `/almondco-inshell-sales-order-lot-assignment/review/${this.baseView.id}`,
            applicationFeatureCode: "AlmondcoInshellSalesOrderLotAssignment",
            visible: FeatureStorage.checkPermission(
              "AlmondcoInshellSalesOrderLotAssignment",
              "review",
            ),
          },
        ];
      }

      return tabItems;
    },
    ...mapGetters({ commentPopUpItems: "ui/getCommentPopUpItems" }),
    ...mapGetters({ gridHeight: "ui/getHeightForDataGrid" }),
    ...mapGetters({
      getValidationSummaryVisible: "ui/getValidationSummaryVisible",
    }),
    gpToolVisible() {
      return FeatureStorage.checkPermission("GP Tool", "create");
    },
    // TO-DO: Feature Code is yet to be decided for price breakdown, once it is decided then implement the permission check for price breakdown visibility
    priceBreakdownVisible() {
      return FeatureStorage.checkPermission("ProductPrice", "review");
    },
    // "Update Item" (Dummy Item line replace) — matches the Split Order
    // popup's permission gate exactly, per Technical Design BR-3.
    updateItemVisible() {
      return FeatureStorage.checkPermission("SalesOrder", "create");
    },
    getHeaderFormItems() {
      return [
        {
          itemType: "group",
          caption: "",
          cssClass: "header-form-items",
          colCount: 2,
          items: [
            {
              itemType: "group",
              caption: "",
              items: [
                {
                  screenField: "DateIn",
                  label: {
                    text: "Date In",
                  },
                  dataField: "DateIn",
                  required: true,
                  editorOptions: {
                    tabIndex: 2,
                    onValueChanged: this.onDateInValueChanged.bind(this),
                  },
                },
                {
                  itemType: "group",
                  caption: "",
                  colCount: 2,
                  items: [
                    {
                      screenField: "RequiredDate",
                      label: {
                        text: "Reqd Date",
                      },
                      dataField: "RequiredByDate",
                      required: true,
                      editorOptions: {
                        tabIndex: this.HasLinkedWorkOrder ? -1 : 3,
                        onValueChanged:
                          this.onRequiredByDateValueChanged.bind(this),
                        readOnly: this.HasLinkedWorkOrder, // if there is linked work order then required date should not be editable from sales order details screen as it is driven by work order required date
                      },
                    },
                    {
                      screenField: "ShipDate",
                      label: {
                        text: "Ship Date",
                      },
                      dataField: "ShipDate",
                      required: true,
                      editorOptions: {
                        tabIndex: this.HasLinkedWorkOrder ? -1 : 4,
                        onValueChanged: this.onShipDateValueChanged.bind(this),
                        readOnly: this.HasLinkedWorkOrder, // if there is linked work order then ship date should not be editable from sales order details screen as it is driven by work order ship date
                      },
                    },
                  ],
                },
                {
                  dataField: "ConsignmentNo",
                  label: {
                    text: "Consignment",
                  },
                  editorType: "dxTextBox",
                  editorOptions: {
                    tabIndex: 5,
                    onValueChanged: this.onShipDateValueChanged.bind(this),
                    maxLength: 100,
                  },
                },
                {
                  screenField: "CustPO",
                  dataField: "OrderNo",
                  editorOptions: {
                    onValueChanged: this.onOrderNoChanged.bind(this),
                    tabIndex: 6,
                  },
                },
                {
                  itemType: "group",
                  caption: "",
                  cssClass: "deliver-to",
                  items: [
                    {
                      screenField: "DeliverTo",
                      dataField: "CustomerDeliveryAddressLineNo",
                      label: {
                        text: "Deliver To",
                      },
                      valueExpr: "LineNo",
                      displayExpr: (data) => {
                        if (data) return data.CompanyName;
                        else if (this.model.DeliveryAddressCompanyName)
                          return this.model.DeliveryAddressCompanyName;
                        else return undefined;
                      },
                      ref: "DeliveryAddressCompanyName",
                      required: true,
                      filter: [
                        ["CustomerCode", "=", this.toolbarModel.CustomerCode],
                      ],
                      editorOptions: {
                        tabIndex: 7,
                        onItemClick: this.onDeliverToRowClick,
                        onFocusIn: async () => {
                          let validationResult =
                            await validationEngine.validateGroup(
                              "gc-form-header-toolbar",
                            );
                          if (!validationResult.isValid)
                            this.displayValidationSummary(
                              validationResult.brokenRules,
                            );
                        },
                        select: [
                          "Inactive",
                          "CustomerCode",
                          "CompanyName",
                          "LineNo",
                          "PhysicalAddressCountry",
                          "PhysicalAddressPostcode",
                          "PhysicalAddressState",
                          "PhysicalAddressStreetLine1",
                          "PhysicalAddressStreetLine2",
                          "PhysicalAddressSuburb",
                          "TransitDays",
                        ],
                        itemTemplate: (data) => {
                          if (!data) return "";

                          var secondaryLines =
                            this.getDeliverToItemSecondaryLines(data)
                              .map((line) => `<div>${line}</div>`)
                              .join("");

                          return `<div class="sales-order-layout custom-auto-complete">
                            <span class="auto-complete-primary">
                              ${data.CompanyName}
                            </span>
                            <div class="record-secondary">
                              ${secondaryLines}
                            </div>
                          </div>`;
                        },
                        dropDownOptions: {
                          width: 250,
                        },
                      },
                      visible: this.baseView.mode != "review",
                    },
                    {
                      screenField:
                        this.baseView.mode != "review"
                          ? "DeliverTo"
                          : undefined,
                      dataField: "DeliveryAddressCompanyName",
                      ref: "DeliveryAddressCompanyName",
                      label: {
                        text: "Deliver To",
                      },
                      required: true,
                      visible: this.baseView.mode == "review",
                    },
                    {
                      screenField: "DeliveryCompanyView",
                      editAddress:
                        this.baseView.mode != "review" ? true : false,
                    },
                  ],
                },
              ],
            },

            {
              itemType: "group",
              caption: "",
              items: [
                {
                  screenField:
                    this.baseView.mode != "review" ? "Branch" : undefined,
                  label: {
                    text: "Branch",
                  },
                  dataField:
                    this.baseView.mode != "review"
                      ? "BranchCode"
                      : "Branch.Description",
                  required: true,
                  editorOptions: {
                    tabIndex: 8,
                    onItemClick: this.onBranchRowClick,
                    onValueChanged: async (e) => {
                      if (!e.event) return;
                      await this.getSalesUMs(this.model.BranchCode);
                    },
                    showClearButton: true,
                    select: ["Code", "Description"],
                  },
                },
                {
                  screenField:
                    this.baseView.mode != "review" ? "Division" : undefined,
                  label: {
                    text: "Division",
                  },
                  dataField:
                    this.baseView.mode != "review"
                      ? "DivisionCode"
                      : "Division.Description",
                  required: true,
                  editorOptions: {
                    tabIndex: 9,
                    onItemClick: this.onDivisionRowClick,
                    onValueChanged: (e) => {
                      if (!e.event) return;
                    },
                    showClearButton: true,
                    select: ["Code", "Description"],
                  },
                },
                {
                  screenField:
                    this.baseView.mode != "review" ? "OrderType" : undefined,
                  dataField:
                    this.baseView.mode != "review"
                      ? "SalesOrderTypeCode"
                      : "SalesOrderType.Description",
                  label: {
                    text: "SO Type",
                  },
                  required: true,
                  editorOptions: {
                    tabIndex: 10,
                    onItemClick: this.onSalesOrderTypeRowClick,
                    onValueChanged: (e) => {
                      if (!e.event) return;
                    },
                    showClearButton: true,
                    select: ["Code", "Description"],
                  },
                },
                {
                  screenField:
                    this.baseView.mode != "review"
                      ? "SalesRepresentative"
                      : undefined,
                  dataField:
                    this.baseView.mode != "review"
                      ? "SalesRepresentativeCode"
                      : "SalesRepresentativeFullName",
                  label: {
                    text: "Sales Rep",
                  },
                  required: true,
                  editorOptions: {
                    tabIndex: 11,
                    onItemClick: this.onSalesRepRowClick,
                    showClearButton: true,
                    select: ["Code", "FirstName", "Surname", "FullName"],
                    onKeyDown: (e) => {
                      if (
                        !this.exchangeRateEditable &&
                        !this.canEditHeaderDiscount
                      )
                        this.moveOnKeyDown(e);
                    },
                  },
                },

                // {
                //   screenField: "CurrencyCode",
                //   dataField: "CurrencyCode",
                // },

                {
                  itemType: "group",
                  caption: "",
                  colCount: 2,
                  items: [
                    {
                      screenField: "ExchangeRate",
                      dataField: "ExchangeRate",
                      cssClass: "exchange-rate",
                      editorOptions: {
                        onKeyDown: (e) => {
                          this.moveOnKeyDown(e);
                        },
                        tabIndex: this.exchangeRateEditable ? 12 : -1,
                        readOnly: !this.exchangeRateEditable,
                        onValueChanged: this.onExchangeValueChanged,
                        step: 0,
                        // customFormat: true,
                        // format: `${this.model.CurrencyCode} ##,###,###,###,##0.0000`,
                      },
                    },
                    {
                      dataField: "CurrencyCode",
                      label: {
                        text: "Currency",
                        visible: false,
                      },
                      editorOptions: {
                        tabIndex: -1,
                        readOnly: true,
                      },
                    },
                  ],
                },
                {
                  dataField: "HeaderDiscountPcnt",
                  label: {
                    text: "Discount %",
                  },
                  editorType: "dxNumberBox",
                  // same permission and enforcement boundary as the line-level Discount column
                  visible: this.canEditHeaderDiscount,
                  editorOptions: {
                    tabIndex:
                      this.canEditHeaderDiscount && !this.headerDiscountLocked
                        ? 13
                        : -1,
                    format: "##0.00 '%'",
                    min: 0,
                    max: 100,
                    showSpinButtons: false,
                    disabled:
                      this.baseView.mode == "review" ||
                      this.headerDiscountLocked,
                    onValueChanged:
                      this.onHeaderDiscountPcntConfirmed.bind(this),
                    onKeyDown: (e) => {
                      setTimeout(() => {
                        this.moveOnKeyDown(e);
                      }, 100);
                    },
                  },
                },
              ],
            },
          ],
        },
      ];
    },
    exchangeRateEditable() {
      return (
        this.baseView.mode != "review" &&
        this.model.CompanyCurrency != this.model.CurrencyCode
      );
    },
    // whole-header lock — becomes true (and stays true) as soon as any line has been
    // delivered against; not a per-line branch, the entire Header Discount % control locks
    headerDiscountLocked() {
      return this.reviewDataList._array.some(
        (line) => line.SalesUMSuppliedQty > 0 || line.PSlipStatus == "C",
      );
    },
    canEditHeaderDiscount() {
      return FeatureStorage.checkPermission(
        "SalesOrderDetailEditDiscount",
        "edit",
      );
    },
    isSaveEnable() {
      if (!FeatureStorage.checkPermission("SalesOrder", "create")) return false;
      else if (this.baseView.mode == "create") return true;
      else {
        if (!this.loaded) return true;
        else if (
          this.toolbarModel.Status == "C" ||
          this.toolbarModel.Status == "X" ||
          this.toolbarModel.Status == "L"
        )
          return false;
        else return true;
      }
    },
    getHeaderToolbar() {
      return {
        audit: this.baseView.mode == "review",
        status: true,
        notes: false,
        save: this.isSaveEnable,
        editURL: "/sales-order",
        editCallBack: this.editSOCallBack,
        saveKey: true,
        saveOptions: {
          tabIndex: -1,
          items:
            this.baseView.mode != "create"
              ? [
                  {
                    text: "Draft",
                    id: "D",
                    visible: this.toolbarModel.Status == "D",
                  },
                  {
                    text: "Pending",
                    id: "P",
                  },
                  {
                    text: "Commit",
                    id: "A",
                    default: true,
                    // visible: this.toolbarModel.Status !== "H",
                    disabled:
                      this.toolbarModel.Status === "P" &&
                      this.toolbarModel.IsEDIOrder === "Y",
                  },
                  {
                    text: "Accept",
                    id: "A",
                    default: true,
                    visible:
                      this.toolbarModel.Status === "P" &&
                      this.toolbarModel.IsEDIOrder === "Y",
                  },
                  {
                    text: "Cancel",
                    id: "X",
                    visible: this.model.InvoiceCode == 0,
                  },
                ]
              : [
                  {
                    text: "Draft",
                    id: "D",
                    visible: this.toolbarModel.Status == "D",
                  },
                  { text: "Pending", id: "P" },
                  { text: "Commit", id: "A", default: true },
                ],
          keyExpr: "id",
          displayExpr: "text",
          dropDownOptions: {
            width: "150px",
          },
          saveKey: "NextStatus",
        },
        // saveCallback: this.saveSalesOrder,
        screenName: "Sales Orders - All",
        pdf: this.baseView.mode == "review",

        toolbarFormItems: [
          {
            screenField: "Customer",
            dataField: "CustomerCode",
            cssClass: "toolbar-customer-label",
            editorOptions: {
              tabIndex: this.HasLinkedWorkOrder ? -1 : 1,
              dropDownOptions: {
                minWidth: 300,
                elementAttr: {
                  id: "so-v4-customer-details",
                },
              },
              width: 300,
              placeholder: "Customer Name",
              showClearButton: true,
              // onItemClick: this.onCustomerRowClick,
              onSelectionChanged: (e) => {
                if (
                  this.toolbarModel.CustomerCode !=
                  e.component.instance().option().value
                )
                  this.onCustomerRowClick({ itemData: e.selectedItem });
                else {
                  if (!e.event) return;
                  this.onCustomerRowClick({ itemData: e.selectedItem });
                }
              },
              elementAttr: {
                id: "so-v4-customer-name",
              },
              onValueChanged: this.onCustomerValueChanged,
              readOnly: !this.IsCustomerEditable || this.HasLinkedWorkOrder,
              select: [
                "Code",
                "CurrencyCode",
                "Name",
                "StopCredit",
                "TermCalculation",
                "SalesRepresentativeCode",
              ],
            },
            label: {
              text: "Customer",
              visible: false,
            },
            required: true,
            visible: this.baseView.mode != "review",
          },
        ],
        formContentReady: this.toolBarFormContentReady,
        auditItems: [
          {
            itemType: "group",
            caption: "",
            items: [
              {
                dataField: "FormattedDateCreated",
                label: { text: "Created By", location: "left" },
                editorType: "dxTextBox",
                editorOptions: {
                  // // disabled: true,
                },
              },
              {
                dataField: "FormattedDateChanged",
                label: { text: "Changed By", location: "left" },
                editorType: "dxTextBox",
                editorOptions: {
                  // disabled: true,
                },
              },
            ],
          },
        ],
        customStatus: {
          enabled: true,
          key: "CustomStatus",
        },
        ellipsis: {
          visible: this.getMenuItems.filter((x) => x.visible).length > 0,
          menuItems: this.getMenuItems,
          // visible: this.baseView.mode == "review",
          clickAction: (e) => {
            if (e.itemData.menuText == "Account Status") {
              this.accountStatusVisible();
            } else if (e.itemData.menuText == "Complete Order") {
              this.completeSalesOrder(this.model);
            } else if (e.itemData.menuText == "Copy") {
              this.copyOrder();
            } else if (e.itemData.menuText == "Split Order") {
              this.splitOrderVisible = true;
            }
          },
        },
      };
    },
    getMenuItems() {
      return [
        {
          menuText: "Account Status",
          disabled: !this.toolbarModel.CustomerCode,
          visible:
            this.baseView.mode == "review" ||
            this.baseView.mode == "create" ||
            this.baseView.mode == "edit",
        },
        {
          menuText: "Complete Order",
          visible:
            this.toolbarModel.Status == "A" && this.model.InvoiceCode != 0,
        },
        {
          menuText: "Copy",
          visible: this.baseView.mode == "review",
        },
        {
          menuText: "Split Order",
          visible:
            this.baseView.mode == "review" &&
            FeatureStorage.checkPermission("SalesOrder", "create") &&
            (this.toolbarModel.Status === "A" ||
              this.toolbarModel.Status === "P" ||
              this.toolbarModel.Status === "D"),
        },
      ];
    },
    backButtonRoute() {
      return "/sales-order";
    },
    getDeliverToFormItems() {
      return [
        {
          itemType: "group",
          caption: "",
          colCount: 1,
          items: [
            {
              dataField: "DeliveryAddressCompanyName",
              editorType: "dxTextBox",
              label: {
                text: "Name",
              },
              editorOptions: {
                maxLength: "100",
                readOnly:
                  this.toolbarModel.Status === "C" ||
                  this.toolbarModel.Status === "X",
              },
              // allowUpdate: false,
            },
            {
              screenField: "PhysicalAddressStreetLine1",
              dataField: "DeliveryAddressStreetLine1",
              editorOptions: {
                maxLength: "50",
                readOnly:
                  this.toolbarModel.Status === "C" ||
                  this.toolbarModel.Status === "X",
              },
              // allowUpdate: false,
            },
            {
              screenField: "PhysicalAddressStreetLine2",
              dataField: "DeliveryAddressStreetLine2",
              editorOptions: {
                maxLength: "50",
                readOnly:
                  this.toolbarModel.Status === "C" ||
                  this.toolbarModel.Status === "X",
              },
              // allowUpdate: false,
            },
            {
              screenField: "PhysicalAddressSuburb",
              dataField: "DeliveryAddressSuburb",
              editorOptions: {
                maxLength: "50",
                readOnly:
                  this.toolbarModel.Status === "C" ||
                  this.toolbarModel.Status === "X",
              },
              // allowUpdate: false,
            },
            {
              screenField: "PhysicalAddressState",
              dataField: "DeliveryAddressState",
              editorOptions: {
                maxLength: "50",
                readOnly:
                  this.toolbarModel.Status === "C" ||
                  this.toolbarModel.Status === "X",
              },
              // allowUpdate: false,
            },
            {
              screenField: "PhysicalAddressPostcode",
              dataField: "DeliveryAddressPostcode",
              editorOptions: {
                maxLength: "50",
                readOnly:
                  this.toolbarModel.Status === "C" ||
                  this.toolbarModel.Status === "X",
              },
              // allowUpdate: false,
            },
            {
              screenField: "PhysicalAddressCountry",
              dataField: "DeliveryAddressCountry",
              editorOptions: {
                maxLength: "50",
                readOnly:
                  this.toolbarModel.Status === "C" ||
                  this.toolbarModel.Status === "X",
              },
              // allowUpdate: false,
            },
          ],
        },
      ];
    },
    deliveryAddressPopupConfig() {
      return {
        save: {
          text: "Save",
          disabled: !this.deliveryAddressFormIsDirty,
          clickCallback: async () => {
            await this.updateDeliveryAddress();
          },
        },
        back: {
          text: "Cancel",
          clickCallback: async () => {
            this.closeDeliveryAddressPopup();
          },
        },
      };
    },
    deliveryAddressFormIsDirty() {
      if (!this.model) return false;

      return (
        this.model.DeliveryAddressCompanyName !==
          this.addressData.DeliveryAddressCompanyName ||
        this.model.DeliveryAddressStreetLine1 !==
          this.addressData.DeliveryAddressStreetLine1 ||
        this.model.DeliveryAddressStreetLine2 !==
          this.addressData.DeliveryAddressStreetLine2 ||
        this.model.DeliveryAddressSuburb !==
          this.addressData.DeliveryAddressSuburb ||
        this.model.DeliveryAddressState !==
          this.addressData.DeliveryAddressState ||
        this.model.DeliveryAddressPostcode !==
          this.addressData.DeliveryAddressPostcode ||
        this.model.DeliveryAddressCountry !==
          this.addressData.DeliveryAddressCountry
      );
    },

    // Details Tab
    getColumns() {
      // Forced only outside the Product view — ProductList's order is driven by
      // sortProductListByFactor478() and must not be re-sorted by this column.
      const defaultDescriptionSortOrder =
        this.soDetailsCurrentView == "ProductList" ? undefined : "asc";
      const defaultDescriptionSortIndex =
        this.soDetailsCurrentView == "ProductList" ? undefined : 0;

      return [
        {
          caption: "Product / Account",
          dataField: "Description",
          sortOrder: this.isProductAccountSplit
            ? undefined
            : defaultDescriptionSortOrder,
          sortIndex: this.isProductAccountSplit
            ? undefined
            : defaultDescriptionSortIndex,
          // cssClass: "class-item-cell-template",
          cellTemplate: "item-cell-template",
          // (data, info) => {
          //   return (data.innerHTML = `<div class="custom-auto-complete" @click="clickOnInfo(${info})">
          //               <span class="auto-complete-primary">
          //                 ${info.data.Description1}
          //               </span>
          //               <span class="record-secondary gc-grid-code">
          //                 ${info.data.OriginItemCode}
          //               </span>
          //             </div>`);
          // },
          allowEditing: false,
          minWidth: 420,
          // tabindex: -1,
          visible: !this.isProductAccountSplit,
        },
        {
          caption: "Product / Account",
          dataField: "OriginItemCode",
          cellTemplate: "item-cell-template-code",
          allowEditing: false,
          visible: this.isProductAccountSplit,
          width: ColumnWidth.ProductCode.width,
          minWidth: ColumnWidth.ProductCode.minWidth,
        },
        {
          name: "ProductAccountDescriptionSplit",
          caption: "Description",
          dataField: "Description",
          cellTemplate: "item-cell-template-description",
          sortOrder: this.isProductAccountSplit
            ? defaultDescriptionSortOrder
            : undefined,
          sortIndex: this.isProductAccountSplit
            ? defaultDescriptionSortIndex
            : undefined,
          allowEditing: false,
          visible: this.isProductAccountSplit,
        },
        {
          caption: "",
          dataField: "HasPromotion",
          allowEditing: false,
          allowSorting: false,
          allowSearch: false,
          showInColumnChooser: false,
          allowHiding: false,
          width: 50,
          allowFiltering: false,
          // tabIndex: -1,k
          cellTemplate: (data, info) => {
            if (info.data.HasPromotion)
              return (data.innerHTML = '<i class="dx-icon dx-icon-money"></i>');
            else return data;
          },
        },
        {
          caption: "ItemCode",
          dataField: "ItemCode",
          allowEditing: false,
          visible: false,
          showInColumnChooser: false,
        },
        {
          caption: "UM",
          dataField: "SalesUM",
          // showEditorAlways: this.dataViewType != "review",
          allowEditing: this.dataViewType != "review",
          allowSorting: false,
          cssClass:
            this.baseView.mode != "review"
              ? "edit-cell-textbox-field-1 gc-editable-cell-color"
              : undefined,
          editCellTemplate:
            this.baseView.mode != "review" ? "sales-um-edit-cell-template" : "",
          setCellValue: async (newData, value, currentRowData) => {
            newData.SalesUM = value;
            newData.SalesUMQty = currentRowData.SalesUMQty;
            var obj = { ...currentRowData, ...newData };

            let contractItemModel = false;

            if (
              FeatureStorage.checkPermission(
                "CustomerSalesContract",
                this.baseView.mode,
              )
            ) {
              contractItemModel = await this.tryProcessSalesContracts(
                value,
                currentRowData.SalesUM,
                "SalesUM",
                obj,
              );
            }

            if (currentRowData.SalesUMSuppliedQty > 0) {
              this.focusOnQtyFromSalesUM(currentRowData);
            } else if (
              this.allowPriceChangeTriggerConfirmation &&
              currentRowData.SalesUMQty != 0
            ) {
              let confirmPriceChange = await this.$confirm(
                `Would you like to recalculate the sell price of ${currentRowData.Description} based upon this new UM ?`,
                "Price Change",
              );

              if (confirmPriceChange) {
                this.focusOnQtyFromSalesUM(currentRowData);
                return this.updateThePriceDataOnUMChange(
                  newData,
                  currentRowData,
                  obj,
                  contractItemModel,
                );
              } else {
                this.focusOnQtyFromSalesUM(currentRowData);
              }
            } else {
              return this.updateThePriceDataOnUMChange(
                newData,
                currentRowData,
                obj,
                contractItemModel,
              );
            }
          },
          width: ColumnWidth.UnitOfMeasure.width,
          maxWidth: ColumnWidth.UnitOfMeasure.minWidth,
        },
        // Quantity
        {
          caption: "Quantity",
          dataField: "SalesUMQty",
          allowEditing: this.baseView.mode != "review",
          allowSorting: false,
          allowSearch: false,
          // showEditorAlways: this.baseView.mode != "review",
          format: "##,###,###,###,##0.0000",
          dataType: "number",
          cssClass:
            this.baseView.mode != "review"
              ? "edit-cell-numberbox-field gc-editable-cell-color qty-cell"
              : undefined,
          editorOptions: {
            // tabIndex: 1,
            format: "##,###,###,###,##0.0000",
            elementAttr: {
              class: "gc-grid-number-box-editor",
            },
            onFocusIn: (e, cellInfo) => {
              if (this.focusedCellData) {
                this.removeCellFocus();
              }
              e.event.target.select();
              this.onSalesUMQtyFocusIn(e, cellInfo);
              e.element.parentElement.classList.add("key-pad-focused-cell");
            },
            onKeyDown: (e, cellInfo) => {
              // TabKey
              if (e.event.keyCode == 9 && e.event.shiftKey) {
                var dataGrid = this.$refs["dx-data-grid-comp"].instance;

                if (cellInfo.data.SLUMLength == 1) {
                  e.event.preventDefault();

                  // getVisibleRows() is what DevExtreme's own editCell()
                  // indexes against internally (_dataController.items()) -
                  // reading it here keeps our row index in lockstep with
                  // what's actually rendered, whether the active view is a
                  // small search-filtered picker result or a large
                  // virtual-scrolled unfiltered catalog (getDataSource()
                  // .items() diverges from the render state in the latter
                  // case and produced a stale index).
                  const visibleRows = dataGrid.getVisibleRows();
                  const index = visibleRows.findIndex(
                    (r) =>
                      r.rowType === "data" && r.data.ID == cellInfo.data.ID,
                  );

                  if (index > 0) {
                    // need to find previous row which is does not have a WorkOrderCode until 0 index
                    let prevRowIndex = index - 1;
                    while (
                      prevRowIndex >= 0 &&
                      visibleRows[prevRowIndex].rowType === "data" &&
                      visibleRows[prevRowIndex].data.WorkOrderCode
                    ) {
                      prevRowIndex--;
                    }

                    const prevRow = visibleRows[prevRowIndex];
                    if (
                      prevRowIndex < 0 ||
                      !prevRow ||
                      prevRow.rowType !== "data"
                    ) {
                      this.removeGridFocusAndSetFormFocus(dataGrid);
                    } else {
                      var cellElement = dataGrid.getCellElement(
                        prevRowIndex,
                        "DiscPcnt",
                      );
                      dataGrid.focus(cellElement);
                      dataGrid.editCell(prevRowIndex, "DiscPcnt");
                    }
                  } else {
                    this.removeGridFocusAndSetFormFocus(dataGrid);
                  }
                }
              }
            },
          },
          setCellValue: async (newData, value, currentRowData) => {
            if (value === currentRowData.SalesUMQty) return;
            // Per-row (FDT70523 fix): only cancel this line's own pending
            // price/tax lookup, not every other in-flight line's.
            if (this.priceLookupTimeouts[currentRowData.ID]) {
              clearTimeout(this.priceLookupTimeouts[currentRowData.ID]);
            }

            if (
              currentRowData.SalesUMSuppliedQty > 0 &&
              value < currentRowData.SalesUMSuppliedQty
            ) {
              newData.SalesUMQty = currentRowData.SalesUMQty;
              this.qtyChangeWarning(currentRowData);
            } else {
              newData.SalesUMQty = value;
            }

            // Qty 0 -> >0: the line should inherit the header Discount %, if one is set.
            // Flows through the pricing lookup/merge below — applied once a final price is known.
            newData.InheritHeaderDiscount =
              currentRowData.SalesUMQty == 0 && newData.SalesUMQty > 0;

            newData.DiscPcntPreviousValue = 0;
            newData.TiggerPriceLookUp = true;
            newData.TriggerPriceUpdate = false;
            newData.TriggerDiscountUpdate = false;

            // if (!currentRowData.SalesUMFXPricePreviousValue)
            //   newData.TiggerPriceLookUp = false;

            var obj = { ...currentRowData, ...newData };

            let contractItemModel = false;

            if (
              FeatureStorage.checkPermission(
                "CustomerSalesContract",
                this.baseView.mode,
              )
            ) {
              contractItemModel = await this.tryProcessSalesContracts(
                value,
                currentRowData.SalesUMQty,
                "SalesUMQty",
                obj,
              );
            }

            newData.customerContractItemsForProduct = contractItemModel;

            if (currentRowData.SalesUMSuppliedQty > 0) {
              return this.updateLineTotals(newData, currentRowData);
            } else if (
              this.allowPriceChangeTriggerConfirmation &&
              currentRowData.FXPriceBeforeUpdate
            ) {
              let confirmPriceChange = await this.$confirm(
                `Would you like to recalculate the sell price of ${currentRowData.Description} based upon this new Quantity ?`,
                "Price Change",
              );

              // Skipped when the user has pressed F2 — they asked to be back
              // in the search box, so don't pull them into the price cell.
              if (!this.searchFocusRequested) {
                var dataGrid = this.$refs["dx-data-grid-comp"].instance;
                var rowIndex = dataGrid.getRowIndexByKey(currentRowData.ID);
                dataGrid.editCell(rowIndex, "SalesUMFXPrice");
              }

              if (confirmPriceChange) {
                this.setUpdatePriceData(newData, obj);
              } else {
                newData.FXPriceBeforeUpdate = currentRowData.SalesUMFXPrice;
                return this.updateLineTotals(newData, currentRowData);
              }
            } else {
              return this.setUpdatePriceData(newData, obj);
            }
          },
          width: ColumnWidth.Quantity4DecimalPoints.width,
          maxWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
        },
        {
          caption: "Delivered",
          dataField: "SalesUMSuppliedQty",
          allowEditing: false,
          visible: this.baseView.mode != "create",
          showInColumnChooser: false,
          format: "##,###,###,###,##0.00",
          width: ColumnWidth.Quantity.width,
          maxWidth: ColumnWidth.Quantity.minWidth,
        },
        {
          caption: `Price (${this.model.CurrencyCode})`,
          dataField: "SalesUMFXPrice",
          allowEditing:
            this.baseView.mode != "review" &&
            FeatureStorage.checkPermission("SalesOrderDetailEditPrice", "edit"),
          // showEditorAlways: this.baseView.mode != "review",
          allowSorting: false,
          allowSearch: false,
          format: "##,###,###,###,##0.0000",
          dataType: "number",
          cssClass:
            this.baseView.mode != "review" &&
            FeatureStorage.checkPermission("SalesOrderDetailEditPrice", "edit")
              ? "edit-cell-numberbox-field gc-editable-cell-color"
              : undefined,
          editorOptions: {
            // valueChangeEvent: "input",
            // tabIndex: 2,
            format: "##,###,###,###,##0.0000",
            elementAttr: {
              class: "gc-grid-number-box-editor",
            },
            mode: "number",
            onFocusIn: (e, cellInfo) => {
              if (this.focusedCellData) {
                this.removeCellFocus();
              }
              e.element.parentElement.classList.add("key-pad-focused-cell");
              e.event.target.select();
              this.onSalesUMQtyFocusIn(e, cellInfo);
            },
          },
          setCellValue: async (newData, value, currentRowData) => {
            logDev(
              "SalesUMFXPrice setCellValue currentRowData",
              currentRowData,
            );

            if (value === currentRowData.SalesUMFXPrice) return;

            try {
              var i = this.reviewDataList._array.findIndex(
                (x) => x.ID == currentRowData.ID,
              );

              if (i >= 0) {
                let record = this.reviewDataList._array[i];
                currentRowData.CustomerSalesContractCode =
                  record.CustomerSalesContractCode;
                currentRowData.CustomerSalesContractLineNo =
                  record.CustomerSalesContractLineNo;
              }
            } catch (error) {
              // eslint-disable-next-line no-console
              console.log("CX Log >>> Error", error);
            }

            if (this.priceUpdateTimeOut) clearTimeout(this.priceUpdateTimeOut);

            newData.SalesUMFXPrice = value;
            newData.TiggerPriceLookUp = false;
            newData.TriggerPriceUpdate = true;
            newData.TriggerDiscountUpdate = false;

            // this.updateDataSource(newData, currentRowData);

            // setTimeout(() => {
            this.setPriceUpdate(newData, value, currentRowData);
            // }, 1);
          },
          width: ColumnWidth.Price4DecimalPoints.width,
          maxWidth: ColumnWidth.Price4DecimalPoints.minWidth,
        },
        {
          caption: "Discount",
          dataField: "DiscPcnt",
          allowEditing:
            this.baseView.mode != "review" &&
            FeatureStorage.checkPermission(
              "SalesOrderDetailEditDiscount",
              "edit",
            ),
          visible: FeatureStorage.checkPermission(
            "SalesOrderDetailEditDiscount",
            "edit",
          ),
          showInColumnChooser: FeatureStorage.checkPermission(
            "SalesOrderDetailEditDiscount",
            "edit",
          ),
          allowSorting: false,
          allowSearch: false,
          format: "##0.00 '%'",
          cssClass:
            this.baseView.mode != "review" &&
            FeatureStorage.checkPermission(
              "SalesOrderDetailEditDiscount",
              "edit",
            )
              ? "edit-cell-numberbox-field gc-editable-cell-color"
              : undefined,
          editorOptions: {
            // tabIndex: 3,
            format: "##0.00 '%'",
            elementAttr: {
              class: "gc-grid-number-box-editor",
            },
            onFocusIn: (e, cellInfo) => {
              if (this.focusedCellData) {
                this.removeCellFocus();
              }
              e.element.parentElement.classList.add("key-pad-focused-cell");
              e.event.target.select();
              this.onSalesUMQtyFocusIn(e, cellInfo);
            },
            onKeyDown: (e, cellInfo) => {
              // TabKey
              if (e.event.keyCode == 9 && !e.event.shiftKey) {
                e.event.preventDefault();
                var dataGrid = this.$refs["dx-data-grid-comp"].instance;

                // getVisibleRows() is what DevExtreme's own editCell()
                // indexes against internally (_dataController.items()) -
                // reading it here keeps our row index in lockstep with
                // what's actually rendered. This column is shared across
                // Review/ProductList/CustomerList (getColumns() is one
                // array for all three); getDataSource().items() (the raw
                // store cache) diverges from the render state on a large
                // virtual-scrolled unfiltered catalog, which was producing
                // a stale index and crashing dataGrid.editCell() with
                // "Cannot read properties of undefined (reading 'key')".
                const visibleRows = dataGrid.getVisibleRows();
                const index = visibleRows.findIndex(
                  (r) => r.rowType === "data" && r.data.ID == cellInfo.data.ID,
                );

                if (index >= 0) {
                  // need to find next row which is does not have a WorkOrderCode until end of the list
                  let nextRowIndex = index + 1;
                  while (
                    nextRowIndex < visibleRows.length &&
                    visibleRows[nextRowIndex].rowType === "data" &&
                    visibleRows[nextRowIndex].data.WorkOrderCode
                  ) {
                    nextRowIndex++;
                  }

                  const nextRow = visibleRows[nextRowIndex];
                  if (!nextRow || nextRow.rowType !== "data") {
                    e.component.blur();
                    setTimeout(() => {
                      this.removeGridFocusAndSetFormFocus(dataGrid);
                    }, 1);
                  } else {
                    const dataField =
                      nextRow.data.SLUMLength == 1 ? "SalesUMQty" : "SalesUM";

                    var cellElement = dataGrid.getCellElement(
                      nextRowIndex,
                      dataField,
                    );
                    dataGrid.focus(cellElement);
                    dataGrid.editCell(nextRowIndex, dataField);
                  }
                } else {
                  e.component.blur();
                  setTimeout(() => {
                    this.removeGridFocusAndSetFormFocus(dataGrid);
                  }, 1);
                }
              }
            },
          },
          setCellValue: async (newData, value, currentRowData) => {
            if (this.discountTimeOut) clearTimeout(this.discountTimeOut);

            // this.$nextTick(() => {
            //   setTimeout(() => {
            //     this.applyFocusClass();
            //   }, 10);
            // });

            newData.DiscPcnt = value;

            var dataGrid = this.$refs["dx-data-grid-comp"].instance;

            // productDataSourceItems/customerDataSourceItems have no
            // .update() method (server-paged CustomStore, load() only) -
            // saveEditData() against a row still backed by the picker
            // throws DevExtreme E4011. Review rows (reviewDataList's
            // ArrayStore) support saveEditData() fine.
            if (
              this.soDetailsCurrentView != "CustomerList" &&
              this.soDetailsCurrentView != "ProductList"
            ) {
              dataGrid.saveEditData();
            }

            // ADO #78300 (FDT70662): used to be gated on SalesUMQty > 0, which
            // meant a Discount % edit on a still-zero-qty row never ran the
            // min-margin check at all. Always run it — onDiscountPrctUpdate
            // already no-ops its blocking confirmation when Qty is 0.
            newData.TiggerPriceLookUp = false;
            newData.TriggerPriceUpdate = false;
            newData.TriggerDiscountUpdate = true;

            // this.updateDataSource(newData, currentRowData);

            this.setDiscountUpdate(newData, value, currentRowData);
          },
          width: ColumnWidth.Discount.width,
          maxWidth: ColumnWidth.Discount.minWidth,
        },
        {
          caption: `Net Price (${this.model.CurrencyCode})`,
          // ADO #77292: was "SalesUMNetPrice" (base-currency, exchange-rate-
          // divided) — wrong field for a column labelled in the order's own
          // currency. "Price"/"Ex Total" use their FX-suffixed sibling; this
          // column must too.
          dataField: "SalesUMNetFXPrice",
          allowEditing: false,
          allowSorting: false,
          allowSearch: false,
          format: "##,###,###,###,##0.0000",
          dataType: "number",
          width: ColumnWidth.Price4DecimalPoints.width,
          maxWidth: ColumnWidth.Price4DecimalPoints.minWidth,
        },
        {
          caption: "GP %",
          dataField: "gpprcnt",
          allowSorting: false,
          allowSearch: false,
          visible: false,
          showInColumnChooser: false,
        },
        // {
        //   caption: 'Discount',
        // },
        // {
        //   caption: 'Net',
        // },
        {
          caption: `Ex Total (${this.model.CurrencyCode})`,
          dataField: "TaxableFXLineTotal",
          allowEditing: false,
          format: "##,###,###,###,##0.00",
          allowSorting: false,
          allowSearch: false,
          width: ColumnWidth.Price2DecimalPoints.width,
          maxWidth: ColumnWidth.Price2DecimalPoints.minWidth,
        },
        {
          caption: `Tax (${this.model.CurrencyCode})`,
          dataField: "TaxFXLineTotal",
          allowEditing: false,
          format: "##,###,###,###,##0.00",
          allowSorting: false,
          allowSearch: false,
          width: ColumnWidth.Price2DecimalPoints.width,
          maxWidth: ColumnWidth.Price2DecimalPoints.minWidth,
        },
        {
          caption: `Inc Total (${this.model.CurrencyCode})`,
          dataField: "FXLineTotal",
          allowEditing: false,
          format: "##,###,###,###,##0.00",
          allowSorting: false,
          allowSearch: false,
          width: ColumnWidth.Price2DecimalPoints.width,
          maxWidth: ColumnWidth.Price2DecimalPoints.minWidth,
        },
        // Available
        {
          caption: "Available",
          dataField: "Available",
          allowEditing: false,
          format: "##,###,###,###,##0.0000",
          cellTemplate: "available-cell-template",
          allowSearch: false,
          width: ColumnWidth.Quantity4DecimalPoints.width,
          maxWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
        },
        {
          caption: "",
          dataField: "PSlipStatus",
          allowEditing: false,
          visible: this.baseView.mode != "create",
          cssClass: "pslip-status-cell",
          showInColumnChooser: false,
          cellTemplate: (data, info) => {
            if (
              info.data.SalesUMSuppliedQty == info.data.SalesUMQty &&
              info.data.SalesUMQty != 0
            )
              return (data.innerHTML =
                '<i class="pslip-status green fa-solid fa-clipboard-check"></i>');
            else if (
              info.data.PSlipStatus == "A" &&
              info.data.SalesUMSuppliedQty == 0
            )
              return (data.innerHTML =
                '<i class="pslip-status grey fa-solid fa-clipboard-check"></i>');
            else if (info.data.SalesUMSuppliedQty == 0)
              return (data.innerHTML = "");
            else if (info.data.PSlipStatus != "C")
              return (data.innerHTML =
                '<i class="pslip-status orange fa-solid fa-clipboard-check"></i>');
            else if (
              info.data.PSlipStatus == "C" &&
              info.data.SalesUMSuppliedQty == info.data.SalesUMQty
            )
              return (data.innerHTML =
                '<i class="pslip-status green fa-solid fa-clipboard-check"></i>');
            else if (
              info.data.PSlipStatus == "C" &&
              info.data.SalesUMSuppliedQty != info.data.SalesUMQty
            )
              return (data.innerHTML =
                '<i class="pslip-status red fa-solid fa-clipboard-check"></i>');
            else return data;
          },
          width: ColumnWidth.PSlipStatus.width,
          maxWidth: ColumnWidth.PSlipStatus.minWidth,
        },
        {
          caption: "",
          dataField: "",
          width: 30,
          minWidth: 30,
          maxWidth: 30,
          allowEditing: false,
          cellTemplate: "menu-cell-template",
          alignment: "left",
          cssClass: "more-menu",
          fixedPosition: "right",
          fixed: true,
          allowFixing: true,
          allowReordering: false,
          allowSorting: false,
          allowSearch: false,
          allowFiltering: false,
          allowGrouping: false,
        },
      ];
    },
    getDataSource() {
      if (this.soDetailsCurrentView == "ProductList")
        return this.sortProductListByFactor478(
          this.productListPostProcess(this.productDataSourceItems._array),
        );
      // return this.getProductList;
      else if (this.soDetailsCurrentView == "CustomerList") {
        return this.productListPostProcess(this.customerDataSourceItems._array);
        // return this.getCustomerList;
      } else return this.reviewDataList._array;
    },
    getGridHeight() {
      return this.gridHeight - 20 - 40 - 55 - this.headerFormHeight;
      // 20 => Top & Bottom Padding
      // 40 => Footer Height
      // 55 => Toolbar Height + Toolbar bottom padding
    },
    // getProductList() {
    //   var filter = [
    //     [
    //       ["ItemType", "=", "S"],
    //       "and",
    //       ["IsDefault", "=", "Y"],
    //       "and",
    //       ["AvailableForSale", "=", "Y"],
    //     ],
    //     "and",
    //     [
    //       ["CustomerCode", "=", this.toolbarModel.CustomerCode],
    //       "and",
    //       ["Inactive", "=", "N"],
    //     ],
    //   ];
    //   if (this.model.BranchCode) {
    //     filter[2].push("and");
    //     filter[2].push(["BranchCode", "=", this.model.BranchCode]);
    //   }
    //   return SalesOrderService.dataSourceConfig(
    //     "api/ProductFilterGroupSaleItemCompanyBranch",
    //     {
    //       filter: filter,
    //       count: true,
    //       key: "ItemCode",
    //       postProcess: (data) => {
    //         var items = [];
    //         data.forEach((element) => {
    //           element.Origin = "Product";
    //           element.SalesUMQty = 0;
    //           element.SalesUMFXPrice = 0;
    //           element.TaxableFXLineTotal = 0;
    //           element.TaxFXLineTotal = 0;
    //           element.FXLineTotal = 0;
    //           element.HasPromotion = false;
    //           element = { ...soDetailsBaseRecord, ...element };

    //           element.ID = element.ItemCode;
    //           element.OriginItemCode = element.ItemCode;
    //           element.ProductCode = element.ItemCode;

    //           var i = this.reviewDataList._array.findIndex(
    //             (x) => x.ID == element.ID
    //           );

    //           var item = undefined;
    //           var ums = [];
    //           if (i == -1) item = element;
    //           else {
    //             this.updateReviewList({
    //               HasPromotion: false,
    //               ID: this.reviewDataList._array[i].ID,
    //             });
    //             item = this.reviewDataList._array[i];
    //           }

    //           ums = this.salesUMsLocalDictionary[item.ItemCode]
    //             ? this.salesUMsLocalDictionary[item.ItemCode]
    //             : [];

    //           item.SLUMLength = ums.length;
    //           item.SalesUMs = ums;

    //           items.push(item);

    //           this.localCopyItems.forEach((copy) => {
    //             if (copy.OriginItemCode == item.ItemCode) {
    //               var c_i = this.reviewDataList._array.findIndex(
    //                 (x) => x.ID == copy.ID
    //               );

    //               if (c_i != -1) {
    //                 this.updateReviewList({
    //                   HasPromotion: false,
    //                   ID: this.reviewDataList._array[c_i].ID,
    //                 });
    //                 items.push(this.reviewDataList._array[c_i]);
    //               } else {
    //                 items.push(copy);
    //               }
    //             }
    //           });
    //         });

    //         this.setItemPromotions(items);
    //         return items;
    //       },
    //     }
    //   );
    // },
    getCustomerList() {
      var filter = [
        [["ItemType", "=", "S"], "and", ["IsDefault", "=", "Y"]],
        "and",
        [
          ["CustomerCode", "=", this.toolbarModel.CustomerCode],
          "and",
          ["Inactive", "=", "N"],
        ],
      ];
      if (this.model.BranchCode) {
        filter[2].push("and");
        filter[2].push(["BranchCode", "=", this.model.BranchCode]);
      }

      return SalesOrderService.dataSourceConfig(
        "api/CustomerSaleItemCompanyBranch",
        {
          key: "ItemCode",
          filter: filter,
          postProcess: (data) => {
            var items = [];
            data.forEach((element) => {
              element.Origin = "Customer";
              element.SalesUMQty = 0;
              element.SalesUMFXPrice = 0;
              element.TaxableFXLineTotal = 0;
              element.TaxFXLineTotal = 0;
              element.FXLineTotal = 0;
              element.HasPromotion = false;

              element = { ...soDetailsBaseRecord, ...element };

              element.ID = element.ItemCode;
              element.OriginItemCode = element.ItemCode;
              element.ProductCode = element.ItemCode;

              var i = this.reviewDataList._array.findIndex(
                (x) => x.ID == element.ID || x.ProductCode == element.ID,
              );

              var item = undefined;
              var ums = [];
              if (i == -1) item = element;
              else {
                this.updateReviewList({
                  HasPromotion: false,
                  ID: this.reviewDataList._array[i].ID,
                });
                item = this.reviewDataList._array[i];
                // return;
              }

              ums = this.salesUMsLocalDictionary[item.ItemCode]
                ? this.salesUMsLocalDictionary[item.ItemCode]
                : [];
              item.SLUMLength = ums.length;
              item.SalesUMs = ums;

              // if(item.ItemCode != "CON10PETPOUCH")
              items.push(item);

              this.localCopyItems.forEach((copy) => {
                if (
                  copy.OriginItemCode == item.ItemCode ||
                  copy.ProductCode == item.OriginItemCode
                ) {
                  var c_i = this.reviewDataList._array.findIndex(
                    (x) => x.ID == copy.ID,
                  );

                  if (c_i != -1) {
                    this.updateReviewList({
                      HasPromotion: false,
                      ID: this.reviewDataList._array[c_i].ID,
                    });
                    items.push(this.reviewDataList._array[c_i]);
                  } else {
                    items.push(copy);
                  }
                }
              });
            });
            this.setItemPromotions(items);
            return items;
          },
        },
      );
    },
    getDefaultFilters() {
      if (this.soDetailsCurrentView == "ProductList") {
        let filter = [
          [
            ["ItemType", "=", "S"],
            "and",
            ["IsDefault", "=", "Y"],
            "and",
            ["AvailableForSale", "=", "Y"],
          ],
          "and",
          [
            ["CustomerCode", "=", this.toolbarModel.CustomerCode],
            "and",
            ["Inactive", "=", "N"],
          ],
        ];
        if (this.model.BranchCode) {
          filter[2].push("and");
          filter[2].push(["BranchCode", "=", this.model.BranchCode]);
        }

        return filter;
      } else if (this.soDetailsCurrentView == "CustomerList") {
        let filter = [
          [["ItemType", "=", "S"], "and", ["IsDefault", "=", "Y"]],
          "and",
          [
            ["CustomerCode", "=", this.toolbarModel.CustomerCode],
            "and",
            ["Inactive", "=", "N"],
          ],
        ];
        if (this.model.BranchCode) {
          filter[2].push("and");
          filter[2].push(["BranchCode", "=", this.model.BranchCode]);
        }

        return filter;
      } else {
        return undefined;
      }
    },
    getPersistenceKey() {
      if (this.soDetailsCurrentView == "Review") {
        return (
          "sales-order-" +
          this.soDetailsCurrentView +
          "_" +
          appConfig.VUE_APP_COOKIE_NAME
        );
      } else {
        return "sales-order-details_" + appConfig.VUE_APP_COOKIE_NAME;
      }
    },
    getFilterPersistenceKey() {
      // return "sales-order-" + this.soDetailsCurrentView;
      if (this.soDetailsCurrentView == "Review") {
        return "sales-order-" + this.soDetailsCurrentView;
      } else {
        return "sales-order-details";
      }
    },
    lineCommentItems() {
      return [
        {
          itemType: "group",
          caption: "",
          colCount: 1,
          items: [
            {
              itemType: "group",
              caption: "",
              items: [
                {
                  dataField: "Comment",
                  editorType: "dxTextArea",
                  label: { visible: false },
                  editorOptions: {
                    height: "140",
                    maxLength: 1000,
                  },
                },
                {
                  dataField: "IsCommentVisible",
                  label: { text: "Visible On Invoice" },
                  editorType: "dxSwitch",
                },
              ],
            },
          ],
        },
      ];
    },
    customerItemHistoryDataSourceConfig() {
      if (!this.toolbarModel.CustomerCode || !this.model.LineItemCode) {
        return;
      } else {
        return SalesItemHistoryService.dataSourceConfig({
          // select: ["Note", "LineNo", "SalesOrderCode"],
          sort: [{ selector: "SalesOrderCode", desc: true }],
          filter: [
            ["CustomerCode", "=", this.toolbarModel.CustomerCode],
            "and",
            ["ItemType", "=", "S"],
            "and",
            ["ItemCode", "=", this.model.LineItemCode],
          ],
        });
      }
    },
    customerItemHistoryColumns() {
      return [
        {
          caption: "Order",
          dataField: "SalesOrderCode",
          alignment: "left",
          width: 130,
        },
        {
          caption: "Date",
          dataField: "DateIn",
          width: 150,
          cellTemplate: "date-in-cell-template",
          allowFiltering: false,
        },
        {
          caption: "Qty",
          dataField: "SalesUMQty",
          alignment: "right",
          format: getDecimalFormat("Decimal-16-2").ReadOnlyFormat,
          width: 150,
        },
        {
          caption: "UM",
          dataField: "SalesUM",
          alignment: "left",
          width: 100,
        },
        {
          caption: "Unit Price",
          dataField: "SalesUMNetPrice",
          alignment: "right",
          // format: this.getDecimalFormatByCode(13).Format,
          format: getDecimalFormat("Decimal-16-4C").ReadOnlyFormat,
          width: 150,
        },
        {
          caption: "Status",
          dataField: "FriendlyStatus",
          cellTemplate: "historyStatusTemplate",
          width: 100,
        },
        {
          caption: "Price Group",
          dataField: "PriceGroupDescription",
          alignment: "left",
        },
      ];
    },

    // GP Tool
    getGPToolFormItems() {
      var items = [
        {
          itemType: "group",
          caption: "",
          colCount: 2,
          items: [
            {
              itemType: "group",
              caption: "",
              colCound: 1,
              items: [
                {
                  dataField: "quantity",
                  editorType: "dxNumberBox",
                  editorOptions: {
                    readOnly: true,
                  },
                },
                {
                  dataField: "standardSell",
                  label: {
                    text: this.getStandardSellLabel,
                  },
                  editorType: "dxNumberBox",
                  editorOptions: {
                    format: getDecimalFormat("Decimal-16-4C").ReadOnlyFormat,
                    readOnly: true,
                  },
                },
                {
                  dataField: "currentPrice",
                  label: {
                    text: this.getCurrentPriceLabel,
                  },
                  editorType: "dxNumberBox",
                  editorOptions: {
                    format: getDecimalFormat("Decimal-16-4C").ReadOnlyFormat,
                    readOnly: true,
                  },
                },
              ],
            },
            {
              itemType: "group",
              caption: "",
              colCound: 1,
              items: [
                {
                  dataField: "gp",
                  label: {
                    text: "GP %",
                  },
                  editorType: "dxNumberBox",
                  editorOptions: {
                    format: getDecimalFormat("Decimal-16-4").EditFormat,
                    onChange: this.gpPercentageChange.bind(this),
                    onValueChanged: this.onGpValueChanged,
                    onFocusIn: (e) => {
                      e.event.target.select();
                    },
                  },
                },
                {
                  dataField: "newSalesUMNetPrice",
                  label: {
                    text: this.getGPToolNewPriceLable,
                  },
                  editorType: "dxNumberBox",
                  editorOptions: {
                    format: getDecimalFormat("Decimal-16-4C").EditFormat,
                    onFocusIn: (e) => {
                      e.event.target.select();
                    },
                    onChange: this.gpNewPriceChange.bind(this),
                  },
                },
              ],
            },
          ],
        },
      ];

      return items;
    },
    getStandardSellLabel() {
      return `Std Sell (${this.model.CurrencyCode})`;
    },
    getCurrentPriceLabel() {
      return `Current Price (${this.model.CurrencyCode})`;
    },
    getGPToolNewPriceLable() {
      return `New Price (${this.model.CurrencyCode})`;
    },

    // Item Details
    productDetailsItems() {
      return [
        {
          itemType: "group",
          caption: "",
          colCount: 1,
          items: [
            {
              dataField: "apn",
              label: {
                text: "APN",
              },
              editorType: "dxTextBox",
            },
            {
              dataField: "standardSell",
              // gcType: "Decimal-4-2C",
              label: {
                text: "Standard Sell",
              },
              editorType: "dxNumberBox",
              editorOptions: {
                format: getDecimalFormat("Decimal-16-2C").ReadOnlyFormat,
                readOnly: true,
              },
            },
            {
              dataField: "customerSell",
              // gcType: "Decimal-4-2C",
              label: {
                text: "Customer Sell",
              },
              editorType: "dxNumberBox",
              editorOptions: {
                format: getDecimalFormat("Decimal-16-2C").ReadOnlyFormat,
                readOnly: true,
              },
            },
            {
              dataField: "promotionStartDate",
              label: {
                text: "Promotion Start Date",
              },
              // gcType: "Date",
              editorType: "dxDateBox",
              visible: this.productDetails.hasPromotion,
              editorOptions: {
                displayFormat: "dd MMM yyyy",
              },
            },
            {
              dataField: "promotionEndDate",
              label: "Promotion End Date",
              // gcType: "Date",
              editorType: "dxDateBox",
              visible: this.productDetails.hasPromotion,
              editorOptions: {
                displayFormat: "dd MMM yyyy",
              },
            },
            {
              dataField: "cartonQty",
              label: {
                text: "Carton Quantity",
              },
              // gcType: "String",
              editorType: "dxTextBox",
            },
            {
              dataField: "salesUM",
              label: {
                text: "Sales UM",
              },
              // gcType: "String",
              editorType: "dxTextBox",
            },
          ],
        },
      ];
    },

    // Footer
    getFooterItems() {
      return [
        {
          screenField: "SalesUMItemCount",
          dataField: "SupplierRequired",
          location: "left",
          label: "Line Count",
          value: 0,
          customCalc: (headerData, gridItems) => {
            var count = 0;
            gridItems.forEach((element) => {
              if (element.ItemCode) count++;
            });

            return count;
          },
        },
        {
          screenField: "SalesUMItemCount",
          dataField: "SalesUMQty",
          location: "left",
          value: 0,
        },
        {
          screenField: "FXLineTotal",
          dataField: "TaxableFXLineTotal",
          location: "right",
          value: 0,
          format: "Decimal-16-4C",
          label: `Sub Total (${this.model.CurrencyCode})`,
        },
        {
          screenField: "TaxFXTotal",
          dataField: "TaxFXLineTotal",
          location: "right",
          value: 0,
          format: "Decimal-16-4C",
          label: `Tax (${this.model.CurrencyCode})`,
        },
        {
          screenField: "FXTotal",
          dataField: "FXLineTotal",
          location: "right",
          value: 0,
          format: "Decimal-16-4C",
          label: `Total (${this.model.CurrencyCode})`,
        },
      ];
    },

    footerLeftItems() {
      var leftItems = this.footerItems.filter((x) => x.location == "left");

      return leftItems
        .map((c) => {
          let globalField = { ...getGlobalField(c.screenField) };
          if (Object.keys(globalField).length == 0) return c;

          if (c.value != undefined) globalField.value = c.value;
          if (c.dataField != undefined) globalField.dataField = c.dataField;
          if (c.label != undefined) globalField.label = c.label;
          if (c.format != undefined) globalField.format = c.format;
          if (c.fieldType != undefined) globalField.fieldType = c.fieldType;
          if (c.length != undefined) globalField.length = c.length;
          if (globalField.type === "Decimal") {
            globalField.label = globalField.label.replace(
              "(Customer Currency)",
              `(${this.model.CurrencyCode})`,
            );
          }
          if (globalField.type === "Decimal") {
            globalField.label = globalField.label.replace(
              "(Customer Currency)",
              `(${this.model.CurrencyCode})`,
            );
          }
          if (globalField) return globalField;
          else return c;
        })
        .filter((x) => x != null);
    },
    footerRightItems() {
      var rightItems = this.footerItems.filter(
        (x) =>
          x.location == "right" &&
          (x.hasOwnProperty("visible") ? x.visible : true),
      );

      return rightItems
        .map((c) => {
          let globalField = { ...getGlobalField(c.screenField) };

          if (Object.keys(globalField).length == 0) return c;

          if (c.value != undefined) globalField.value = c.value;
          if (c.dataField != undefined) globalField.dataField = c.dataField;
          if (c.label != undefined) globalField.label = c.label;
          if (c.format != undefined) globalField.format = c.format;

          if (globalField.type === "Decimal") {
            globalField.label = globalField.label.replace(
              "(Customer Currency)",
              `(${this.model.CurrencyCode})`,
            );
          }

          if (globalField) return globalField;
          else return c;
        })
        .filter((x) => x != null);
    },

    // filter side panel
    filterPanelTabOptions() {
      return {
        dataSource: [
          {
            id: 0,
            text: "Filters",
          },
          {
            id: 1,
            text: "Number Pad",
          },
        ],
        onItemClick: this.changeFilterPanelTab,
        selectedIndex: this.sidebarTabSelectedIndex,
      };
    },
    filterFormItems() {
      return [
        {
          screenField: "ProductCategory1",
          touchEnabled: false,
        },
        {
          screenField: "ProductCategory2",
          touchEnabled: false,
        },
        {
          screenField: "ProductCategory3",
          touchEnabled: false,
        },
        {
          screenField: "ProductCategory4",
          touchEnabled: false,
        },
        {
          screenField: "ProductType",
          touchEnabled: false,
        },
      ];
    },
    customerCodeComputed() {
      return this.toolbarModel.CustomerCode;
    },
  },
  methods: {
    sortProductListByFactor478(items) {
      var factor478 = getFactorByCode(478);
      var factorValue =
        factor478 && factor478.Factorc ? factor478.Factorc.trim() : "";

      if (!factorValue) return this.sortByDescriptionAsc(items);

      var sortFields = [];

      factorValue
        .split(",")
        .map((key) => key.trim())
        .filter((key) => key)
        .forEach((key) => {
          var normalizedKey = key.replace(/\s+/g, "").toUpperCase();

          if (
            !Object.prototype.hasOwnProperty.call(
              factor478SortKeyFieldMap,
              normalizedKey,
            )
          ) {
            this.$notify(
              `Factor 478: unrecognised product sort key "${key}" — skipped.`,
              "error",
            );
          } else {
            var field = factor478SortKeyFieldMap[normalizedKey];
            // A null field is a recognised key with no backing data on this grid
            // today (e.g. Bin Code, History 1-4) — no-op tier, not an error.
            if (field) sortFields.push(field);
          }
        });

      if (!sortFields.length) return this.sortByDescriptionAsc(items);

      return items.sort((a, b) => {
        for (var i = 0; i < sortFields.length; i++) {
          var result = this.compareFactor478SortValues(
            a[sortFields[i]],
            b[sortFields[i]],
            sortFields[i],
          );
          if (result !== 0) return result;
        }
        return 0;
      });
    },
    sortByDescriptionAsc(items) {
      return items.sort((a, b) =>
        (a.Description || "").localeCompare(b.Description || ""),
      );
    },
    compareFactor478SortValues(a, b, field) {
      if (factor478NumericSortFields.indexOf(field) !== -1) {
        var numericA = typeof a == "number" ? a : parseFloat(a);
        var numericB = typeof b == "number" ? b : parseFloat(b);
        if (isNaN(numericA)) numericA = 0;
        if (isNaN(numericB)) numericB = 0;
        return numericA - numericB;
      }

      return String(a || "").localeCompare(String(b || ""));
    },
    getAvailableCellShading(data) {
      // No branch selected means Available isn't meaningful yet — matches the
      // prior 2-tier implementation's same guard.
      if (!this.model.BranchCode) return "";

      var rop = this.getRopForAvailableShading(data);
      // Rop is an optional LEFT JOIN column — null/undefined means no tier can
      // be determined, so the cell gets no shading at all (BR-2082-5).
      if (rop === null || rop === undefined) return "";

      if (data.Available <= 0) return "rgba(232, 56, 79, 0.1)"; // red
      if (data.Available <= rop) return "rgba(230, 126, 34, 0.1)"; // orange — distinct hue from Inshell Lot Allocation's "Accepted" rgba(255, 165, 0, 0.1)
      return "rgba(98, 210, 111, 0.1)"; // green
    },
    getRopForAvailableShading(data) {
      if (data.Rop !== null && data.Rop !== undefined) return data.Rop;

      // Customer view (api/CustomerSaleItemCompanyBranch) and Review view
      // (api/SalesOrderDetailCompanyBranch) rows don't carry Rop directly —
      // only the Product view's api/ProductFilterGroupSaleItemCompanyBranch
      // does. loadProductItems/loadCustomerItems always load together
      // regardless of which view is active, so fall back to the Product
      // catalog's copy of the same item, matched by ItemCode.
      var catalogItem = this.productDataSourceItems._array.find(
        (p) => p.ItemCode == data.ItemCode,
      );

      return catalogItem ? catalogItem.Rop : null;
    },
    // loadProductItems/loadCustomerItems insert directly into the grid's
    // bound ArrayStore, which doesn't push a re-render on its own — cells
    // already painted by onCellPrepared (e.g. Available shading) stay stale
    // until something repaints them. Call this once the source the shading
    // depends on (the Rop-carrying product catalog) has arrived.
    refreshAvailableShading() {
      var dataGrid =
        this.$refs["dx-data-grid-comp"] &&
        this.$refs["dx-data-grid-comp"].instance;
      if (!dataGrid) return;

      var rowIndexes = dataGrid.getVisibleRows().map((r) => r.rowIndex);
      if (rowIndexes.length) dataGrid.repaintRows(rowIndexes);
    },
    getDeliverToItemSecondaryLines(data) {
      var suburbStatePostcode = [
        data.PhysicalAddressSuburb,
        data.PhysicalAddressState,
        data.PhysicalAddressPostcode,
      ]
        .filter((part) => part)
        .join(" ");

      return [
        data.PhysicalAddressStreetLine1,
        data.PhysicalAddressStreetLine2,
        suburbStatePostcode,
        data.PhysicalAddressCountry,
      ].filter((part) => part);
    },
    removeGridFocusAndSetFormFocus(dataGrid) {
      let formField = "SalesRepresentativeCode";

      if (this.canEditHeaderDiscount && !this.headerDiscountLocked) {
        formField = "HeaderDiscountPcnt";
      } else if (this.exchangeRateEditable) {
        formField = "ExchangeRate";
      }

      dataGrid.option("focusedRowIndex", -1);
      dataGrid.saveEditData();
      var headerFormInstance =
        this.$refs["gc-header-form"].$refs["gc-form-comp"].instance;

      headerFormInstance.getEditor(formField).instance().focus();
    },

    handleGridKeyboardShortcut(e) {
      // Tabbing or escaping is the user deliberately going somewhere else, so
      // drop any pending F2 (see requestGridSearchBoxFocus) rather than
      // yanking focus back to search once the recalculation finishes.
      if (e.key === "Tab" || e.key === "Escape") {
        this.releaseGridSearchBoxFocus();
      }

      if (e.key !== "F1" && e.key !== "F2") return;

      // Always swallow F1/F2 on this screen so neither reaches the global
      // GcHotKey handler (window-level) — including in Review, where this
      // screen's shortcuts must have no effect at all, not fall back to the
      // global F2 behaviour.
      e.preventDefault();
      e.stopPropagation();

      if (this.soDetailsCurrentView === "Review") return;

      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      if (!dataGrid) return;

      if (e.key === "F1") {
        // F1 is the user asking for the Qty cell, so drop any pending F2 or
        // it would pull focus back out from under them.
        this.releaseGridSearchBoxFocus();

        // totalCount() is unreliable here — getDataSource() (see computed,
        // above) binds a plain array, not a paged store. getVisibleRows()
        // reflects the currently filtered/sorted set, per the existing
        // pattern at getDataSource().items().length (~line 3196).
        var visibleRows = dataGrid.getVisibleRows();
        if (!visibleRows.length) return;
        var rowIndex = visibleRows[0].rowIndex;
        var cell = dataGrid.getCellElement(rowIndex, "SalesUMQty");
        if (!cell) return;
        dataGrid.editCell(rowIndex, "SalesUMQty");
        this.$nextTick(() => {
          dataGrid.focus(cell);
        });
      } else if (e.key === "F2") {
        this.requestGridSearchBoxFocus();
      }
    },

    getGridSearchBox() {
      return document.querySelectorAll(
        "input[aria-label='Search in the data grid']",
      )[0];
    },

    // F2 focuses the search box straight away, but when it is pressed from
    // the Qty cell that focus does not survive: leaving the cell commits the
    // Qty, which kicks off the price recalculation, and that repaints the row
    // repeatedly afterwards (cascading into SalesUMFXPrice's own
    // setCellValue). DevExtreme focuses a cell editor's input every time it
    // re-renders it, so focus keeps getting pulled back into the grid for as
    // long as the recalculation runs. Rather than fight each of those, the
    // request is remembered here and applied once by
    // applyPendingSearchFocus() when the recalculation finishes.
    requestGridSearchBoxFocus() {
      var searchBox = this.getGridSearchBox();
      if (!searchBox) return;

      this.searchFocusRequested = true;
      searchBox.focus();
    },

    releaseGridSearchBoxFocus() {
      this.searchFocusRequested = false;
    },

    // Called at the end of the Qty-change paths, once the new price has been
    // written to the data source.
    applyPendingSearchFocus() {
      if (!this.searchFocusRequested) return;
      this.searchFocusRequested = false;

      var dataGrid = this.$refs["dx-data-grid-comp"];
      dataGrid = dataGrid && dataGrid.instance;
      if (!dataGrid) return;

      // Closing the editor removes the input that the remaining repaints
      // would otherwise focus. Safe here in a way it is not earlier: by this
      // point the typed Qty has been persisted to the data source, so
      // closing the cell cannot discard it.
      dataGrid.closeEditCell();

      // After the close has been rendered, so the grid is finished moving
      // focus around before the search box claims it.
      this.$nextTick(() => {
        setTimeout(() => {
          var searchBox = this.getGridSearchBox();
          if (searchBox) searchBox.focus();
        }, 0);
      });
    },
    focusOnQtyFromSalesUM(currentRowData) {
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;

      setTimeout(() => {
        var rowIndex = dataGrid.getRowIndexByKey(currentRowData.ID);
        var cell = dataGrid.getCellElement(rowIndex, "SalesUMQty");
        dataGrid.editCell(rowIndex, "SalesUMQty");

        this.$nextTick(() => {
          dataGrid.focus(cell);
        });
      }, 100);
    },
    updateLineTotals(newData, currentRowData) {
      // setStockPrices
      newData.StockUMQty = calculateStockUMQty(
        newData.SalesUMQty,
        currentRowData.SalesUMConversion,
      );

      // Stock
      newData.StockUMFXPrice = calculateStockUMFXPrice(
        newData.SalesUMFXPrice,
        currentRowData.SalesUMConversion,
      );

      newData.StockUMNetFXPrice = calculateStockUMNetFXPrice(
        newData.SalesUMNetFXPrice,
        currentRowData.SalesUMConversion,
      );

      newData.StockUMPrice = calculateStockUMPrice(
        newData.SalesUMPrice,
        currentRowData.SalesUMConversion,
      );

      newData.StockUMNetPrice = calculateStockUMNetPrice(
        newData.SalesUMNetPrice,
        currentRowData.SalesUMConversion,
      );

      // setLineTotals
      // FX Totals
      newData.DiscFXLineTotal = calculateDiscFXLineTotal(
        currentRowData.SalesUMFXPrice,
        currentRowData.SalesUMNetFXPrice,
        newData.SalesUMQty,
      );
      newData.NetFXLineTotal = calculateNetFXLineTotal(
        currentRowData.SalesUMNetFXPrice,
        newData.SalesUMQty,
        currentRowData.SalesUMFXPrice,
      );
      newData.WetFXLineTotal = calculateWetFXLineTotal(
        newData.NetFXLineTotal,
        currentRowData.WetPcnt,
      );

      newData.TaxableFXLineTotal = calculateTaxableFXLineTotal(
        newData.NetFXLineTotal,
        newData.WetFXLineTotal,
      );
      newData.TaxFXLineTotal = calculateTaxFXLineTotal(
        newData.NetFXLineTotal,
        newData.WetFXLineTotal,
        currentRowData.SalesTaxPcnt,
      );
      newData.FXLineTotal = calculateFXLineTotal(
        newData.TaxableFXLineTotal,
        newData.TaxFXLineTotal,
      );

      // Totals
      newData.DiscLineTotal = calculateDiscLineTotal(
        newData.DiscFXLineTotal,
        this.model.ExchangeRate,
      );
      newData.NetLineTotal = calculateNetLineTotal(
        newData.NetFXLineTotal,
        this.model.ExchangeRate,
      );
      newData.WetLineTotal = calculateWetLineTotal(
        newData.WetFXLineTotal,
        this.model.ExchangeRate,
      );
      newData.TaxableLineTotal = calculateTaxableLineTotal(
        newData.TaxableFXLineTotal,
        this.model.ExchangeRate,
      );
      newData.TaxLineTotal = calculateTaxLineTotal(
        newData.TaxFXLineTotal,
        this.model.ExchangeRate,
      );
      newData.LineTotal = calculateLineTotal(
        newData.FXLineTotal,
        this.model.ExchangeRate,
      );
      newData = { ...currentRowData, ...newData };

      // Qty 0 -> >0 with a header Discount % set: inherit it onto the line, even on this
      // no-price-lookup path (e.g. the user answered "No" to the price recalculation prompt).
      if (newData.InheritHeaderDiscount && this.model.HeaderDiscountPcnt > 0) {
        newData = this.applyDiscountToLine(
          newData,
          this.model.HeaderDiscountPcnt,
        );
      }
      newData.InheritHeaderDiscount = false;

      this.addToReview(newData);
      this.updateDataSource(newData, undefined, true);

      // No price lookup on this path (answering No to the price-change
      // confirmation, or a partly supplied line), so the line is finished
      // here — honour a pending F2.
      this.applyPendingSearchFocus();
    },
    async updateThePriceDataOnUMChange(
      newData,
      currentRowData,
      obj,
      contractItemModel,
    ) {
      return SalesOrderV3Service.setSaleItemPrice(
        newData,
        obj,
        this.model.SalesOrderTypeCode,
        this.model.BranchCode,
        this.toolbarModel.CustomerCode,
        this.model.ExchangeRate,
        this.model.DateIn,
        this.model.RequiredByDate,
        false,
      ).then((res) => {
        newData = { ...currentRowData, ...res };

        if (contractItemModel && contractItemModel.Proceed == true) {
          this.updateSalesOrderDetailsRecord(newData, contractItemModel);

          // ADO #78300 (FDT70662): the contract price set just above can move
          // Net Price after setSaleItemPrice() already computed the margin
          // flag, leaving it stale — recompute here, same formula as
          // onDiscountPrctUpdate/applyDiscountToLine. Mirrors the Qty-
          // triggered contract path (updatePriceData), which already
          // recomputes via onPriceUpdateValidateGPToolPrcnt.
          let tempGp = calculateGrossProfitPcntRaw(
            newData.SalesUMStandardCost,
            newData.SalesUMNetPrice,
          );
          let globalGPVal = parseFloat(getFactorByCode(571).Factorc);
          newData.MarginOverrideWarning =
            globalGPVal != 0 && (tempGp < globalGPVal || tempGp > 100);
        }

        if (newData.SalesUMQty > 0) {
          this.addToReview(newData);
        }
      });
    },
    moveOnKeyDown(e) {
      if (e.event.keyCode == 9 && e.event.shiftKey == false) {
        var dataGrid = this.$refs["dx-data-grid-comp"].instance;

        if (dataGrid.getDataSource().items().length != 0) {
          e.event.preventDefault();
          let cells = document.querySelectorAll(
            ".gc-editable-cell-color:not([role='columnheader'])",
          );

          this.$nextTick(() => {
            var cell = cells[0];
            if (cell) {
              cell.classList.remove("key-pad-focused-cell");
              setTimeout(() => {
                cell.click();
              }, 10);
            }
          });
        }
      }
    },
    toolBarFormContentReady(e) {
      if (
        e &&
        this.baseView.mode != "review" &&
        this.loaded &&
        this.IsCustomerEditable == true
      )
        setTimeout(() => {
          var headerToolBarForm = e.component;
          if (
            headerToolBarForm &&
            headerToolBarForm.getEditor("CustomerCode") &&
            !this.HasLinkedWorkOrder
          )
            headerToolBarForm.getEditor("CustomerCode").instance().focus();
        }, 500);
    },
    loadCustomerSalesContracts() {
      if (!this.SalesContractDrawdownEnabled) return;
      this.CustomerSalesContracts.loadCustomerContractItems(
        this.toolbarModel.CustomerCode,
        this.model.Code,
        this.reviewDataList._array,
      );
    },
    async tryProcessSalesContracts(newValue, oldValue, dataField, record) {
      if (!this.SalesContractDrawdownEnabled) return null;

      let newSalesUM, oldSalesUM, newSalesUMQty, oldSalesUMQty;

      if (dataField == "SalesUM") {
        newSalesUM = newValue;
        oldSalesUM = oldValue;
        newSalesUMQty = record.SalesUMQty;
        oldSalesUMQty = record.SalesUMQty;
      } else if (dataField == "SalesUMQty") {
        newSalesUM = record.SalesUM;
        oldSalesUM = record.SalesUM;
        newSalesUMQty = newValue;
        oldSalesUMQty = oldValue;
      }

      var existingContractLineUsages = this.reviewDataList._array
        .filter(
          (detail) =>
            detail.CustomerSalesContractCode !== null &&
            detail.CustomerSalesContractLineNo !== null,
        )
        .map((detail) => ({
          CustomerSalesContractCode: detail.CustomerSalesContractCode,
          CustomerSalesContractLineNo: detail.CustomerSalesContractLineNo,
          LineNo: detail.LineNo,
          ItemCode: detail.ItemCode,
        }));

      var customerContractItemsForProduct =
        this.CustomerSalesContracts.drawProduct(
          record.LineNo,
          record.ProductCode,
          oldSalesUMQty,
          newSalesUMQty,
          oldSalesUM,
          newSalesUM,
          record.CustomerSalesContractCode,
          record.CustomerSalesContractLineNo,
          existingContractLineUsages,
        );

      if (customerContractItemsForProduct !== null) {
        customerContractItemsForProduct.Proceed = true;

        if (
          customerContractItemsForProduct.CustomerSalesContractCode == 0 &&
          customerContractItemsForProduct.CustomerSalesContractLineNo == 0 &&
          customerContractItemsForProduct.SalesContractItem == null
        ) {
          customerContractItemsForProduct.CustomerSalesContractCode = null;
          customerContractItemsForProduct.CustomerSalesContractLineNo = null;

          let confirmTitle, confirmMessage;

          if (
            customerContractItemsForProduct.MessageType &&
            customerContractItemsForProduct.MessageType !== ""
          ) {
            if (
              customerContractItemsForProduct.MessageType == "SalesUMChanged"
            ) {
              confirmTitle = "Confirmation";
              confirmMessage = `Contract could not be associated for the UM ${newSalesUM}. Do you want to proceed anyway?`;
            } else {
              confirmTitle = "Insufficient Quantity";
              let cont = "";
              if (
                record.CustomerSalesContractCode &&
                record.CustomerSalesContractCode != 0
              ) {
                cont = `(${record.CustomerSalesContractCode}) `;
              } else if (
                customerContractItemsForProduct.FailedCustomerSalesContractCode
              ) {
                cont = `(${customerContractItemsForProduct.FailedCustomerSalesContractCode}) `;
              }

              confirmMessage = `There is insufficient quantity available on the associated contract ${cont}and the product ${record.ProductCode} will not be allocated.`;
            }

            await alert(confirmMessage, confirmTitle);
            customerContractItemsForProduct.Proceed = false;

            return customerContractItemsForProduct;
          } else {
            return customerContractItemsForProduct;
          }
        } else {
          return customerContractItemsForProduct;
        }
      }

      return null;
    },
    updateSalesOrderDetailsRecord(record, customerContractItemsForProduct) {
      this.$logger.debug(
        "DrawProduct: UpdateSalesOrderDetailsRecord",
        customerContractItemsForProduct,
      );
      record.CustomerSalesContractCode =
        customerContractItemsForProduct.CustomerSalesContractCode;

      record.CustomerSalesContractLineNo =
        customerContractItemsForProduct.CustomerSalesContractLineNo;

      if (customerContractItemsForProduct.SalesContractItem) {
        if (
          customerContractItemsForProduct.SalesContractItem.UseMarket === "N"
        ) {
          record.SalesUMFXPrice =
            customerContractItemsForProduct.SalesContractItem.DefaultPrice;
        }

        // apply freight
        if (
          customerContractItemsForProduct.SalesContractItem.ApplyFreight == "Y"
        ) {
          record.SalesUMFXPrice += this.getFreightCharge(record.SalesUM);
        }

        record.SalesUMNetFXPrice = calculateSalesUMNetFXPriceFromSalesUMFXPrice(
          record.SalesUMFXPrice,
          record.DiscPcnt,
        );

        record = this.setBaseCurrencyPriceFromFXPrice(record);
      }

      return record;
    },
    getFreightCharge(salesUM) {
      let item = this.freightCharges.find(
        (x) => x.UnitOfMeasureCode.toUpperCase() == salesUM.toUpperCase(),
      );

      if (item) {
        return item.FreightCharge;
      } else return 0;
    },
    async populateFreightCharges(customerCode, deliveryCode) {
      if (
        FeatureStorage.checkPermission(
          "CustomerSalesContract",
          this.baseView.mode,
        )
      ) {
        let ds = CustomerFreightChargeService.dataSourceConfig(
          "api/CustomerSalesContractFreightCharge",
          {
            filter: [
              ["CustomerCode", "=", customerCode],
              "and",
              ["DeliveryCode", "=", parseInt(deliveryCode)],
            ],
          },
        );
        this.freightCharges = await ds.load();
        this.$logger.debug("Freight charges Loaded", this.freightCharges);
      }
    },

    setBaseCurrencyPriceFromFXPrice(record) {
      record.SalesUMPrice = calculateSalesUMPriceFromSalesUMFXPrice(
        record.SalesUMFXPrice,
        this.model.ExchangeRate,
      );

      record.CalculatedDiscPcnt = calculateCalculatedDiscPcnt(
        record.SalesUMStandardPrice,
        record.SalesUMPrice,
      );

      record.SalesUMNetPrice = calculateSalesUMNetPriceFromSalesUMNetFXPrice(
        record.SalesUMNetFXPrice,
        this.model.ExchangeRate,
      );

      record = SalesOrderState.setStockPrices(record);
      record = SalesOrderState.setLineTotals(record, this.model);

      return record;
    },
    qtyChangeWarning(data) {
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      var rowIndex = dataGrid.getRowIndexByKey(data.ID);
      if (rowIndex != -1) this.SelectedQTYCell = rowIndex;

      this.displayWarningSummary([
        {
          message: `The current line has already had a delivery processed and you may not reduce the order qty below ${data.SalesUMSuppliedQty}. The order qty has been reset.`,
        },
      ]);
    },
    async completeSalesOrder(data) {
      let payload = {
        SalesOrderCode: data.Code,
      };

      this.showLoading(true);
      await SalesOrderService.completeSalesOrder(payload)
        .then(() => {
          this.showLoading(false);
          this.$notify(`SO #${data.Code} has been completed.`, "success");

          this.setIgnoreRouteChangeConfirmation();
          this.$router.push({
            path: `/sales-order`,
          });
        })
        .catch((err) => {
          if (err) this.showLoading(false);
          this.$notify(`SO #${data.Code} could not be completed.`, "error");
        });
    },
    copyOrder() {
      this.$router
        .push({
          path: `/sales-order/create/${this.baseView.id}`,
          query: {
            copy: true,
          },
        })
        .then(() => {
          this.initialized();
        });
    },
    onSplitOrderSaved(newCode) {
      this.$notify(
        `Sales Order split successfully. New Sales Order ${newCode} created.`,
        "success",
      );
      // Reload the current review screen so totals/status reflect the split
      this.loaded = false;
      this.showLoading(true);
      this.initialized();
    },
    accountStatusVisible() {
      this.accountStatusPopupVisible = !this.accountStatusPopupVisible;
    },
    async onExchangeValueChanged(e) {
      if (!e.event) return;
      if (this.isExchangeReverting) {
        this.isExchangeReverting = false;
        return;
      }
      if (e.value === 0) {
        this.displayValidationSummary([
          {
            message: "Exchange rate should be greater than 0.0000",
          },
        ]);
        this.isExchangeReverting = true;
        this.model.ExchangeRate = e.previousValue;
      } else {
        if (this.reviewDataList._array.length > 0) {
          this.triggerPriceChange("Exchange rate");
        }
      }
    },
    onHeaderFormContentReady(e) {
      const headerForm = document.querySelectorAll(".gc-header-form");
      this.headerFormHeight = headerForm[0].clientHeight;

      this.headerDxFormHeight = e.element.offsetHeight;

      if (
        e &&
        this.baseView.mode == "edit" &&
        (this.IsCustomerEditable == false || this.HasLinkedWorkOrder)
      ) {
        setTimeout(() => {
          var headerToolBarForm = e.component;
          if (headerToolBarForm && headerToolBarForm.getEditor("DateIn"))
            headerToolBarForm.getEditor("DateIn").instance().focus();
        }, 500);
      }
    },
    onInitialized(e) {
      var dataGrid = e.component.instance();
      dataGrid.option("loadPanel.enabled", false);
    },
    setDiscountUpdate(newData, value, currentRowData) {
      // var obj = { ...currentRowData, ...newData };
      // this.discountTimeOut = setTimeout(() => {
      //   if (this.getCurrentDataObj(obj).TriggerDiscountUpdate)
      this.onDiscountPrctUpdate(newData, value, currentRowData);
      // }, 2000);
    },
    setPriceUpdate(newData, value, currentRowData) {
      // var obj = { ...currentRowData, ...newData };
      // this.priceUpdateTimeOut = setTimeout(() => {
      // if (this.getCurrentDataObj(obj).TriggerPriceUpdate) {

      newData = this.onPriceUpdateValidateGPToolPrcnt(
        newData,
        value,
        currentRowData,
      );
      // }
      // }, 100);
    },
    setUpdatePriceData(newData, obj) {
      // Per-row (FDT70523 fix): keyed by row ID so a debounce on one line
      // can't cancel another line's in-flight lookup (Finding 2).
      this.priceLookupTimeouts[obj.ID] = setTimeout(() => {
        //   if (this.getCurrentDataObj(obj).TiggerPriceLookUp)
        Promise.resolve(this.updatePriceData(newData, obj)).then(
          () => this.applyPendingSearchFocus(),
          () => this.applyPendingSearchFocus(),
        );
      }, 250);
    },
    updateDataSource(newData, currentRowData, refresh) {
      var res = undefined;
      if (currentRowData) res = { ...currentRowData, ...newData };
      else res = newData;

      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      if (res.IsCopyLine) {
        var index = this.localCopyItems.findIndex((x) => x.ID == res.ID);
        if (index != -1) {
          this.localCopyItems[index] = res;
          this.$nextTick(() => {
            this.addToReview(res);
          });
        }
      } else if (
        this.soDetailsCurrentView == "CustomerList" ||
        res.Origin == "Customer"
      ) {
        this.customerDataSourceItems.update(res.ID, res).then((data) => {
          if (refresh) {
            dataGrid.refresh(true);
            this.addToReview(data);
          }
        });
      } else if (
        this.soDetailsCurrentView == "ProductList" ||
        res.Origin == "Product"
      ) {
        this.productDataSourceItems.update(res.ID, res).then((data) => {
          if (refresh) {
            dataGrid.refresh(true);
            this.addToReview(data);
          }
        });
      } else {
        this.reviewDataList.update(res.ID, res);
      }
    },
    getAvailableLookups(dataField) {
      if (this.localCopyItems.filter((i) => i[dataField]).length > 0) {
        return this.localCopyItems.filter((i) => i[dataField]);
      } else if (this.soDetailsCurrentView == "CustomerList") {
        return this.customerDataSourceItems._array.filter((i) => i[dataField]);
      } else if (this.soDetailsCurrentView == "ProductList") {
        return this.productDataSourceItems._array.filter((i) => i[dataField]);
      } else {
        return this.reviewDataList._array.filter((i) => i[dataField]);
      }
    },
    getCurrentDataObj(res) {
      if (res.IsCopyLine) {
        return this.localCopyItems.find((i) => i.ID == res.ID);
      } else if (
        this.soDetailsCurrentView == "CustomerList" ||
        res.Origin == "Customer"
      ) {
        return this.customerDataSourceItems._array.find((i) => i.ID == res.ID);
      } else if (
        this.soDetailsCurrentView == "ProductList" ||
        res.Origin == "Product"
      ) {
        return this.productDataSourceItems._array.find((i) => i.ID == res.ID);
      } else {
        return this.reviewDataList._array.find((i) => i.ID == res.ID);
      }
    },
    // Load Products
    async loadProductItems(totalItemsCount, skip) {
      var filter = `ItemType eq 'S' and IsDefault eq 'Y' and AvailableForSale eq 'Y' and CustomerCode eq '${this.toolbarModel.CustomerCode}' and Inactive eq 'N'`;

      if (this.model.BranchCode) {
        filter = filter + ` and BranchCode eq '${this.model.BranchCode}'`;
      }

      if (!totalItemsCount || skip < totalItemsCount)
        ProductService.getCustomData({
          url: "api/ProductFilterGroupSaleItemCompanyBranch",
          count: true,
          skip: skip,
          top: 1000,
          filter: filter,
          orderby: "Description asc",
          select: [
            "ItemType",
            "IsDefault",
            "AvailableForSale",
            "CustomerCode",
            "Inactive",
            "Available",
            "BranchCode",
            "Description",
            "HasPrimaryImage",
            // "IsBatchProduct",
            "ItemCode",
            "ItemType",
            "ProductCategory1Code",
            "ProductCategory2Code",
            "ProductCategory3Code",
            "ProductCategory4Code",
            "ProductTypeCode",
            "SalesUM",
            "StandardSell",
            "Status",
            "StockUM",
            "Rop",
            "BinCode",
          ],
        }).then((res) => {
          if (!totalItemsCount) totalItemsCount = res.data["@odata.count"];
          res.data.value.forEach((element) => {
            element.OriginItemCode = element.ItemCode;
            element.ProductCode = element.ItemCode;
            element.HasPromotion = false;

            element.Origin = "Product";
            element.SalesUMQty = 0;
            element.SalesUMFXPrice = 0;
            element.TaxableFXLineTotal = 0;
            element.TaxFXLineTotal = 0;
            element.FXLineTotal = 0;
            element = { ...soDetailsBaseRecord, ...element };

            element.ID = element.ItemCode;

            this.productDataSourceItems.insert(element);

            var items = this.reviewDataList._array.filter((x) => {
              return x.OriginItemCode == element.OriginItemCode;
            });

            items.forEach((i) => {
              this.reviewDataList.update(i.ID, {
                Available: element.Available,
              });
            });
          });

          this.setItemPromotions(res.data.value);
          this.refreshAvailableShading();

          if (totalItemsCount > 0)
            this.loadProductItems(totalItemsCount, skip + 1000);
        });
      else return;
    },
    productListPostProcess(data) {
      var items = [];

      data.forEach((element) => {
        var i = this.reviewDataList._array.findIndex((x) => {
          if (x.LineNo > 0) {
            return x.ID == element.ID + "-" + x.LineNo;
          } else return x.ID == element.ID;
        });

        var item = undefined;
        var ums = [];
        if (i == -1) item = element;
        else {
          item = this.reviewDataList._array[i];
          item.Available = element.Available;
        }

        ums = this.salesUMsLocalDictionary[item.OriginItemCode]
          ? this.salesUMsLocalDictionary[item.OriginItemCode]
          : [];

        item.SLUMLength = ums.length;
        item.SalesUMs = ums;

        items.push(item);

        this.localCopyItems.forEach((copy) => {
          if (
            copy.OriginItemCode == item.ItemCode ||
            copy.OriginItemCode == item.OriginItemCode
          ) {
            var c_i = this.reviewDataList._array.findIndex(
              (x) => x.ID == copy.ID,
            );

            if (c_i != -1) {
              items.push(this.reviewDataList._array[c_i]);
            } else {
              items.push(copy);
            }
          }
        });
      });

      return items;
    },
    loadCustomerItems(totalItemsCount, skip) {
      var filter = `ItemType eq 'S' and IsDefault eq 'Y' and CustomerCode eq '${this.toolbarModel.CustomerCode}' and Inactive eq 'N'`;

      if (this.model.BranchCode) {
        filter = filter + ` and BranchCode eq '${this.model.BranchCode}'`;
      }

      if (!totalItemsCount || skip < totalItemsCount)
        ProductService.getCustomData({
          url: "api/CustomerSaleItemCompanyBranch",
          count: true,
          skip: skip,
          top: 1000,
          filter: filter,
          orderby: "Description asc",
          select: [
            "ItemType",
            "IsDefault",
            "AvailableForSale",
            "CustomerCode",
            "Inactive",
            "Available",
            "BranchCode",
            "Description",
            "HasPrimaryImage",
            // "IsBatchProduct",
            "ItemCode",
            "ItemType",
            "ProductCategory1Code",
            "ProductCategory2Code",
            "ProductCategory3Code",
            "ProductCategory4Code",
            "ProductTypeCode",
            "SalesUM",
            "StandardSell",
            "Status",
            "StockUM",
          ],
        }).then((res) => {
          if (!totalItemsCount) totalItemsCount = res.data["@odata.count"];
          res.data.value.forEach((element) => {
            element.OriginItemCode = element.ItemCode;
            element.ProductCode = element.ItemCode;

            element.HasPromotion = false;
            element.Origin = "Customer";
            element.SalesUMQty = 0;
            element.SalesUMFXPrice = 0;
            element.TaxableFXLineTotal = 0;
            element.TaxFXLineTotal = 0;
            element.FXLineTotal = 0;
            element = { ...soDetailsBaseRecord, ...element };

            element.ID = element.ItemCode;

            this.customerDataSourceItems.insert(element);
          });

          this.setItemPromotions(res.data.value);
          this.refreshAvailableShading();

          if (totalItemsCount > 0)
            this.loadCustomerItems(totalItemsCount, skip + 1000);
        });
      else return;
    },
    closeDeliveryAddressPopup() {
      this.editDeliveryAddressVisible = false;
    },
    showDeliveryAddress() {
      var item = { ...this.model };
      this.addressData = {
        DeliveryAddressCompanyName: item.DeliveryAddressCompanyName,
        DeliveryAddressCountry: item.DeliveryAddressCountry,
        DeliveryAddressPostcode: item.DeliveryAddressPostcode,
        DeliveryAddressState: item.DeliveryAddressState,
        DeliveryAddressStreetLine1: item.DeliveryAddressStreetLine1,
        DeliveryAddressStreetLine2: item.DeliveryAddressStreetLine2,
        DeliveryAddressSuburb: item.DeliveryAddressSuburb,
      };
      this.editDeliveryAddressVisible = true;
    },
    updateDeliveryAddress() {
      this.editDeliveryAddressVisible = false;

      this.model.DeliveryAddressCompanyName =
        this.addressData.DeliveryAddressCompanyName;
      this.model.DeliveryAddressStreetLine1 =
        this.addressData.DeliveryAddressStreetLine1;
      this.model.DeliveryAddressStreetLine2 =
        this.addressData.DeliveryAddressStreetLine2;
      this.model.DeliveryAddressSuburb = this.addressData.DeliveryAddressSuburb;
      this.model.DeliveryAddressState = this.addressData.DeliveryAddressState;
      this.model.DeliveryAddressPostcode =
        this.addressData.DeliveryAddressPostcode;
      this.model.DeliveryAddressCountry =
        this.addressData.DeliveryAddressCountry;
      this.populateFreightCharges(
        this.toolbarModel.CustomerCode,
        this.model.CustomerDeliveryAddressLineNo,
      );
    },
    headerFormValueChanged(isValid, brokenRules) {
      this.isValid = false;
      if (!isValid) this.displayValidationSummary(brokenRules);
      // else {
      // }
    },
    onSalesUMInitialized(e) {
      if (e.component.option().readOnly) {
        e.element.parentElement.parentElement.classList.add("cell-disabled");
      }
    },
    onSalesUMFocusIn(e, cellInfo) {
      if (this.focusedCellData) {
        this.removeCellFocus();
      }

      if (cellInfo.data.SLUMLength < 2) return;
      e.element.parentElement.classList.add("key-pad-focused-cell");

      let inputEl = e.element.querySelector(".dx-texteditor-input");

      this.focusedCellData = {
        e: e,
        cellInfo: cellInfo,
        requestSent: false,
        inputEl: inputEl,
      };

      this.selectedRowData = cellInfo;
    },
    onSalesUMKeyDown(e, cellInfo) {
      if (e.event.keyCode == 9 && e.event.shiftKey) {
        e.component.blur();

        let rowIndex = cellInfo.rowIndex;

        if (rowIndex === 0) {
          setTimeout(() => {
            e.event.preventDefault();
          }, 1);

          setTimeout(() => {
            var rowIndex = cellInfo.rowIndex;

            if (rowIndex == 0) {
              var headerFormInstance =
                this.$refs["gc-header-form"].$refs["gc-form-comp"].instance;

              let formField = "SalesRepresentativeCode";

              if (this.canEditHeaderDiscount && !this.headerDiscountLocked) {
                formField = "HeaderDiscountPcnt";
              } else if (this.exchangeRateEditable) {
                formField = "ExchangeRate";
              }

              headerFormInstance.getEditor(formField).instance().focus();
            }
          }, 2);
        } else {
          e.event.preventDefault();
          const dataGrid = cellInfo.component;

          setTimeout(() => {
            var gridDS = dataGrid.getDataSource();
            var loadOptions = gridDS.loadOptions();
            gridDS
              .store()
              .load(loadOptions)
              .done((items) => {
                const index = items.findIndex((x) => x.ID == cellInfo.data.ID);

                if (index > 0) {
                  // need to find previous row which is does not have a WorkOrderCode until 0 index
                  let prevRowIndex = index - 1;
                  while (prevRowIndex >= 0) {
                    if (!items[prevRowIndex].WorkOrderCode) {
                      break;
                    }
                    prevRowIndex--;
                  }

                  if (prevRowIndex < 0)
                    this.removeGridFocusAndSetFormFocus(dataGrid);
                  else {
                    var cellElement = dataGrid.getCellElement(
                      prevRowIndex,
                      "DiscPcnt",
                    );
                    dataGrid.focus(cellElement);
                    dataGrid.editCell(prevRowIndex, "DiscPcnt");
                  }
                } else {
                  this.removeGridFocusAndSetFormFocus(dataGrid);
                }
              });
          }, 1);
        }
      }
    },
    popupHiddenCallBack(show) {
      this.popupVisible = show;
      this.gpToolPopupVisible = false;
      this.productDetailsPopupVisible = false;
    },
    async checkHeaderFormValidation() {
      let validationResult = await validationEngine.validateGroup(
        "gc-form-header-toolbar",
      );
      let headerform = await validationEngine.validateGroup("gc-header-form");
      let commentsform = await validationEngine.validateGroup(
        "gc-header-comments",
      );
      if (
        !validationResult.isValid ||
        !headerform.isValid ||
        !commentsform.isValid
      )
        this.displayValidationSummary(
          validationResult.brokenRules
            .concat(headerform.brokenRules)
            .concat(commentsform.brokenRules),
        );
      else {
        if (this.reviewDataList._array.length == 0) {
          this.displayValidationErrors([
            "Sales Order has no detail. Please add one or more lines before attempting to save the transaction again.",
          ]);
          return;
        } else if (this.toolbarModel.NextStatus == "X") {
          let confirmLeaving = await this.$confirm(
            "Do you really wish to cancel sales order?",
            "Cancel sales order",
          );

          if (confirmLeaving) {
            this.$logger.trackEventButtonClick("Cancel Sales Order");
            this.cancelSalesOrder();
          }
        } else {
          let payload = this.getSavePayload();

          // 2113/76797 — block Commit (and Accept, which writes the same
          // Status "A") while any line is still linked to the Dummy Item
          // placeholder. Checked on payload.Status rather than which
          // dropdown label was clicked, since Commit and Accept both map to
          // the same id and Amazon/EDI orders (the main source of Dummy
          // Item lines) use Accept. Draft ("D")/Pending ("P") never reach
          // this branch. No request is sent when blocked.
          if (payload.Status === "A") {
            var dummyItemLine = this.findDummyItemLine();
            if (dummyItemLine) {
              this.displayValidationErrors([
                `You are attempting to commit this Sales Order when 1 or more lines are linked to the placeholder product ${this.DummyProductItemCode} - ${dummyItemLine.Description}. Please update the respective line(s) with the correct Product(s) before attempting to Commit this order.`,
              ]);
              return;
            }
          }

          var confirmSave = true;

          const zeroDollarItems = this.getSalesOrderDetailPayload().filter(
            (i) => i.SalesUMFXPrice == 0,
          );
          if (zeroDollarItems.length > 0) {
            confirmSave = await this.$confirm(
              "You are attempting to save this order with a Unit Price of 0.00 on one or more lines. Do you wish to proceed with the save?",
              "Save Sales Order",
            );
          }

          if (confirmSave) {
            this.showLoading(true);
            await SalesOrderV3Service.saveSalesOrder(payload, payload.Code)
              .then(async (res) => {
                this.showLoading(false);

                if (this.baseView.mode == "create") {
                  this.model.Code = res.data.Code;
                }

                this.$notify(
                  `Sales Order ${this.model.Code} Successfully Saved`,
                  "success",
                );

                let EDIPassed = true;

                if (this.model.isPendingEDIOrder) {
                  await SalesOrderEDIService.sendSalesOrderEDIPOA(
                    this.model.Code,
                  ).catch((error) => {
                    EDIPassed = false;
                    this.displayServerError(error, "Save sales order Failed");
                  });
                }

                if (EDIPassed) {
                  this.setIgnoreRouteChangeConfirmation();
                  this.$router.push({
                    path: `/sales-order`,
                  });
                }
              })
              .catch((error) => {
                this.showLoading(false);

                this.displayServerError(error, "Save Sales Order Failed");
              });
          }
        }
      }
    },
    // 2113/76797 — same Dummy Item predicate already used by the ellipsis
    // menu's "Update Item" visibility check and the OriginItemCode/
    // Description cell highlight (both inline elsewhere on this screen);
    // this is the first shared helper wrapping it. No separate API call is
    // needed for the product's description — a Dummy Item line's own
    // Description is already the placeholder product's real, current
    // description (it's resolved server-side from ItemCode the same way
    // every other line's Description is, and is the exact field the
    // Update Item popup's read-only "Current Item Description" already
    // trusts as authoritative), so the matched line is returned as-is for
    // the caller to read straight off.
    findDummyItemLine() {
      return this.reviewDataList._array.find(
        (element) =>
          element.ItemCode === this.DummyProductItemCode ||
          element.OriginItemCode === this.DummyProductItemCode,
      );
    },
    getSavePayload() {
      var ExTaxTotal = 0;
      var TaxTotal = 0;
      var Total = 0;
      this.reviewDataList._array.forEach((element) => {
        if (element.ItemCode && element.SalesUMQty) {
          ExTaxTotal = ExTaxTotal + element.TaxableFXLineTotal;
          TaxTotal = TaxTotal + element.TaxFXLineTotal;
          Total = Total + element.FXLineTotal;
        }
      });
      let payload = {
        IncludesGeneralLedgerLines: this.reviewDataList._array.find(
          (x) => x.ItemType == "G",
        )
          ? "Y"
          : "N",
        BranchCode: this.model.BranchCode,
        Code: this.baseView.mode != "create" ? parseInt(this.baseView.id) : 0,
        CompanyCode: this.model.CompanyCode,
        ConsignmentNo: this.model.ConsignmentNo,
        CurrencyCode: this.model.CurrencyCode,
        CustomerCode: this.toolbarModel.CustomerCode,
        CustomerDeliveryAddressLineNo:
          this.model.CustomerDeliveryAddressLineNo.toString(),
        DateIn: new Date(this.model.DateIn).toSaveDate(),
        DeliveryAddressCompanyName: this.model.DeliveryAddressCompanyName,
        DeliveryAddressCountry: this.model.DeliveryAddressCountry,
        DeliveryAddressPostcode: this.model.DeliveryAddressPostcode,
        DeliveryAddressState: this.model.DeliveryAddressState,
        DeliveryAddressStreetLine1: this.model.DeliveryAddressStreetLine1,
        DeliveryAddressStreetLine2: this.model.DeliveryAddressStreetLine2,
        DeliveryAddressSuburb: this.model.DeliveryAddressSuburb,
        Status: this.toolbarModel.NextStatus,
        DivisionCode: this.model.DivisionCode,
        ExchangeRate: this.model.ExchangeRate,
        ExTaxTotal: parseFloat(ExTaxTotal.toFixed(2)),
        ItemCount: this.getSalesOrderDetailPayload().length,
        OrderNo: this.model.OrderNo,
        RequiredByDate: new Date(this.model.RequiredByDate).toSaveDate(),
        SalesOrderTypeCode: this.model.SalesOrderTypeCode,
        SalesRepresentativeCode: this.model.SalesRepresentativeCode,
        ShipDate: new Date(this.model.ShipDate).toSaveDate(),
        // Status: "A",
        TaxTotal: parseFloat(TaxTotal.toFixed(2)),
        Total: parseFloat(Total.toFixed(2)),
        UserComment1: this.orderCommentModel.UserComment1,
        UserComment2: this.orderCommentModel.UserComment2,
        UserComment3: this.orderCommentModel.UserComment3,
        UserComment4: this.orderCommentModel.UserComment4,
        UserComment5: this.orderCommentModel.UserComment5,
        UserComment6: this.orderCommentModel.UserComment6,
        UserComment7: this.orderCommentModel.UserComment7,
        UserComment8: this.orderCommentModel.UserComment8,
        UserComment9: this.orderCommentModel.UserComment9,
        UserComment10: this.orderCommentModel.UserComment10,
        UserComment11: this.orderCommentModel.UserComment11,
        UserComment12: this.orderCommentModel.UserComment12,
        UserComment13: this.orderCommentModel.UserComment13,
        UserComment14: this.orderCommentModel.UserComment14,
        UserComment15: this.orderCommentModel.UserComment15,
        UserComment16: this.orderCommentModel.UserComment16,
        UserComment17: this.orderCommentModel.UserComment17,
        UserComment18: this.orderCommentModel.UserComment18,
        UserComment19: this.orderCommentModel.UserComment19,
        UserComment20: this.orderCommentModel.UserComment20,

        SalesOrderDetails: this.getSalesOrderDetailPayload(),
        SalesOrderNotes: [],
      };

      if (this.model.Notes && this.model.Notes.length > 0) {
        let notes = this.model.Notes.map((note) => {
          let n = {
            LineNo: note.LineNo ? note.LineNo : 0,
            Note: note.Note,
          };
          return n;
        });
        payload.SalesOrderNotes = notes;
      }

      if (this.baseView.mode != "create") {
        payload.DateChanged = this.model.DateChanged;
      }
      return payload;
    },
    getSalesOrderDetailPayload() {
      let details = [];

      this.reviewDataList._array.forEach((data) => {
        let detailObj = {
          SalesOrderCode: data.SalesOrderCode ? data.SalesOrderCode : 0,
          LineNo: data.LineNo ? data.LineNo : 0,
          ItemCode: data.OriginItemCode,
          SalesUM: data.SalesUM,
          SalesUMQty: data.SalesUMQty,
          DiscPcnt: data.DiscPcnt,
          SalesUMFXPrice: data.SalesUMFXPrice,
          Comment: data.Comment,
          IsCommentVisible: data.IsCommentVisible,
          RecordId: "00000000-0000-0000-0000-000000000000",
          CustomerSalesContractCode: data.CustomerSalesContractCode,
          CustomerSalesContractLineNo: data.CustomerSalesContractLineNo,
        };

        if (data.OriginItemCode && data.SalesUMQty) details.push(detailObj);
      });

      return details;
    },
    async cancelSalesOrder() {
      this.showLoading(true);
      var payload = {
        Code: this.baseView.id,
      };
      await SalesOrderService.cancelSalesOrder(payload)
        // eslint-disable-next-line no-unused-vars
        .then((result) => {
          this.showLoading(false);
          this.$notify({
            title: "Success",
            message: `Sales order ${result.data.Code} cancelled successfully`,
            type: "success",
          });
          this.setIgnoreRouteChangeConfirmation();
          this.$router.push({
            path: `/sales-order`,
          });
        })
        .catch((error) => {
          this.showLoading(false);
          this.displayServerError(error, "Cancel sales order Failed");
        });
    },
    backButtonFunction() {
      this.$router
        .push({
          path: this.backButtonRoute,
        })
        .then(() => {
          this.$store.dispatch("dataContext/setDataViewType", undefined);
        });
    },
    async onCustomerValueChanged(e) {
      if (!e.event) return;
      if (e.value == "") this.toolbarModel.CustomStatus = undefined;
      this.toolbarModel.CustomerCode = e.value;
      this.promotionItems = [];
      await this.getSalesPromotionItems();

      this.productDataSourceItems.clear();
      this.customerDataSourceItems.clear();
      this.loadProductItems(undefined, 0);
      this.loadCustomerItems(undefined, 0);
    },
    async checkSoAlert(customerCode) {
      if (!customerCode) return;

      let result = await CustomerNoteService.getSoAlertNote(customerCode);
      if (result.data.value.length > 0) {
        await alert(result.data.value[0].Note, "Alert");
      }
    },
    async onCustomerRowClick(e) {
      let customerRecord = e.itemData;
      await this.setSalesRep(customerRecord.SalesRepresentativeCode);

      if (!customerRecord) {
        return;
      }

      if (
        !(
          this.baseView.mode == "edit" &&
          customerRecord.Code == this.soAlertLoadedCustomerCode
        )
      ) {
        this.checkSoAlert(customerRecord.Code);
      }

      if (customerRecord.StopCredit == "Y")
        this.toolbarModel.CustomStatus = "Credit Hold";
      else if (customerRecord.TermCalculation == "CBD") {
        this.toolbarModel.CustomStatus = "Cash Before Despatch";
      } else this.toolbarModel.CustomStatus = undefined;

      this.model.CurrencyCode = customerRecord.CurrencyCode;
      this.model.CustomerName = customerRecord.Name;

      // this.setFooterItems([]);

      this.setExchangeRateProperties();
      await this.setDeliveryAddressDataSourceItems(
        this.toolbarModel.CustomerCode,
      );

      this.triggerPriceChange("Customer");
    },
    async onBranchRowClick(e) {
      this.model.BranchCode = e.itemData.Code;
      this.model.BranchDescription = e.itemData.Description;

      this.productDataSourceItems.clear();
      this.customerDataSourceItems.clear();
      this.loadProductItems(undefined, 0);
      this.loadCustomerItems(undefined, 0);

      this.triggerPriceChange("Branch");
    },
    async onDivisionRowClick(e) {
      this.model.DivisionCode = e.itemData.Code;
      this.model.DivisionDescription = e.itemData.Description;
      // this.triggerPriceChange("Division");
    },
    async onSalesOrderTypeRowClick(e) {
      this.model.SalesOrderTypeCode = e.itemData.Code;
      this.model.SalesOrderTypeDescription = e.itemData.Description;
      this.setOrderComments(this.model.SalesOrderTypeCode);
      this.triggerPriceChange("SO Type");
    },
    onSalesRepRowClick(e) {
      this.popupHiddenCallBack(false);
      this.model.SalesRepresentativeCode = e.itemData.Code;
      this.model.SalesRepresentativeFullName =
        e.itemData.FirstName + " " + e.itemData.Surname;
    },
    async onOrderNoChanged(e) {
      if (getFactorByCode(578).Factorc !== "Y" || e.value == "") return;
      let orderNo = e.value;

      let result = await SalesOrderService.findOrderNoUsageForSalesOrder(
        orderNo,
        this.model.Code,
        this.toolbarModel.CustomerCode,
      );

      let orderNoUsedPreviously = result.data.value.length > 0;
      if (!orderNoUsedPreviously) {
        result = await SalesOrderService.findOrderNoUsageForInvoice(
          orderNo,
          this.model.Code,
          this.model.CustomerCode,
        );
        orderNoUsedPreviously = result.data.value.length > 0;
      }

      if (orderNoUsedPreviously) {
        // Show prompt
        let confirmOrderNoReuse = await this.$confirm(
          `Customer ${this.model.CustomerName} has used Customer PO ${orderNo} previously. Will you use Customer PO ${orderNo} again?`,
          "Customer PO Used Previously",
        );

        if (!confirmOrderNoReuse) {
          e.component.focus();
          e.element.querySelector("input").select();
        }
      }
    },
    async onDeliverToRowClick(e) {
      var deliveryAddressLine1Record = e.itemData;

      this.model.DeliveryAddressCompanyName =
        deliveryAddressLine1Record.CompanyName;
      this.model.DeliveryAddressCountry =
        deliveryAddressLine1Record.PhysicalAddressCountry;
      this.model.DeliveryAddressPostcode =
        deliveryAddressLine1Record.PhysicalAddressPostcode;
      this.model.DeliveryAddressState =
        deliveryAddressLine1Record.PhysicalAddressState;
      this.model.DeliveryAddressStreetLine1 =
        deliveryAddressLine1Record.PhysicalAddressStreetLine1;
      this.model.DeliveryAddressStreetLine2 =
        deliveryAddressLine1Record.PhysicalAddressStreetLine2;
      this.model.DeliveryAddressSuburb =
        deliveryAddressLine1Record.PhysicalAddressSuburb;
      this.model.CustomerDeliveryAddressLineNo =
        deliveryAddressLine1Record.LineNo;
      this.TransitDays = deliveryAddressLine1Record.TransitDays;

      let factor565ShipDateSameAsRequiredByDate = getFactorByCode(565);

      if (
        factor565ShipDateSameAsRequiredByDate.Factorc == "T" &&
        this.TransitDays != 0
      ) {
        let confirmTransitDateChange = await this.$confirm(
          `Do you want to update the Ship Date by deducting the Transit Days from the current Required By Date?`,
          "Date Change",
        );

        if (confirmTransitDateChange) {
          this.model.ShipDate = new Date().setDate(
            new Date(this.model.RequiredByDate).getDate() - this.TransitDays,
          );
        }
      }
    },
    onProductCategory1Changed(e) {
      this.popupHiddenCallBack(false);

      this.selectedProductCategory1 = e;
    },
    async initialized() {
      var isCopyOrder = false;
      if (this.baseView.query && this.baseView.query.copy == "true") {
        isCopyOrder = true;
      }

      this.reviewDataList.clear();
      this.productDataSourceItems.clear();
      this.customerDataSourceItems.clear();
      if (this.baseView.mode == "create" && !this.baseView.id) {
        await this.$store.dispatch(
          "screenFactors/getFactors",
          [616, 549, 550, 95, 87, 565, 368, 18, 65, 505, 66, 571, 578, 478],
        );

        let factor87DefaultSalesOrderTypeCode = getFactorByCode(87);
        this.model.SalesOrderTypeCode =
          factor87DefaultSalesOrderTypeCode.Factorc;

        // await SalesOrderTypeService.getByCode(
        //   this.model.SalesOrderTypeCode
        // ).then((res) => {
        //   if (res.data.value.length > 0)
        //     this.model.SalesOrderTypeDescription =
        //       res.data.value[0].Description;
        // });

        await this.setOrderComments(this.model.SalesOrderTypeCode);

        await this.setDefaultSalesRep();

        this.factor368ExchangeRateFieldEditable = getFactorByCode(368);

        //  Populate defaults in the Create Mode
        this.model.DateIn = new Date();
        this.toolbarModel.Status = "D";
        this.model.Code = 0;

        let response = await DefaultCompanyBranchDivisionService.getDefault();

        if (response.data.value.length > 0) {
          this.model.CompanyCode = response.data.value[0].CompanyCode;
          this.model.BranchCode = response.data.value[0].BranchCode;
          this.model.DivisionCode = response.data.value[0].DivisionCode;
        }

        if (!this.model.CompanyCode) {
          this.model.CompanyCode = getFactorByCode(18).Factorc;
        }
        if (!this.model.BranchCode) {
          this.model.BranchCode = getFactorByCode(65).Factorc;
        }
        if (!this.model.DivisionCode) {
          this.model.DivisionCode = getFactorByCode(66).Factorc;
        }

        await this.getSalesUMs(this.model.BranchCode);

        await CompanyService.getCompanyCurrencyData(
          this.model.CompanyCode,
        ).then((res) => {
          if (res.data.value.length > 0) {
            this.model.CurrencyCode = res.data.value[0].CurrencyCode;
            this.model.CompanyCurrency = res.data.value[0].CurrencyCode;
            this.model.ExchangeRate = res.data.value[0].BuyRate;
          }
        });

        await this.setRequiredAndShipDates();

        this.setFooterItems([]);

        this.showLoading(false);
        this.loaded = true;
      } else {
        //
        await SalesOrderBatchAllocationsService.getBatchAllocationRule().then(
          (res) => {
            if (res.data.value.length > 0) {
              if (res.data.value[0].RuleAction !== "N") {
                this.isBatchAllocationVisible = true;
              } else {
                this.isBatchAllocationVisible = false;
              }
            }
          },
        );

        this.soDetailsCurrentView = "Review";
        var factors = [505, 549, 550, 616, 571, 565, 578, 611, 610, 478];
        if (this.baseView.mode == "edit") factors.push(95);
        await this.$store.dispatch("screenFactors/getFactors", factors);

        await SalesOrderService.getByCode(this.baseView.id, {
          select: [
            "Code",
            "CompanyCode",
            "BranchCode",
            "CurrencyCode",
            "CustomerCode",
            "DivisionCode",
            "SalesOrderTypeCode",
            "SalesRepresentativeCode",
            "DateIn",
            "RequiredByDate",
            "ShipDate",
            "OrderNo",
            "CustomerDeliveryAddressLineNo",
            "DeliveryAddressCompanyName",
            "DeliveryAddressCountry",
            "DeliveryAddressPostcode",
            "DeliveryAddressState",
            "DeliveryAddressStreetLine1",
            "DeliveryAddressStreetLine2",
            "DeliveryAddressSuburb",
            "CurrencyCode",
            "ExchangeRate",
            "UserComment1",
            "UserComment2",
            "UserComment3",
            "UserComment4",
            "UserComment5",
            "UserComment6",
            "UserComment7",
            "UserComment8",
            "UserComment9",
            "UserComment10",
            "UserComment11",
            "UserComment12",
            "UserComment13",
            "UserComment14",
            "UserComment15",
            "UserComment16",
            "UserComment17",
            "UserComment18",
            "UserComment19",
            "UserComment20",
            "ProcessStatus",
            "Status",
            "ChangedBy",
            "ConsignmentNo",
            "CreatedBy",
            "DateChanged",
            "DateCreated",
            "InvoiceCode",
            "IsLockedForPicking",
            "IsEDIOrder",
            "HasLinkedWorkOrder",
          ],
        })
          .then(async (res) => {
            // eslint-disable-next-line no-console

            if (res.data.value.length > 0) {
              this.HasLinkedWorkOrder =
                res.data.value[0].HasLinkedWorkOrder === "Y";
              if (res.data.value[0].Customer.StopCredit == "Y")
                res.data.value[0].CustomStatus = "Credit Hold";
              else if (res.data.value[0].Customer.TermCalculation == "CBD") {
                res.data.value[0].CustomStatus = "Cash Before Despatch";
              } else res.data.value[0].CustomStatus = undefined;

              res.data.value[0].LineItemCode = undefined;
              res.data.value[0].CustomerName = res.data.value[0].Customer.Name;

              res.data.value[0].SalesOrder = res.data.value[0].Code;

              res.data.value[0].ShipDate = DateFromIsoWithoutZ(
                res.data.value[0].ShipDate,
              );

              this.model = res.data.value[0];
              if (isCopyOrder) {
                this.model.Status = "D";
                this.model.DateIn = new Date();
                this.model.Code = 0;
                this.model.ConsignmentNo = "";
                this.model.OrderNo = null;
                await this.setRequiredAndShipDates();
              }

              if (this.model.Status == "P") {
                SalesOrderEDIService.getEDICustomerDocumentTypeByCustomerCode(
                  this.model.CustomerCode,
                ).then((res3) => {
                  if (res3.data.value.length > 0) {
                    if (
                      res3.data.value.filter(
                        (a) => a.EDIDocumentTypeCode == "POA",
                      ) != null
                    ) {
                      this.model.isPendingEDIOrder = true;
                    }
                  }
                });
              }

              this.model.SalesRepresentativeFullName =
                res.data.value[0].SalesRepresentative.FirstName +
                " " +
                res.data.value[0].SalesRepresentative.Surname;

              this.model.BranchDescription = this.model.Branch.Description;
              this.model.DivisionDescription = this.model.Division.Description;
              this.model.SalesOrderTypeDescription =
                this.model.SalesOrderType.Description;

              this.toolbarModel = { ...this.model };

              if (!isCopyOrder) {
                this.soAlertLoadedCustomerCode = this.model.CustomerCode;
              }

              for (const key in this.model) {
                if (key.match(/UserComment/)) {
                  if (isCopyOrder) {
                    this.orderCommentModel[key] = "";
                  } else {
                    this.orderCommentModel[key] = this.model[key];
                  }
                }
              }

              this.model.DeliveryAddressCompanyName =
                res.data.value[0].DeliveryAddressCompanyName;

              this.model.DeliveryAddressCountry =
                res.data.value[0].DeliveryAddressCountry;
              this.model.DeliveryAddressPostcode =
                res.data.value[0].DeliveryAddressPostcode;
              this.model.DeliveryAddressState =
                res.data.value[0].DeliveryAddressState;
              this.model.DeliveryAddressStreetLine1 =
                res.data.value[0].DeliveryAddressStreetLine1;
              this.model.DeliveryAddressStreetLine2 =
                res.data.value[0].DeliveryAddressStreetLine2;
              this.model.DeliveryAddressSuburb =
                res.data.value[0].DeliveryAddressSuburb;
              this.model.CustomerDeliveryAddressLineNo = parseInt(
                res.data.value[0].CustomerDeliveryAddressLineNo,
              );

              this.TransitDays = 0;

              this.populateFreightCharges(
                this.toolbarModel.CustomerCode,
                this.model.CustomerDeliveryAddressLineNo,
              );

              if (this.model.Company) {
                this.model.CompanyCurrency =
                  res.data.value[0].Company.CurrencyCode;
              }

              if (this.model.IsLockedForPicking == "Y")
                this.toolbarModel.Status = "L";
            }
          })
          .catch((err) => {
            if (err) this.showLoading(false);
          });

        if (this.model.Code) {
          await this.loadLinkedContractDates(this.model.Code);
        }

        if (this.baseView.mode == "edit" && this.toolbarModel.Status == "L") {
          this.setIgnoreRouteChangeConfirmation();
          this.$router
            .push({
              path: `/sales-order/review/${this.model.Code}`,
            })
            .then(() => {
              this.initialized();
            });
        }

        if (!this.model.CompanyCurrency) {
          await CompanyService.getCompanyCurrencyData(
            this.model.CompanyCode,
          ).then((res) => {
            if (res.data.value.length > 0) {
              this.model.CompanyCurrency = res.data.value[0].CurrencyCode;
            }
          });
        }

        if (this.baseView.mode == "edit") {
          await this.getSalesUMs(this.model.BranchCode);
          await this.checkInvoiceAvailability();
          this.productDataSourceItems.clear();
          this.customerDataSourceItems.clear();
          this.loadProductItems(undefined, 0);
          this.loadCustomerItems(undefined, 0);
        }
        // for copy order, reload the product and customer items
        if (isCopyOrder) {
          this.productDataSourceItems.clear();
          this.customerDataSourceItems.clear();
          this.loadProductItems(undefined, 0);
          this.loadCustomerItems(undefined, 0);
        }

        if (
          !isCopyOrder &&
          this.toolbarModel.Status == "D" &&
          this.baseView.mode !== "edit"
        ) {
          // force push screen to edit mode when the Sales Order is in Draft mode
          this.$router
            .push({
              path: `/sales-order/edit/${this.model.Code}/header`,
            })
            .then(() => {
              this.initialized();
            });
        } else {
          if (getFactorByCode(616).Factorc != "Date In") {
            this.model.FilterDate = new Date(
              this.model.RequiredByDate,
            ).toYYYYMMDD();
          } else {
            this.model.FilterDate = new Date(this.model.DateIn).toYYYYMMDD();
          }
          await this.setOrderComments(this.model.SalesOrderTypeCode);

          let record = await SalesOrderV3Service.getSalesOrderDetailsByCode(
            this.baseView.id,
            {
              select: [
                "Available",
                "AvailableForSale",
                "BranchCode",
                "CalculatedDiscPcnt",
                "ChangedBy",
                "Comment",
                "CompanyCode",
                "CreatedBy",
                "CustomerSalesContractCode",
                "CustomerSalesContractLineNo",
                "DateChanged",
                "DateCreated",
                "Description",
                "Description1",
                "Description2",
                "Description3",
                "DiscFXLineTotal",
                "DiscLineTotal",
                "DiscPcnt",
                "FXLineTotal",
                "HasPrimaryImage",
                "IsCommentVisible",
                "ItemCode",
                "ItemType",
                "LineNo",
                "LineTotal",
                "NetFXLineTotal",
                "NetLineTotal",
                "PSlipStatus",
                "AddDiscPriceGroupCode",
                "AddDiscPricingMethod",
                "AddDiscPricingMethodValue",
                "AddDiscSalesUMPrice",
                "AddDiscStockUMPrice",
                "PriceGroupCode",
                "PriceGroupSalesUMPrice",
                "PricingMethod",
                "PricingMethodValue",
                "ProductCategory1Code",
                "ProductCategory2Code",
                "ProductCategory3Code",
                "ProductSizeCode",
                "ProductTypeCode",
                "RecordId",
                "RequiredByDate",
                "SalesOrderCode",
                "SalesTaxPcnt",
                "SalesUM",
                "SalesUMConversion",
                "SalesUMFXPrice",
                "SalesUMNetFXPrice",
                "SalesUMNetPrice",
                "SalesUMPrice",
                "SalesUMPromotionNetPrice",
                "SalesUMQty",
                "SalesUMStandardCost",
                "SalesUMStandardPrice",
                "SalesUMSuppliedQty",
                "ShipDate",
                "Status",
                "StockUM",
                "StockUMFXPrice",
                "StockUMNetFXPrice",
                "StockUMNetPrice",
                "StockUMPrice",
                "StockUMQty",
                "TaxCode",
                "TaxFXLineTotal",
                "TaxLineTotal",
                "TaxableFXLineTotal",
                "TaxableLineTotal",
                "WetFXLineTotal",
                "WetLineTotal",
                "WetPcnt",
                "IsBatchProduct",
                "CustomerProduct",
                "WorkOrderCode",
              ],
            },
          );
          if (record && record.data.value.length > 0) {
            var data = record.data.value;
            let isBatchAvailable = false;
            await data.forEach(async (element) => {
              if (!isCopyOrder || (isCopyOrder && element.ItemType != "G")) {
                if (element.IsBatchProduct != "No") {
                  isBatchAvailable = true;
                }
                var ums = [];
                if (isCopyOrder) {
                  element.SalesOrderCode = 0;
                }
                // Description is the deduped PROD_DESCN1-3 concatenation (fnConcatenateWithoutDuplicate);
                // Description1 is the raw PROD_DESCN1 passthrough and is not what these aliases should show.
                element.ItemDescription = element.Description;
                element.OriginItemCode = element.ItemCode;
                element.ProductCode = element.ItemCode;
                element.ProductDescription = element.Description;
                element.CalculatedDiscPcnt_Original =
                  element.CalculatedDiscPcnt;
                element.ItemCode = element.ItemCode + "-" + element.LineNo;
                element.ID = element.ItemCode;
                element.customerContractItemsForProduct = undefined;
                // element.ID = index;
                // element.validCode = element.ItemCode;

                ums = this.salesUMsLocalDictionary[element.OriginItemCode]
                  ? this.salesUMsLocalDictionary[element.OriginItemCode]
                  : [];
                element.SLUMLength = ums.length;
                element.SalesUMs = ums;
                element.FXPriceBeforeUpdate = element.SalesUMFXPrice;

                // Below-margin Net Price highlight must be computed on load too, not only
                // inside the edit-time handlers (onDiscountPrctUpdate/applyDiscountToLine/
                // onPriceUpdateValidateGPToolPrcnt) — same formula as those. Bug 75840.
                let tempGp = calculateGrossProfitPcntRaw(
                  element.SalesUMStandardCost,
                  element.SalesUMNetPrice,
                );
                let globalGPVal = parseFloat(getFactorByCode(571).Factorc);
                element.MarginOverrideWarning =
                  globalGPVal != 0 && (tempGp < globalGPVal || tempGp > 100);

                this.reviewDataList.insert(element);

                var items = this.reviewDataList._array.filter(
                  (x) => x.ProductCode == element.ProductCode,
                );
                if (items.length > 1) {
                  element.IsCopyLine = true;
                  this.localCopyItems.push(element);
                  this.reviewDataList.update(element.ID, { IsCopyLine: true });
                }
              }
            });

            if (isBatchAvailable === false)
              this.isBatchAllocationVisible = false;
          }

          this.setFooterItems(this.reviewDataList._array);

          let resSoConsumption =
            await CustomerSalesContractService.getCustomerSalesContractSalesOrderConsumptionBySalesOrder(
              +this.model.Code,
            );

          if (resSoConsumption.data.value.length > 0) {
            // enable Drawdown only if there is existing drawdowns against the SO
            this.SalesContractDrawdownEnabled = true;
          }

          this.loadCustomerSalesContracts();

          this.loaded = true;
          this.showLoading(false);

          if (isCopyOrder) {
            this.checkSoAlert(this.model.CustomerCode);
            setTimeout(() => {
              this.priceUpdateInCopy();
            }, 250);
          }
        }
      }
    },
    priceUpdateInCopy() {
      this.$nextTick(function () {
        let myDialog = custom({
          title: "Price Update",
          messageHtml: `Would you like to apply the Sales Prices from Sales Order # ${this.baseView.id} or use the current pricing for ${this.model.CustomerName} ?`,
          buttons: [
            {
              text: "Source Pricing",
              onClick: (e) => {
                return { buttonText: e.component.option("text") };
              },
            },
            {
              text: "Current Pricing",
              onClick: (e) => {
                return { buttonText: e.component.option("text") };
              },
            },
          ],
        });
        myDialog.show().then((dialogResult) => {
          if (dialogResult.buttonText == "Current Pricing") {
            this.updatePriceWithCurrentPrice();
          }
        });
      });
    },
    updatePriceWithCurrentPrice() {
      if (this.reviewDataList._array.length > 0) {
        var promises = [];
        for (var i = 0; i < this.reviewDataList._array.length; i++) {
          var record = this.reviewDataList._array[i];
          // skip price calculations for GL items
          if (record.ItemCode && record.ItemType != "G")
            promises.push(
              SalesOrderV3Service.setSaleItemPrice(
                // REPRICE ALL
                record,
                record,
                this.model.SalesOrderTypeCode,
                this.model.BranchCode,
                this.toolbarModel.CustomerCode,
                this.model.ExchangeRate,
                this.model.DateIn,
                new Date(this.model.RequiredByDate),
                false,
              ),
            );
        }

        Promise.all(promises).then((data) => {
          data.forEach((element) => {
            this.addToReview(element);

            // if (this.soDetailsCurrentView != "Review") {
            var dataGrid = this.$refs["dx-data-grid-comp"].instance;
            setTimeout(() => {
              dataGrid.refresh(true).then(() => {});
            }, 500);
            // }
          });
        });
      }
      this.setFooterItems(this.reviewDataList._array);
    },
    async setRequiredAndShipDates() {
      let factor549DefaultRequiredBy = getFactorByCode(549);

      if (factor549DefaultRequiredBy.Factorc == "Y") {
        let factor550RequiredByLeadDays = getFactorByCode(550);
        let requiredByLeadDays = parseInt(factor550RequiredByLeadDays.Factorc);
        let requiredByDate = new Date();
        requiredByDate.setDate(requiredByDate.getDate() + requiredByLeadDays);

        this.model.RequiredByDate = requiredByDate;

        let factor616DateForPriceGroupPriceSelection = getFactorByCode(616);
        if (factor616DateForPriceGroupPriceSelection.Factorc != "Date In") {
          this.model.FilterDate = this.model.RequiredByDate.toYYYYMMDD();
        } else {
          this.model.FilterDate = this.model.DateIn.toYYYYMMDD();
        }

        let factor565ShipDateSameAsRequiredByDate = getFactorByCode(565);

        if (factor565ShipDateSameAsRequiredByDate.Factorc == "Y") {
          this.model.ShipDate = this.model.RequiredByDate;
        } else {
          this.model.ShipDate = new Date();
        }
      }
    },
    async editSOCallBack() {
      await this.$store.dispatch(
        "screenFactors/getFactors",
        [505, 616, 571, 565, 578, 95, 478],
      );
      await this.getSalesUMs(this.model.BranchCode);
      await this.checkInvoiceAvailability();

      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      var rowIndex = [];
      this.reviewDataList._array.forEach((element) => {
        var ums = [];

        ums = this.salesUMsLocalDictionary[element.OriginItemCode]
          ? this.salesUMsLocalDictionary[element.OriginItemCode]
          : [];

        element.SLUMLength = ums.length;
        element.SalesUMs = ums;

        if (ums.length > 1 && element.PSlipStatus != "C")
          rowIndex.push(dataGrid.getRowIndexByKey(element.ID));
      });

      if (rowIndex.length > 0) dataGrid.repaintRows(rowIndex);

      this.productDataSourceItems.clear();
      this.customerDataSourceItems.clear();
      this.loadProductItems(undefined, 0);
      this.loadCustomerItems(undefined, 0);
    },
    async checkInvoiceAvailability() {
      await InvoiceService.getInvoiceBySalesOrder(this.baseView.id, {
        select: ["SalesOrderCode", "Code"],
      }).then((res) => {
        if (res.data.value.length > 0) this.IsCustomerEditable = false;
      });
    },
    async setSalesRep(customerRepCode) {
      var featureItems = FeatureStorage.getItems();
      if (featureItems[0].ApplicationUserTypeCode == "Sales") {
        await SalesRepresentativeService.getUserDefaultSalesRepresentative().then(
          (res) => {
            if (res.data.value.length > 0)
              this.model.SalesRepresentativeCode =
                res.data.value[0].SalesRepresentativeCode;
          },
        );
      } else if (customerRepCode != "") {
        this.model.SalesRepresentativeCode = customerRepCode;
      } else {
        this.model.SalesRepresentativeCode = getFactorByCode(95).Factorc;
      }

      // await SalesRepresentativeService.getByCode(
      //   this.model.SalesRepresentativeCode
      // ).then((res) => {
      //   if (res && res.data && res.data.value && res.data.value.length > 0)
      //     this.model.SalesRepresentativeFullName =
      //       res.data.value[0].FirstName + " " + res.data.value[0].Surname;
      // });

      if (this.model.SalesRepresentativeCode != "") {
        this.selectPopupModel.filterBySalesRep =
          this.model.SalesRepresentativeCode;
      }
    },
    async setDefaultSalesRep() {
      var featureItems = FeatureStorage.getItems();
      if (featureItems[0].ApplicationUserTypeCode == "Sales") {
        await SalesRepresentativeService.getUserDefaultSalesRepresentative({
          select: ["SalesRepresentativeCode"],
        }).then((res) => {
          if (res.data.value.length > 0)
            this.model.SalesRepresentativeCode =
              res.data.value[0].SalesRepresentativeCode;
        });
      } else {
        if (getFactorByCode(95).Factorc) {
          this.model.SalesRepresentativeCode = getFactorByCode(95).Factorc;
        } else {
          await CustomerService.getByCode(this.baseView.id, [
            "Code",
            "SalesRepresentativeCode",
          ])
            .then((res) => {
              if (res.data.value.length > 0)
                this.model.SalesRepresentativeCode =
                  res.data.value[0].SalesRepresentativeCode;
            })
            .catch((err) => {
              if (err) {
                this.model.SalesRepresentativeCode = undefined;
              }
            });
        }
      }

      // await SalesRepresentativeService.getByCode(
      //   this.model.SalesRepresentativeCode,
      //   {
      //     select: ["Code", "FirstName", "Surname"],
      //   }
      // ).then((res) => {
      //   if (res && res.data && res.data.value && res.data.value.length > 0)
      //     this.model.SalesRepresentativeFullName =
      //       res.data.value[0].FirstName + " " + res.data.value[0].Surname;
      // });

      if (this.model.SalesRepresentativeCode != "") {
        this.selectPopupModel.filterBySalesRep =
          this.model.SalesRepresentativeCode;
      }
    },
    async getSalesUMs(branchCode) {
      await SalesOrderV3Service.getSaleItemCompanyBranchProduct(
        branchCode,
      ).then((res) => {
        for (const item of res.data.value) {
          if (this.salesUMsLocalDictionary[item.ItemCode] === undefined) {
            this.salesUMsLocalDictionary[item.ItemCode] = [
              {
                BranchCode: item.BranchCode,
                ItemCode: item.ItemCode,
                ItemType: item.ItemType,
                SalesUM: item.SalesUM,
              },
            ];
          } else {
            this.salesUMsLocalDictionary[item.ItemCode].push({
              BranchCode: item.BranchCode,
              ItemCode: item.ItemCode,
              ItemType: item.ItemType,
              SalesUM: item.SalesUM,
            });
          }
        }
      });
    },
    async updatePriceData(newData, rowData) {
      // Per-row (FDT70523 fix): only clear this row's own timer entry.
      if (this.priceLookupTimeouts[rowData.ID]) {
        clearTimeout(this.priceLookupTimeouts[rowData.ID]);
        delete this.priceLookupTimeouts[rowData.ID];
      }

      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      // await dataGrid.saveEditData();

      // this.timeOutID = setTimeout(() => {
      // Returned so callers can tell when the recalculation has finished —
      // applyPendingSearchFocus() depends on it (see setUpdatePriceData).
      return SalesOrderV3Service.setSaleItemPrice(
        newData,
        rowData,
        this.model.SalesOrderTypeCode,
        this.model.BranchCode,
        this.toolbarModel.CustomerCode,
        this.model.ExchangeRate,
        this.model.DateIn,
        this.model.RequiredByDate,
        false,
      ).then(async (res) => {
        var rowIndex = dataGrid.getRowIndexByKey(rowData.ID);
        var dataObj = undefined;

        dataGrid.byKey(rowData.ID).then((data) => {
          dataObj = data;
        });
        // dataGrid.option("loadPanel.enabled", false);

        if (dataObj && dataObj.TriggerPriceUpdate) {
          // Update Values on SalesUMFXPrice change
          res = await this.onPriceUpdateValidateGPToolPrcnt(
            res,
            dataObj.SalesUMFXPrice,
            res,
          );

          dataGrid.cellValue(
            rowIndex,
            "SalesUMFXPrice",
            dataObj.SalesUMFXPrice,
          );
          res.TriggerPriceUpdate = false;
          return;
        } else {
          if (
            this.focusedCellData &&
            this.focusedCellData.cellInfo.column.dataField ==
              "SalesUMFXPrice" &&
            // Don't pull focus into the price cell when the user has asked
            // to be in the search box (F2).
            !this.searchFocusRequested
          ) {
            setTimeout(() => {
              dataGrid.cellValue(
                rowIndex,
                "SalesUMFXPrice",
                res.SalesUMFXPrice,
              );
              var cell = dataGrid.getCellElement(rowIndex, "SalesUMFXPrice");
              dataGrid.editCell(rowIndex, "SalesUMFXPrice");
              this.$nextTick(() => {
                dataGrid.focus(cell);
              });
            }, 100);
          }
        }

        res = { ...rowData, ...res };

        if (
          newData.customerContractItemsForProduct &&
          newData.customerContractItemsForProduct.Proceed == true
        ) {
          res = await this.updateSalesOrderDetailsRecord(
            res,
            newData.customerContractItemsForProduct,
          );
          res = await this.onPriceUpdateValidateGPToolPrcnt(
            res,
            res.SalesUMFXPrice,
            res,
          );
          // dataGrid.cellValue(rowIndex, "SalesUMFXPrice", res.SalesUMFXPrice);
        } else if (
          newData.customerContractItemsForProduct &&
          newData.customerContractItemsForProduct.Proceed == false
        ) {
          res = await this.updateSalesOrderDetailsRecord(
            res,
            newData.customerContractItemsForProduct,
          );
        }

        // Qty 0 -> >0 with a header Discount % set: inherit it onto the newly-priced line.
        // Runs after the rowData/contract merges above so nothing here clobbers it, and
        // skipped when a customer sales contract governed the price — that's a
        // pre-negotiated price the header push shouldn't casually override.
        if (
          res.InheritHeaderDiscount &&
          this.model.HeaderDiscountPcnt > 0 &&
          !(
            newData.customerContractItemsForProduct &&
            newData.customerContractItemsForProduct.Proceed == true
          )
        ) {
          res = this.applyDiscountToLine(res, this.model.HeaderDiscountPcnt);
        }
        res.InheritHeaderDiscount = false;

        res.TiggerPriceLookUp = false;
        res.FXPriceBeforeUpdate = JSON.parse(
          JSON.stringify(res.SalesUMFXPrice),
        );
        res.SalesUMFXPricePreviousValue = res.SalesUMFXPrice;

        // Handle IsCopyLine
        if (res.IsCopyLine) {
          const index = this.localCopyItems.findIndex((x) => x.ID === res.ID);
          if (index !== -1) {
            this.localCopyItems[index] = res;
            this.$nextTick(() => {
              this.addToReview(res);
              dataGrid.repaintRows([rowIndex]);
            });
          }
          return;
        }

        // Determine data source and view type
        const viewConfig = this.getViewConfig(rowData);

        if (!viewConfig) {
          return;
        }

        // Update data source
        await viewConfig.dataSource.update(res.ID, res);

        // Update critical cell values
        dataGrid.cellValue(rowIndex, "SalesUMFXPrice", res.SalesUMFXPrice);
        dataGrid.cellValue(rowIndex, "Quantity", res.Quantity);
        // Also force these explicitly (as above) — repaintRows alone can render a stale
        // cached row when the grid's bound dataSource is a derived/cloned array (e.g. the
        // ProductList/CustomerList post-processing), which is exactly the case for a
        // freshly-inherited header discount.
        setTimeout(async () => {
          dataGrid.cellValue(
            rowIndex,
            "SalesUMNetFXPrice",
            res.SalesUMNetPrice,
          );
          dataGrid.cellValue(
            rowIndex,
            "TaxableFXLineTotal",
            res.TaxableFXLineTotal,
          );
          dataGrid.cellValue(rowIndex, "TaxFXLineTotal", res.TaxFXLineTotal);
          dataGrid.cellValue(rowIndex, "FXLineTotal", res.FXLineTotal);

          this.addToReview(res);

          // Reload data source
          await viewConfig.dataSource.load();

          // ADO #78300 (FDT70662) follow-up, QA issue 1: this was previously
          // fire-and-forget. saveEditData() resolves once DevExtreme has
          // actually committed the staged cellValue() edits above; not
          // awaiting it let the refresh(true) below run concurrently with
          // that commit, so the row was sometimes (not always — hence
          // "intermittent") repainted correctly by refresh(true) and then
          // immediately repainted AGAIN, without the flag, by
          // saveEditData()'s own still-in-flight internal repaint — the
          // "flashes red then reverts to white" symptom. Awaiting it first
          // guarantees refresh(true)/repaintRows below is the last word.
          await dataGrid.saveEditData();

          // ADO #78300 (FDT70662): repaintRows() alone redraws the row from
          // whatever array the grid already has bound — for Product/Customer
          // views that's the derived/cloned array getDataSource() computed via
          // productListPostProcess(), not the live reviewDataList entry
          // addToReview() just updated above. Once a line exists in
          // reviewDataList, productListPostProcess() itself already prefers
          // that (correctly-flagged) row over the raw catalog row — but only
          // once getDataSource() actually re-runs and the grid picks up the
          // result, which repaintRows() does not force. refresh(true) does,
          // matching the same fix already used for this exact staleness
          // pattern in setSaleItemPriceAllLines/updatePriceWithCurrentPrice.
          this.$nextTick(() => {
            dataGrid.refresh(true).then(() => {
              // ADO #78300 (FDT70662) follow-up, QA issue 3: refresh(true)
              // above re-renders the row from whatever object DevExtreme's
              // own repaintChangesOnly row cache is still holding for this
              // key — on a line's first-ever repricing (Qty 0 -> >0) that
              // cache lags behind reviewDataList/getDataSource() (confirmed
              // live: getVisibleRows()[rowIndex].data pointed at a stale,
              // pre-discount object even immediately after refresh(true) and
              // a manual repaintRows()/refresh(true) retry), so Net Price/Ex
              // Total/Tax/Inc Total kept showing the pre-discount values
              // (Discount % itself repainted fine — only these four didn't).
              // Re-issuing the same cellValue() force-patch used above, but
              // now after refresh(true) has settled rather than before it,
              // is what actually lands the correct values in the DOM.
              dataGrid.cellValue(
                rowIndex,
                "SalesUMNetFXPrice",
                res.SalesUMNetPrice,
              );
              dataGrid.cellValue(
                rowIndex,
                "TaxableFXLineTotal",
                res.TaxableFXLineTotal,
              );
              dataGrid.cellValue(rowIndex, "TaxFXLineTotal", res.TaxFXLineTotal);
              dataGrid.cellValue(rowIndex, "FXLineTotal", res.FXLineTotal);

              dataGrid.repaintRows([rowIndex]);

              // ADO #78300 (FDT70662) follow-up, QA issue 2: the footer
              // recompute that addToReview() already scheduled (above) reads
              // reviewDataList._array on its own earlier $nextTick, which can
              // fire before this row's line totals/discount finish settling
              // through saveEditData()/refresh(true) — most visibly on a
              // line's first-ever repricing (Qty 0 -> >0), where the footer
              // was seen to lag by exactly the newly-applied discount.
              // Recomputing here, once this row is fully settled, closes
              // that gap regardless of which earlier snapshot lost the race.
              this.setFooterItems(this.reviewDataList._array);
            });
          });
        }, 1);
      });
    },

    getViewConfig(rowData) {
      if (
        this.soDetailsCurrentView === "CustomerList" ||
        rowData.Origin === "Customer"
      ) {
        return { dataSource: this.customerDataSourceItems };
      }

      if (
        this.soDetailsCurrentView === "ProductList" ||
        rowData.Origin === "Product"
      ) {
        return { dataSource: this.productDataSourceItems };
      }

      // Default to review data list
      return { dataSource: this.reviewDataList };
    },

    // Header Form
    async setExchangeRateProperties() {
      if (this.model.CompanyCurrency != this.model.CurrencyCode) {
        let result = await CompanyCurrencyService.getByCurrencyCode(
          this.model.CurrencyCode,
        );
        this.model.ExchangeRate = result.data.value[0].BuyRate;
      } else {
        this.model.ExchangeRate = 1;
      }
    },

    async setDeliveryAddressDataSourceItems(customerCode, lineNo) {
      let defaultLineNo = 1;
      if (lineNo) defaultLineNo = +lineNo;

      let deliveryAddresDs = CustomerDeliveryAddressService.dataSourceConfig(
        "api/CustomerDeliveryAddress",
        {
          select: [
            "CompanyName",
            "PhysicalAddressStreetLine1",
            "PhysicalAddressStreetLine2",
            "PhysicalAddressSuburb",
            "PhysicalAddressState",
            "PhysicalAddressPostcode",
            "PhysicalAddressCountry",
            "RecordId",
            "LineNo",
            "TransitDays",
          ],
          key: "RecordId",
          filter: [
            ["Inactive", "=", "N"],
            "and",
            ["CustomerCode", "=", customerCode],
            "and",
            ["LineNo", "=", defaultLineNo],
          ],
        },
      );

      let results = await deliveryAddresDs.load();

      if (results.length > 0) {
        let deliveryAddressLine1Record = results[0];

        if (deliveryAddressLine1Record) {
          this.model.DeliveryAddressCompanyName =
            deliveryAddressLine1Record.CompanyName;

          this.model.DeliveryAddressCountry =
            deliveryAddressLine1Record.PhysicalAddressCountry;
          this.model.DeliveryAddressPostcode =
            deliveryAddressLine1Record.PhysicalAddressPostcode;
          this.model.DeliveryAddressState =
            deliveryAddressLine1Record.PhysicalAddressState;
          this.model.DeliveryAddressStreetLine1 =
            deliveryAddressLine1Record.PhysicalAddressStreetLine1;
          this.model.DeliveryAddressStreetLine2 =
            deliveryAddressLine1Record.PhysicalAddressStreetLine2;
          this.model.DeliveryAddressSuburb =
            deliveryAddressLine1Record.PhysicalAddressSuburb;
          this.model.CustomerDeliveryAddressLineNo =
            deliveryAddressLine1Record.LineNo;
          this.TransitDays = deliveryAddressLine1Record.TransitDays;
        }
      } else {
        this.model.DeliveryAddressCompanyName = undefined;
        this.model.DeliveryAddressCountry = undefined;
        this.model.DeliveryAddressPostcode = undefined;
        this.model.DeliveryAddressState = undefined;
        this.model.DeliveryAddressStreetLine1 = undefined;
        this.model.DeliveryAddressStreetLine2 = undefined;
        this.model.DeliveryAddressSuburb = undefined;
        this.model.CustomerDeliveryAddressLineNo = undefined;
        this.TransitDays = 0;
      }

      if (this.baseView.mode == "review") return;
      let factor565ShipDateSameAsRequiredByDate = getFactorByCode(565);
      if (
        factor565ShipDateSameAsRequiredByDate.Factorc == "T" &&
        this.TransitDays != 0
      ) {
        this.model.ShipDate = new Date(this.model.RequiredByDate).setDate(
          new Date(this.model.RequiredByDate).getDate() - this.TransitDays,
        );
      } else if (
        factor565ShipDateSameAsRequiredByDate.Factorc == "T" &&
        this.TransitDays == 0
      ) {
        this.model.ShipDate = this.model.RequiredByDate;
      }
    },
    async onDateInValueChanged(e) {
      if (!e.event) return;
      if (e.event && getFactorByCode(616).Factorc == "Date In") {
        this.triggerPriceChange("Date In");
      }
    },
    async loadLinkedContractDates(salesOrderCode) {
      try {
        const contractResponse =
          await CustomerSalesContractService.getContractDatesBySalesOrder(
            salesOrderCode,
          );
        const contractRecords =
          (contractResponse &&
            contractResponse.data &&
            contractResponse.data.value) ||
          [];

        if (contractRecords.length > 0) {
          const contract = contractRecords[0].CustomerSalesContract;
          if (contract && contract.StartDate && contract.EndDate) {
            this.linkedContractDates = {
              startDate: new Date(contract.StartDate),
              endDate: new Date(contract.EndDate),
            };
            return;
          }
        }
        this.linkedContractDates = null;
      } catch (e) {
        this.linkedContractDates = null;
      }
    },

    async onRequiredByDateValueChanged(e) {
      if (!e.event) return;

      // Validate against cached linked Sales Contract dates
      if (this.linkedContractDates) {
        const selectedDate = new Date(e.value);
        const startDate = this.linkedContractDates.startDate;
        const endDate = this.linkedContractDates.endDate;

        if (selectedDate < startDate || selectedDate > endDate) {
          await alert(
            "Invalid Required By Date: The selected date falls outside the linked Sales Contract period. Please choose a date between " +
              formatDate(startDate, "dd MMM yyyy") +
              " and " +
              formatDate(endDate, "dd MMM yyyy") +
              " to proceed.",
            "Validation Error",
          );

          this.model.RequiredByDate = e.previousValue;
          return;
        }
      }

      let factor565ShipDateSameAsRequiredByDate = getFactorByCode(565);

      if (
        factor565ShipDateSameAsRequiredByDate.Factorc == "T" &&
        this.TransitDays != 0
      ) {
        let confirmTransitDateChange = await this.$confirm(
          `Do you want to update the Ship Date by deducting the Transit Days from the new Required By Date?`,
          "Date Change",
        );

        if (confirmTransitDateChange) {
          this.model.ShipDate = new Date(e.value).setDate(
            new Date(e.value).getDate() - this.TransitDays,
          );
        }
      } else if (
        factor565ShipDateSameAsRequiredByDate.Factorc == "Y" ||
        (factor565ShipDateSameAsRequiredByDate.Factorc == "T" &&
          this.TransitDays == 0)
      ) {
        this.model.ShipDate = e.value;
      }

      if (getFactorByCode(616).Factorc != "Date In") {
        this.triggerPriceChange("Reqd Date");
      }
    },
    async onShipDateValueChanged(e) {
      if (!e.event) return;

      let factor565ShipDateSameAsRequiredByDate = getFactorByCode(565);

      if (
        factor565ShipDateSameAsRequiredByDate.Factorc == "T" &&
        this.TransitDays != 0
      ) {
        let confirmTransitDateChange = await this.$confirm(
          `Do you want to update the Required By date by adding the Transit Days to the new Ship Date?`,
          "Date Change",
        );

        if (confirmTransitDateChange) {
          this.model.RequiredByDate = new Date(e.value).setDate(
            new Date(e.value).getDate() + this.TransitDays,
          );
        }
      }
    },

    // Details Tab
    async onDiscountPrctUpdate(newData, value, currentRowData) {
      if (this.discountTimeOut) clearTimeout(this.discountTimeOut);

      // this.discountTimeOut = setTimeout(async () => {
      // newData.DiscPcnt = value;
      var SalesUMNetFXPrice = calculateSalesUMNetFXPrice(
        currentRowData.SalesUMFXPrice,
        value,
      );

      let newsalesUMNetPrice = calculateSalesUMNetPriceFromSalesUMNetFXPrice(
        SalesUMNetFXPrice,
        this.model.ExchangeRate,
      );

      let tempGp = calculateGrossProfitPcntRaw(
        currentRowData.SalesUMStandardCost,
        newsalesUMNetPrice,
      );

      let globalGPVal = parseFloat(getFactorByCode(571).Factorc);

      let marginBreached =
        globalGPVal != 0 && (tempGp < globalGPVal || tempGp > 100);
      let hasMinMarginOverride =
        marginBreached &&
        FeatureStorage.checkPermission("MinMarginOverride", "edit");

      if (marginBreached && !hasMinMarginOverride) {
        if (currentRowData.SalesUMQty != 0) {
          let res = await alert(
            `Net Price is below the minimum GP of ${
              globalGPVal.roundNumber(2) + " %"
            }. Please enter a discount that meets the minimum GP % requirement.`,
            "Validation Error",
          );

          if (res) {
            this.$store.dispatch(
              "numericInputStore/updateValue",
              currentRowData.DiscPcnt,
            );

            this.$store.dispatch("numericInputStore/lastEntry", {
              value: 0,
              remove: true,
            });

            // newData.DiscPcnt = currentRowData.DiscPcnt;
            var dataGrid = this.$refs["dx-data-grid-comp"].instance;

            this.$nextTick(() => {
              dataGrid.saveEditData();
            });

            setTimeout(() => {
              newData.TriggerDiscountUpdate = false;
              newData.DiscPcnt = currentRowData.DiscPcntPreviousValue;
              this.updateDataSource(newData, currentRowData, true);

              setTimeout(() => {
                var rowIndex = dataGrid.getRowIndexByKey(currentRowData.ID);
                var cell = dataGrid.getCellElement(rowIndex, "DiscPcnt");
                dataGrid.editCell(rowIndex, "DiscPcnt");

                this.$nextTick(() => {
                  dataGrid.focus(cell);
                });
              }, 50);

              setTimeout(() => {
                this.applyFocusClass();
              }, 100);
            }, 100);
          }
        }

        return newData;
      }

      if (
        marginBreached &&
        hasMinMarginOverride &&
        currentRowData.SalesUMQty != 0
      ) {
        this.$notify(
          `Net Price is below the minimum GP of ${
            globalGPVal.roundNumber(2) + " %"
          }. Proceeding — you hold the Min Margin Override permission.`,
          "error",
        );
      }

      newData.TriggerDiscountUpdate = false;
      newData.DiscPcnt = value;
      newData.DiscPcntPreviousValue = value;
      newData.SalesUMNetFXPrice = SalesUMNetFXPrice;
      newData.SalesUMNetPrice = newsalesUMNetPrice;
      newData.MarginOverrideWarning = marginBreached;

      var obj = { ...currentRowData, ...newData };
      SalesOrderState.setStockPrices(obj);
      SalesOrderState.setLineTotals(obj, this.model);

      if (obj.SalesUMQty > 0) {
        this.addToReview(obj);
      }

      this.updateDataSource(obj, undefined, true);

      // ADO #75825 (ported from feature/2.63-SalesOrderPerformance, commit
      // 241de2177 - never merged into this branch): updateDataSource() above
      // only calls dataGrid.refresh(true) for Product/CustomerList rows,
      // which doesn't reliably repaint a row whose grid dataSource is the
      // derived/post-processed array (see getDataSource()/productListPostProcess).
      // Net Price already renders correctly via DevExtreme's own change-
      // tracking on newData (set above); Ex Total/Tax/Inc Total are only set
      // on the separate obj here and need the same explicit dataGrid.cellValue()
      // force-patch already used by updatePriceData's tail for this exact reason.
      // Patches "SalesUMNetFXPrice" (not the original fix's "SalesUMNetPrice")
      // to match the Net Price column's dataField after ADO #77292.
      if (
        this.soDetailsCurrentView == "CustomerList" ||
        this.soDetailsCurrentView == "ProductList" ||
        obj.Origin == "Customer" ||
        obj.Origin == "Product"
      ) {
        dataGrid = this.$refs["dx-data-grid-comp"].instance;
        var rowIndex = dataGrid.getRowIndexByKey(obj.ID);
        setTimeout(() => {
          dataGrid.cellValue(
            rowIndex,
            "SalesUMNetFXPrice",
            obj.SalesUMNetFXPrice,
          );
          dataGrid.cellValue(
            rowIndex,
            "TaxableFXLineTotal",
            obj.TaxableFXLineTotal,
          );
          dataGrid.cellValue(rowIndex, "TaxFXLineTotal", obj.TaxFXLineTotal);
          dataGrid.cellValue(rowIndex, "FXLineTotal", obj.FXLineTotal);
        }, 1);
      }
      // }, 100);
    },
    onHeaderDiscountPcntConfirmed(e) {
      if (!e.event) return; // ignore programmatic value resets (e.g. model reload)
      if (this.headerDiscountLocked) return;

      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      if (dataGrid) {
        dataGrid.saveEditData();
      }

      var value = e.value;
      if (value === null || value === undefined || isNaN(value)) return;

      this.cascadeHeaderDiscountToLines(value);
    },
    // Shared discount-recalculation used both for the header bulk push and for a single line
    // inheriting the header discount on Qty 0 -> >0. Mirrors the client-side recalculation
    // onDiscountPrctUpdate does for a direct line edit (no server-side pricing-engine re-fetch),
    // but never blocks/reverts on a margin breach — callers decide whether that's appropriate.
    applyDiscountToLine(record, discountPcnt) {
      var SalesUMNetFXPrice = calculateSalesUMNetFXPrice(
        record.SalesUMFXPrice,
        discountPcnt,
      );

      let newSalesUMNetPrice = calculateSalesUMNetPriceFromSalesUMNetFXPrice(
        SalesUMNetFXPrice,
        this.model.ExchangeRate,
      );

      let tempGp = calculateGrossProfitPcntRaw(
        record.SalesUMStandardCost,
        newSalesUMNetPrice,
      );

      let globalGPVal = parseFloat(getFactorByCode(571).Factorc);
      let marginBreached =
        globalGPVal != 0 && (tempGp < globalGPVal || tempGp > 100);

      var obj = {
        ...record,
        DiscPcnt: discountPcnt,
        DiscPcntPreviousValue: discountPcnt,
        SalesUMNetFXPrice: SalesUMNetFXPrice,
        SalesUMNetPrice: newSalesUMNetPrice,
        MarginOverrideWarning: marginBreached,
      };

      obj = SalesOrderState.setStockPrices(obj);
      obj = SalesOrderState.setLineTotals(obj, this.model);

      return obj;
    },
    // Pushes a single Discount % onto every line currently on the order — unlike a direct line
    // edit or the Qty-inherit path, this intentionally suppresses the per-line minimum-GP-breach
    // confirmation modal, so lines save with the pushed discount regardless of margin breach.
    cascadeHeaderDiscountToLines(discountPcnt) {
      var lines = this.reviewDataList._array.filter(
        (line) => line.ItemCode && line.ItemType != "G" && line.SalesUMQty > 0,
      );

      if (lines.length == 0) return;

      lines.forEach((currentRowData) => {
        this.addToReview(
          this.applyDiscountToLine(currentRowData, discountPcnt),
        );
      });

      var dataGrid = this.$refs["dx-data-grid-comp"].instance;

      this.setFooterItems(this.reviewDataList._array);

      this.$notify(
        `Discount of ${discountPcnt}% applied to ${lines.length} line(s).`,
        "success",
      );

      this.$nextTick(() => {
        if (dataGrid) dataGrid.refresh(true);
      });
    },
    async onPriceUpdateValidateGPToolPrcnt(newData, value, currentRowData) {
      if (this.priceUpdateTimeOut) clearTimeout(this.priceUpdateTimeOut);
      // this.priceUpdateTimeOut = setTimeout(async () => {
      // -----
      var SalesUMNetFXPrice = calculateSalesUMNetFXPrice(
        value,
        currentRowData.DiscPcnt,
      );

      let newsalesUMNetPrice = calculateSalesUMNetPriceFromSalesUMNetFXPrice(
        SalesUMNetFXPrice,
        this.model.ExchangeRate,
      );

      let tempGp = calculateGrossProfitPcntRaw(
        currentRowData.SalesUMStandardCost,
        newsalesUMNetPrice,
      );

      let globalGPVal = parseFloat(getFactorByCode(571).Factorc);

      // ADO #78300 (FDT70662, Finding 3): was missing the "|| tempGp > 100"
      // clause its siblings (onDiscountPrctUpdate/applyDiscountToLine) have.
      let marginBreached =
        globalGPVal != 0 && (tempGp < globalGPVal || tempGp > 100);
      let hasMinMarginOverride =
        marginBreached &&
        FeatureStorage.checkPermission("MinMarginOverride", "edit");

      if (marginBreached && !hasMinMarginOverride) {
        if (currentRowData.SalesUMQty != 0) {
          let res = await alert(
            `Net Price is below the minimum GP of ${
              globalGPVal.roundNumber(2) + " %"
            }. Please enter a discount that meets the minimum GP % requirement.`,
            "Validation Error",
          );

          if (res) {
            var dataGrid = this.$refs["dx-data-grid-comp"].instance;
            this.$nextTick(() => {
              dataGrid.saveEditData();
            });

            setTimeout(() => {
              // newData.TriggerPriceUpdate = false;
              newData.SalesUMFXPrice =
                currentRowData.SalesUMFXPricePreviousValue;
              this.updateDataSource(newData, currentRowData, true);

              setTimeout(() => {
                var rowIndex = dataGrid.getRowIndexByKey(currentRowData.ID);
                var cell = dataGrid.getCellElement(rowIndex, "SalesUMFXPrice");
                dataGrid.editCell(rowIndex, "SalesUMFXPrice");

                this.$nextTick(() => {
                  dataGrid.focus(cell);
                });
              }, 50);

              setTimeout(() => {
                this.applyFocusClass();
              }, 100);
            }, 100);
          }
        }
      } else {
        if (
          marginBreached &&
          hasMinMarginOverride &&
          currentRowData.SalesUMQty != 0
        ) {
          this.$notify(
            `Net Price is below the minimum GP of ${
              globalGPVal.roundNumber(2) + " %"
            }. Proceeding — you hold the Min Margin Override permission.`,
            "error",
          );
        }

        newData.SalesUMFXPrice = value;
        newData.SalesUMFXPricePreviousValue = value;

        newData.SalesUMNetFXPrice = SalesUMNetFXPrice;

        // setBaseCurrencyPriceFromFXPrice
        newData.SalesUMPrice = calculateSalesUMPriceFromSalesUMFXPrice(
          newData.SalesUMFXPrice,
          this.model.ExchangeRate,
        );

        newData.CalculatedDiscPcnt = calculateCalculatedDiscPcnt(
          currentRowData.SalesUMStandardPrice,
          newData.SalesUMPrice,
        );

        newData.SalesUMNetPrice = newsalesUMNetPrice;

        // setStockPrices
        newData.StockUMQty = calculateStockUMQty(
          currentRowData.SalesUMQty,
          currentRowData.SalesUMConversion,
        );

        // Stock
        newData.StockUMFXPrice = calculateStockUMFXPrice(
          newData.SalesUMFXPrice,
          currentRowData.SalesUMConversion,
        );

        newData.StockUMNetFXPrice = calculateStockUMNetFXPrice(
          newData.SalesUMNetFXPrice,
          currentRowData.SalesUMConversion,
        );

        newData.StockUMPrice = calculateStockUMPrice(
          newData.SalesUMPrice,
          currentRowData.SalesUMConversion,
        );

        newData.StockUMNetPrice = calculateStockUMNetPrice(
          newData.SalesUMNetPrice,
          currentRowData.SalesUMConversion,
        );

        // setLineTotals
        // FX Totals
        newData.DiscFXLineTotal = calculateDiscFXLineTotal(
          newData.SalesUMFXPrice,
          newData.SalesUMNetFXPrice,
          currentRowData.SalesUMQty,
        );
        newData.NetFXLineTotal = calculateNetFXLineTotal(
          newData.SalesUMNetFXPrice,
          currentRowData.SalesUMQty,
          newData.SalesUMFXPrice,
        );
        newData.WetFXLineTotal = calculateWetFXLineTotal(
          newData.NetFXLineTotal,
          currentRowData.WetPcnt,
        );

        newData.TaxableFXLineTotal = calculateTaxableFXLineTotal(
          newData.NetFXLineTotal,
          newData.WetFXLineTotal,
        );

        newData.TaxFXLineTotal = calculateTaxFXLineTotal(
          newData.NetFXLineTotal,
          newData.WetFXLineTotal,
          currentRowData.SalesTaxPcnt,
        );

        newData.FXLineTotal = calculateFXLineTotal(
          newData.TaxableFXLineTotal,
          newData.TaxFXLineTotal,
        );

        // Totals
        newData.DiscLineTotal = calculateDiscLineTotal(
          newData.DiscFXLineTotal,
          this.model.ExchangeRate,
        );
        newData.NetLineTotal = calculateNetLineTotal(
          newData.NetFXLineTotal,
          this.model.ExchangeRate,
        );
        newData.WetLineTotal = calculateWetLineTotal(
          newData.WetFXLineTotal,
          this.model.ExchangeRate,
        );
        newData.TaxableLineTotal = calculateTaxableLineTotal(
          newData.TaxableFXLineTotal,
          this.model.ExchangeRate,
        );
        newData.TaxLineTotal = calculateTaxLineTotal(
          newData.TaxFXLineTotal,
          this.model.ExchangeRate,
        );
        newData.LineTotal = calculateLineTotal(
          newData.FXLineTotal,
          this.model.ExchangeRate,
        );

        // newData.TriggerPriceUpdate = false;

        // ADO #78300 (FDT70662, Finding 2): set on the original newData
        // reference BEFORE the reassignment below, mirroring
        // onDiscountPrctUpdate's (correct) ordering — DevExtreme's batch-edit
        // grid tracks the object setCellValue originally passed in, and a
        // property set only on a later reassigned copy isn't reliably picked
        // up by its own change-tracking under repaintChangesOnly.
        newData.MarginOverrideWarning = marginBreached;

        newData = { ...currentRowData, ...newData };

        this.addToReview(newData);
        this.updateDataSource(newData);

        // this.$nextTick(() => {
        //   setTimeout(() => {
        //     var dataGrid = this.$refs["dx-data-grid-comp"].instance;
        //     dataGrid.saveEditData();

        //     this.applyFocusClass();
        //   }, 100);
        // });

        await this.$nextTick();

        var dataGrid = this.$refs["dx-data-grid-comp"].instance;
        if (dataGrid) {
          await dataGrid.saveEditData();
        }

        // ADO #78300 (FDT70662, Fix #3 — defensive): mirrors the explicit
        // cellValue() force-patch onDiscountPrctUpdate already has (ADO
        // #75825) for the same repaintChangesOnly-under-batch-edit gap, so
        // the netprice-warn class repaints reliably in these views too.
        if (
          this.soDetailsCurrentView == "CustomerList" ||
          this.soDetailsCurrentView == "ProductList" ||
          newData.Origin == "Customer" ||
          newData.Origin == "Product"
        ) {
          var rowIndex = dataGrid.getRowIndexByKey(newData.ID);
          setTimeout(() => {
            dataGrid.cellValue(
              rowIndex,
              "SalesUMNetFXPrice",
              newData.SalesUMNetFXPrice,
            );
            dataGrid.cellValue(
              rowIndex,
              "TaxableFXLineTotal",
              newData.TaxableFXLineTotal,
            );
            dataGrid.cellValue(
              rowIndex,
              "TaxFXLineTotal",
              newData.TaxFXLineTotal,
            );
            dataGrid.cellValue(rowIndex, "FXLineTotal", newData.FXLineTotal);
          }, 1);
        }
      }
      // }, 2000);

      return newData;
    },
    toolbarPreparing(e) {
      var toolbarItems = e.toolbarOptions.items;
      var searchPanel = toolbarItems.find((item) => {
        return item.name === "searchPanel";
      });
      // var columnChooserButton = toolbarItems.find((item) => {
      //   return item.name === "columnChooserButton";
      // });

      let itemsToRemoveFromToolBar = [
        "searchPanel",
        "revertButton",
        "saveButton",
        // "columnChooserButton",
      ];

      e.toolbarOptions.items = toolbarItems.filter(
        (item) => !itemsToRemoveFromToolBar.includes(item.name),
      );

      searchPanel.location = "after";
      e.toolbarOptions.items.unshift(
        {
          location: "before",
          widget: "dxSelectBox",
          options: {
            width: this.isTablet ? 130 : 200,
            items: [
              {
                value: "Review",
                text: "Review",
              },
              {
                value: "ProductList",
                text: "Product",
              },
              {
                value: "CustomerList",
                text: "Customer",
              },
            ],
            displayExpr: "text",
            valueExpr: "value",
            value: this.soDetailsCurrentView,
            disabled: this.baseView.mode == "review",
            onValueChanged: (e) => {
              this.forceRefresh = true;
              this.focusedCellData = undefined;
              this.filterFormItems.forEach((element) => {
                if (element.filterDefaultValue)
                  delete element.filterDefaultValue;
              });
              this.showRightContainer = false;
              this.soDetailsCurrentView = e.value;

              if (this.soDetailsCurrentView == "ProductList") {
                setTimeout(() => {
                  this.$nextTick(() => {
                    var dataGrid = this.$refs["dx-data-grid-comp"]
                      ? this.$refs["dx-data-grid-comp"].instance
                      : undefined;

                    // Defensive: force-drop any column sort state DevExtreme may
                    // be retaining, so the Product view's Factor 478 order isn't
                    // overridden by a leftover Description sort.
                    if (dataGrid) dataGrid.clearSorting();
                  });
                }, 100);
              }

              // FDT70523: switching into Review right after a line's
              // first-time price/tax lookup resolved could show that line's
              // own Tax cell as stale/0.00 even though reviewDataList (and
              // therefore the footer) already had the correct figure — the
              // grid remounts here (:key="soDetailsCurrentView") and, with
              // repaintChangesOnly + virtual row rendering, its first paint
              // of the newly-bound reviewDataList array could still lag the
              // data. Force a hard refresh once the new instance is mounted
              // so it repaints every row from current data instead of
              // whatever it captured on that first pass.
              if (this.soDetailsCurrentView == "Review") {
                setTimeout(() => {
                  this.$nextTick(() => {
                    var dataGrid = this.$refs["dx-data-grid-comp"]
                      ? this.$refs["dx-data-grid-comp"].instance
                      : undefined;
                    if (dataGrid) dataGrid.refresh(true);
                  });
                }, 100);
              }

              if (
                CommonStorage.getItems(
                  this.getFilterPersistenceKey + "-scroll-offset",
                )
              )
                this.scrollingOffSet = JSON.parse(
                  CommonStorage.getItems(
                    this.getFilterPersistenceKey + "-scroll-offset",
                  ),
                );
            },
          },
        },
        {
          location: "after",
          widget: "dxForm",
          cssClass: "toolbar-right-form",
          visible: this.soDetailsCurrentView != "Review",
          options: {
            formData: this.StockModel,
            items: [
              {
                label: {
                  text: "Available Stock",
                },
                editorType: "dxSwitch",
                dataField: "AvailableStock",
                cssClass: "available-stock-switch",
                editorOptions: {
                  onValueChanged: (e) => {
                    this.applyStockFilter(e);
                  },
                },
              },
            ],
            validationGroup: "DataGridToolBarForm",
            labelLocation: "left",
            showRequiredMark: false,
            colCount: 1,
          },
        },
        searchPanel,
        // columnChooserButton,
        {
          location: "after",
          locateinmenu: "auto",
          widget: "dxButton",
          // visible: !this.isTablet,
          options: {
            tabIndex: -1,
            icon: this.gridExpand ? "collapse" : "expand",
            onClick: () => {
              this.gridExpand = !this.gridExpand;

              setTimeout(() => {
                this.reSizeGrid();
              }, 100);
            },
          },
        },
        {
          location: "after",
          locateinmenu: "auto",
          widget: "dxButton",
          options: {
            tabIndex: -1,
            validationGroup: "formsData",
            icon: "filter",
            elementAttr: { id: "filter-btn" },
            onClick: () => {
              this.showRightContainer = !this.showRightContainer;
              var item = document.getElementById("filter-btn");
              item.classList.add("dx-state-invisible");

              setTimeout(() => {
                this.reSizeGrid();
              }, 100);
            },
          },
        },
      );
    },
    onCellPrepared(e) {
      if (e.rowType == "data") {
        if (e.column.dataField == "Available") {
          // e.cellElement.style.backgroundColor = this.getAvailableCellShading(
          //   e.data,
          // );

          e.cellElement.style.setProperty(
            "background-color",
            this.getAvailableCellShading(e.data),
            "important",
          );
        }

        if (e.column.dataField == "SalesUMNetFXPrice") {
          if (e.data.MarginOverrideWarning) {
            e.cellElement.classList.add("netprice-warn");
          } else {
            e.cellElement.classList.remove("netprice-warn");
          }
        }

        if (
          ["SalesUM", "SalesUMQty", "DiscPcnt", "SalesUMFXPrice"].includes(
            e.column.dataField,
          )
        ) {
          if (e.row.data.PSlipStatus == "C" || e.row.data.WorkOrderCode) {
            e.cellElement.className = "";
          }

          if (
            e.row.data.SalesUMSuppliedQty > 0 &&
            ["DiscPcnt", "SalesUMFXPrice"].includes(e.column.dataField)
          ) {
            e.cellElement.className = "";
          }

          if (e.column.dataField == "SalesUM") {
            if (e.data.SLUMLength < 2) {
              e.cellElement.classList.remove("gc-editable-cell-color");
            }
          }
        }

        if (
          e.column.dataField === "OriginItemCode" ||
          e.column.dataField === "Description"
        ) {
          const data = e.data;
          if (
            data &&
            (data.ItemCode === this.DummyProductItemCode ||
              data.OriginItemCode === this.DummyProductItemCode)
          ) {
            e.cellElement.style.setProperty(
              "background-color",
              "rgba(232, 56, 79, 0.1)",
              "important",
            );
            e.cellElement.classList.add("dummy-product-row");
          } else {
            e.cellElement.classList.remove("dummy-product-row");
          }
        }
      }
    },
    async getItemPriceDetails(newData, currentRowData) {
      SalesOrderV3Service.setSaleItemPrice(
        newData,
        currentRowData,
        this.model.SalesOrderTypeCode,
        this.model.BranchCode,
        this.toolbarModel.CustomerCode,
        this.model.ExchangeRate,
        this.model.DateIn,
        this.model.RequiredByDate,
        false,
      ).then((res) => {
        return res;
      });
    },
    addToReview(item) {
      if (item.OriginItemCode || item.ItemCode) {
        if (this.soDetailsCurrentView != "Review") {
          var i = this.reviewDataList._array.findIndex((x) => x.ID == item.ID);

          if (i == -1 && item.SalesUMQty != 0) {
            this.reviewDataList.push([
              {
                type: "insert",
                key: item.ID,
                data: item,
              },
            ]);
          } else {
            if (item.SalesUMQty == 0) {
              this.updateCopyItems(item);
              // this.reviewDataList.push([
              //   {
              //     type: "remove",
              //     key: item.ID,
              //   },
              // ]);
              this.reviewDataList.remove(item.ID);

              this.focusedCellData = undefined;
              this.selectedRowData = undefined;
            } else {
              this.reviewDataList.push([
                {
                  type: "update",
                  data: item,
                  key: item.ID,
                },
              ]);
            }
            this.reviewDataList.load();
          }
        } else {
          if (item.SalesUMQty == 0) {
            this.updateCopyItems(item);
            this.reviewDataList.push([
              {
                type: "remove",
                key: item.ID,
              },
            ]);

            this.focusedCellData = undefined;
            this.selectedRowData = undefined;
          } else {
            this.reviewDataList.push([
              {
                type: "update",
                data: item,
                key: item.ID,
              },
            ]);
          }
          this.reviewDataList.load();
        }

        // FDT70523 fix: recompute the footer as soon as the push into
        // reviewDataList above has actually landed, rather than behind a
        // fixed, unconditional 200ms timer that let the footer/Review view
        // render before the most-recently-entered line was counted.
        this.$nextTick(() => {
          this.setFooterItems(this.reviewDataList._array);
        });
      }
    },
    updateCopyItems(item) {
      if (item.IsCopyLine) {
        let index = this.localCopyItems.findIndex((x) => x.ID == item.ID);
        if (index != -1) {
          this.localCopyItems[index] = item;
        }
      }
    },
    onContentReady(e) {
      this.addGridNoResults(e, this.noDataMessage());

      if (e.component.getDataSource()) {
        this.gridDataSourceItems = e.component.getDataSource()._items;

        if (this.gridDataSourceItems.length > 0 && this.scrollingOffSet) {
          var scrollComponent = e.component.getScrollable();
          scrollComponent.scrollBy(this.scrollingOffSet);
          CommonStorage.saveItems(
            this.getFilterPersistenceKey + "-scroll-offset",
            JSON.stringify(this.scrollingOffSet),
          );
          this.scrollingOffSet = undefined;
        }

        if (this.forceRefresh) {
          this.forceRefresh = false;
          if (this.soDetailsCurrentView != "Review") {
            if (this.StockModel.AvailableStock) {
              this.applyStockFilter({ value: true });
            }
          }
        }
      }

      // remove table headers tabindex
      var headers = e.element.querySelector(".dx-datagrid-headers");
      headers.querySelectorAll("[tabindex]").forEach(function (header) {
        header.removeAttribute("tabIndex");
      });
    },
    onOptionChanged(e) {
      var scrollComponent = e.component.getScrollable();

      if (scrollComponent) {
        CommonStorage.saveItems(
          this.getFilterPersistenceKey + "-scroll-offset",
          JSON.stringify(scrollComponent.scrollOffset()),
        );
      }
    },

    noDataMessage() {
      let noDataMessageObject = {
        addNew: {},
      };

      if (this.soDetailsCurrentView == "Review") {
        noDataMessageObject.addNew = {
          class: "add-new",
          title: "No Order Lines",
          message: "Get Started, Add an Order Line",
        };
      } else if (this.soDetailsCurrentView == "ProductList") {
        noDataMessageObject.addNew = {
          class: "no-data",
          title: "No Product Lines",
          message:
            "Products must be marked Available for Sale to appear here.  Head over to the Product screen to adjust this setting.",
        };
      } else if (this.soDetailsCurrentView == "CustomerList") {
        noDataMessageObject.addNew = {
          class: "no-data",
          title: "No Order Lines",
          message:
            "Products despatched and invoiced to the customer will show here.  Head over to the Despatch Order screen to despatch and invoice orders for this customer",
        };
      } else {
        noDataMessageObject.addNew = {
          class: "add-new",
          title: "No Order Lines",
          message: "Get Started, Add an Order Line",
        };
      }

      return noDataMessageObject;
    },
    refreshDataGrid() {
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      dataGrid.repaint(true);
    },

    reSizeGrid() {
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;

      dataGrid.beginUpdate();
      dataGrid.updateDimensions();
      dataGrid.endUpdate();
    },
    async onSalesUMQtyFocusIn(e, cellInfo) {
      let inputEl = e.element.querySelector(".dx-texteditor-input");

      this.focusedCellData = {
        e: e,
        cellInfo: cellInfo,
        requestSent: false,
        inputEl: inputEl,
      };

      this.applyFocusClass(inputEl);
    },
    onEditingStart(e) {
      if (e.data.PSlipStatus == "C") e.cancel = true;
      if (e.data.WorkOrderCode) {
        e.cancel = true;
      }
      if (
        e.data.SalesUMSuppliedQty > 0 &&
        e.column &&
        ["SalesUMFXPrice", "DiscPcnt"].includes(e.column.dataField)
      ) {
        e.cancel = true;
      }
    },
    onEditorPreparing(e) {
      if (e.editorOptions && e.editorOptions.onFocusIn) {
        let func = e.editorOptions.onFocusIn;
        if (this.baseView.mode != "review" && e.parentType != "searchPanel")
          e.editorOptions.onFocusIn = (event) => {
            func(event, {
              column: {
                dataField: e.dataField,
              },
              data: e.row.data,
              rowIndex: e.row.rowIndex,
            });
          };
      }

      if (e.editorOptions && e.editorOptions.onKeyDown) {
        let func = e.editorOptions.onKeyDown;
        if (this.baseView.mode != "review" && e.parentType != "searchPanel")
          e.editorOptions.onKeyDown = (event) => {
            func(event, {
              column: {
                dataField: e.dataField,
              },
              data: e.row.data,
              rowIndex: e.row.rowIndex,
            });
          };
      }
    },
    applyFocusClass(inputEl) {
      if (!this.focusedCellData) return;

      if (!inputEl) inputEl = this.focusedCellData.inputEl;

      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      var cell = dataGrid.getCellElement(
        this.focusedCellData.cellInfo.rowIndex,
        this.focusedCellData.cellInfo.column.dataField,
      );
      // var editor = cell.querySelector("div.gc-grid-number-box-editor");
      // if (editor) editor.classList.add("key-pad-focused");

      dataGrid.editCell(
        this.focusedCellData.cellInfo.rowIndex,
        this.focusedCellData.cellInfo.column.dataField,
      );

      if (cell) cell.classList.add("key-pad-focused-cell");
    },
    removeCellFocus() {
      // var cell = document.querySelectorAll(".key-pad-focused-cell");
      // if (cell.length > 0) cell[0].classList.remove("key-pad-focused-cell");
    },
    showNumericKeyPad() {
      if (!this.showRightContainer) {
        this.showRightContainer = !this.showRightContainer;
      }
      this.sidebarTabSelectedIndex = 1;

      var item = document.getElementById("filter-btn");
      item.classList.add("dx-state-invisible");

      setTimeout(() => {
        this.reSizeGrid();
      }, 50);
    },
    onSoDetailsLineContextMenuItemClick(e) {
      if (e.itemData.menuText == "Line Comment") {
        let rowData = e.itemData.data.data;

        let visibleOnInvoiceFactor = getFactorByCode(505);

        this.lineCommentItems.RowData = rowData;
        this.lineCommentModel.ID = rowData.ID;
        this.lineCommentModel.Comment = rowData.Comment;
        this.lineCommentModel.IsCommentVisible =
          rowData.LineNo == 0
            ? visibleOnInvoiceFactor.Factorc == "Y"
              ? true
              : rowData.IsCommentVisible
            : rowData.IsCommentVisible;

        this.lineCommentsPopupVisible = true;
      } else if (e.itemData.menuText == "Item History") {
        this.model.LineItemCode = e.itemData.data.data.OriginItemCode;
        this.customerItemHistory = e.itemData.data.data;
        this.itemHistoryPopupVisible = true;
        this.$logger.trackEventOpenPopup("Sales Order Item History");
      } else if (e.itemData.menuText == "GP Tool") {
        if (e.itemData.data.data.SalesUMQty == 0) {
          this.displayValidationErrors([
            `Quantity cannot be 0. Please enter a quantity to access the GP Tool.`,
          ]);
        } else {
          this.gpToolPopupVisible = true;
          this.$logger.trackEventOpenPopup("Sales Order GP Tool");
          this.go;
          this.gpTool.data = e.itemData.data.data;
          this.gpTool.cellInfo = e.itemData.data;
          this.gpTool.model.quantity = e.itemData.data.data.SalesUMQty;
          this.gpTool.model.standardSell =
            e.itemData.data.data.SalesUMStandardPrice * this.model.ExchangeRate;
          this.gpTool.model.currentPrice =
            e.itemData.data.data.SalesUMNetFXPrice;

          this.gpTool.model.gp =
            this.gpTool.data.gpprcnt > 0
              ? this.gpTool.data.gpprcnt
              : calculateGrossProfitPcnt(
                  Math.round(e.itemData.data.data.SalesUMStandardCost * 100) /
                    100,
                  e.itemData.data.data.SalesUMNetFXPrice,
                );
          this.gpTool.model.newSalesUMNetPrice =
            e.itemData.data.data.SalesUMNetFXPrice;
        }
      } else if (e.itemData.menuText == "Price Breakdown") {
        this.priceBreakdownLineData = e.itemData.data.data;
        this.priceBreakdownPopupVisible = true;
      } else if (e.itemData.menuText == "Copy Line") {
        if (this.focusedCellData) this.removeCellFocus();
        this.focusedCellData = undefined;
        this.selectedRowData = undefined;
        var copyItem = { ...e.itemData.data.data };
        if (copyItem.IsCopyLine) {
          copyItem.ItemCode =
            copyItem.OriginItemCode + "-" + new Date().getTime();
        } else {
          copyItem.ItemCode = copyItem.ItemCode + "-" + new Date().getTime();
        }
        copyItem.ID = copyItem.ItemCode;

        // copyItem.SalesUMQty = 0;
        // copyItem.CustomerSalesContractCode = null;
        // copyItem.CustomerSalesContractLineNo = null;
        copyItem.IsCopyLine = true;
        copyItem.LineNo = 0;
        // copyItem.SalesUMSuppliedQty = 0;

        copyItem = SalesOrderState.setStockPrices(copyItem);
        copyItem = SalesOrderState.setLineTotals(copyItem, this.model);

        this.localCopyItems.push(copyItem);

        // if (this.soDetailsCurrentView == "Review") {
        this.reviewDataList.insert(copyItem);
        // }

        // this.addToReview(copyItem);
        // this.reviewDataList.push([
        //   {
        //     type: "insert",
        //     key: copyItem.ID,
        //     data: copyItem,
        //   },
        // ]);
        this.reviewDataList.load();

        // var dataGrid = this.$refs["dx-data-grid-comp"].instance;
        // var ds = dataGrid.getDataSource();
        // ds.requireTotalCount(false);
        // ds.reload();

        //  setTimeout(() => {
        this.$nextTick(() => {
          var dataGrid = this.$refs["dx-data-grid-comp"].instance;
          dataGrid.saveEditData();
          dataGrid.repaint();
          setTimeout(() => {
            this.setFooterItems(this.reviewDataList._array);
            this.focusOnQtyFromSalesUM(copyItem);
          }, 100);
        });
        //  }, 500);

        // this.customerDataSourceItems.load();
      } else if (e.itemData.menuText == "Update Item") {
        this.updateItemLineData = e.itemData.data.data;
        this.updateItemPopupVisible = true;
      }
    },
    onUpdateItemSaved(payload) {
      this.showLoading(true);
      var record = this.reviewDataList._array.find(
        (r) => r.ID === payload.rowKey,
      );
      if (!record) {
        this.showLoading(false);
        return;
      }

      // The popup already ran the full repricing (setSaleItemPrice) before
      // emitting "saved" — payload is the complete, already-corrected field
      // set for this line, including Price, Discount %, Tax Code, WET%,
      // costs and all line totals refreshed from the New Item's own pricing
      // (BR-6/BR-7a/BR-8a). Apply it as-is; nothing left to compute here.
      var fields = { ...payload };
      delete fields.rowKey;
      Object.assign(record, fields);
      record.IsDummyRecord = true;

      this.addToReview(record);
      this.updateDataSource(record, undefined, true);

      this.showLoading(false);
      setTimeout(() => {
        var dataGrid = this.$refs["dx-data-grid-comp"].instance;
        var rowIndex = [];
        rowIndex.push(dataGrid.getRowIndexByKey(record.ID));

        if (rowIndex.length > 0) dataGrid.repaintRows(rowIndex);
      }, 50);
    },
    customizeLineCommentItem(item) {
      if (item.itemType == "simple") {
        item.editorOptions = item.editorOptions || {};
        if (item.dataField == "Comment") {
          const rowData = this.lineCommentItems.RowData;
          item.editorOptions.readOnly =
            this.baseView.mode == "review" ||
            (rowData && rowData.SalesUMSuppliedQty > 0);
        } else {
          item.editorOptions.disabled = this.baseView.mode == "review";
        }
      }
    },
    popupLineCommentBackButtonClick() {
      this.lineCommentsPopupVisible = false;

      var rowData = this.lineCommentItems.RowData;
      rowData.Comment = this.lineCommentModel.Comment;
      rowData.IsCommentVisible = this.lineCommentModel.IsCommentVisible;

      this.addToReview(rowData);
    },
    itemHistoryPopupHidden() {
      this.model.LineItemCode = undefined;
      this.$refs["itemHistoryGrid"].instance.clearFilter("search");

      this.itemHistoryPopupVisible = false;
    },
    itemHistoryPopupShown(e) {
      this.itemHistoryDataGridHeight = e.component.content().clientHeight - 60;
    },
    itemDetailsPopupHidden() {
      this.productDetails.imageURL = null;
      this.productDetailsPopupVisible = false;
    },
    itemHistoryToolbarPreparing(e) {
      e.toolbarOptions.items.unshift({
        location: "before",
        template: "displayDescription",
      });
    },
    itemHistoryAddGridNoResults(e) {
      let noDataMessageObject = {
        addNew: {
          class: undefined,
          title: "No History Lines",
          message:
            "Products despatched and invoiced to the customer will show here. Head over to the Despatch Order screen to despatch and invoice orders for this customer.",
        },
      };
      this.addGridNoResults(e, noDataMessageObject);
    },
    async getSalesPromotionItems() {
      await SalesOrderService.getSalesPromotionItems(
        "CustomerCode eq '" +
          this.toolbarModel.CustomerCode +
          "' and (StartDate le " +
          this.model.FilterDate +
          "T00:00:00Z" +
          " and EndDate ge " +
          this.model.FilterDate +
          "T00:00:00Z" +
          ")",
        [
          "CustomerCode",
          "PriceGroupType",
          "StartDate",
          "EndDate",
          "ItemCode",
          "PriceGroupID",
        ],
      ).then((res) => {
        if (res) this.promotionItems = res.data.value;
      });
    },
    async setItemPromotions(items) {
      if (
        this.model.BranchCode != undefined &&
        this.model.FilterDate != undefined
      ) {
        this.updatePromoItems(items, this.promotionItems);
        // let itemCodeChunks = chunkArray(items, 20);
        // var filterValues = "";

        // let promoPromiseArr = [];
        // for (let i = 0; i < itemCodeChunks.length; i++) {
        //   filterValues = getFilterExpressionForArray(
        //     "ItemCode",
        //     "OriginItemCode",
        //     itemCodeChunks[i]
        //   );

        //   var promoFilter =
        //     "(" +
        //     filterValues +
        //     ") and CustomerCode eq '" +
        //     this.toolbarModel.CustomerCode +
        //     "' and (StartDate le " +
        //     this.model.FilterDate +
        //     "T00:00:00Z" +
        //     " and EndDate ge " +
        //     this.model.FilterDate +
        //     "T00:00:00Z" +
        //     ")";

        //   promoPromiseArr.push(
        //     SalesOrderService.getSalesOrderPromotion(promoFilter)
        //   );
        // }

        // Promise.all(promoPromiseArr).then((results) => {
        //   var promoItems = [];
        //   results.forEach((result) => {
        //     var getData = result.data.value;
        //     for (let index = 0; index < getData.length; index++) {
        //       var getIndex = items.findIndex(
        //         (x) =>
        //           x.OriginItemCode == getData[index].ItemCode &&
        //           getData[index].PriceGroupType != "S"
        //       );
        //       if (getIndex > -1) {
        //         promoItems.push(items[getIndex]);
        //       }
        //     }
        //   });

        //   this.updatePromoItems(items, promoItems);
        // });
      }
    },
    updatePromoItems(items, promoItems) {
      promoItems.forEach((element) => {
        var index = this.productDataSourceItems._array.findIndex(
          (x) => x.OriginItemCode == element.ItemCode,
        );
        if (index != -1) {
          this.productDataSourceItems._array[index]["HasPromotion"] = true;
        }

        var cIndex = this.customerDataSourceItems._array.findIndex(
          (x) => x.OriginItemCode == element.ItemCode,
        );

        if (cIndex != -1) {
          this.customerDataSourceItems._array[cIndex]["HasPromotion"] = true;
        }
      });
      // }

      if (this.baseView.mode != "create") {
        items.forEach((element, elIndex) => {
          var index = promoItems.findIndex(
            (i) => i.OriginItemCode == element.OriginItemCode,
          );
          if (index != -1) {
            this.reviewDataList.push([
              {
                type: "update",
                data: {
                  HasPromotion: true,
                },
                key: items[elIndex].ID,
              },
            ]);
          }
        });
      }
    },
    async triggerPriceChange(label) {
      if (this.reviewDataList._array.length > 0) {
        if (this.allowPriceChangeTriggerConfirmation) {
          let confirmPriceChange = await this.$confirm(
            `Would you like to recalculate the sell price of all items based upon this new ${label}?`,
            "Price Change",
          );

          if (confirmPriceChange) {
            this.setSaleItemPriceAllLines();
          }
        } else {
          this.setSaleItemPriceAllLines();
        }
      }
    },
    setSaleItemPriceAllLines() {
      if (this.reviewDataList._array.length > 0) {
        var promises = [];
        for (var i = 0; i < this.reviewDataList._array.length; i++) {
          var record = this.reviewDataList._array[i];
          if (record.ItemCode)
            promises.push(
              SalesOrderV3Service.setSaleItemPrice(
                // REPRICE ALL
                record,
                record,
                this.model.SalesOrderTypeCode,
                this.model.BranchCode,
                this.toolbarModel.CustomerCode,
                this.model.ExchangeRate,
                this.model.DateIn,
                new Date(this.model.RequiredByDate),
                false,
              ),
            );
        }

        Promise.all(promises).then((data) => {
          data.forEach((element) => {
            this.addToReview(element);

            // if (this.soDetailsCurrentView != "Review") {
            var dataGrid = this.$refs["dx-data-grid-comp"].instance;
            setTimeout(() => {
              dataGrid.refresh(true).then(() => {});
            }, 500);
            // }
          });
        });
      }
    },
    onSalesUMRowClick(e, cellInfo) {
      this.popupHiddenCallBack(false);
      var rowIndex = cellInfo.rowIndex;
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      dataGrid.cellValue(rowIndex, "SalesUM", e.value);

      this.selectedRowData = undefined;

      setTimeout(() => {
        dataGrid.saveEditData();
      }, 10);
    },
    updateReviewList(data) {
      this.reviewDataList.push([
        {
          type: "update",
          data: data,
          key: data.ID,
        },
      ]);
    },
    applyStockFilter(e) {
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      var filters = [];

      if (e.value) {
        filters.push([["Available", ">", 0]]);
        dataGrid.filter(filters);
      } else {
        filters.push([["Available", "<>", 0], "or", ["Available", "=", 0]]);
        dataGrid.filter(filters);
      }
    },

    // GP Tool
    async applyGPToolValue() {
      var globalGPVal = parseFloat(getFactorByCode(571).Factorc);
      let rawGp = calculateGrossProfitPcntRaw(
        Math.round(this.gpTool.data.SalesUMStandardCost * 100) / 100,
        this.gpTool.model.newSalesUMNetPrice,
      );
      if (
        globalGPVal != 0 &&
        (this.gpTool.model.gp < 0 || this.gpTool.model.gp > 100)
      ) {
        this.displayValidationErrors([
          `GP % value should be between 99.99 and 0.00`,
        ]);
      } else if (rawGp < globalGPVal) {
        this.displayValidationErrors([
          `Cannot save New Price below the min GP % (` +
            globalGPVal.toFixed(2) +
            `)`,
        ]);
      } else {
        var dataGrid = this.$refs["dx-data-grid-comp"].instance;
        var rowIndex = this.gpTool.cellInfo.rowIndex;

        var SalesUMFXPrice = calculateSalesUMFXPriceFromSalesUMNetFXPrice(
          this.gpTool.model.newSalesUMNetPrice,
          this.gpTool.data.DiscPcnt,
        );

        dataGrid.cellValue(rowIndex, "SalesUMFXPrice", SalesUMFXPrice);
        dataGrid.cellValue(rowIndex, "gpprcnt", this.gpTool.model.gp);

        this.gpToolPopupVisible = false;
      }
    },
    gpPercentageChange() {
      this.gpTool.model.newSalesUMNetPrice = calculateSalesUMPriceForGP(
        Math.round(this.gpTool.model.standardSell * 100) / 100,
        Math.round(this.gpTool.data.SalesUMStandardCost * 100) / 100,
        this.gpTool.model.gp,
      );
    },
    async onGpValueChanged(e) {
      if (!e.event) return;

      if (e.value >= 100 || e.value < 0) {
        let res = await alert(
          "GP % must have a value between 0 and 100",
          "Invalid GP Value",
        );
        if (res) {
          this.gpTool.model.gp = e.previousValue;
          this.gpPercentageChange();
        }
      }
    },
    gpNewPriceChange() {
      this.gpTool.model.gp =
        this.gpTool.model.newSalesUMNetPrice == 0
          ? 0
          : (
              ((this.gpTool.model.newSalesUMNetPrice -
                Math.round(this.gpTool.data.SalesUMStandardCost * 100) / 100) /
                this.gpTool.model.newSalesUMNetPrice) *
              100
            ).roundNumber(2);
    },

    // Item Details
    async clickOnInfo(data) {
      this.showLoading(true);

      this.productDetails.data = { ...data.data };
      if (
        this.productDetails.data.SalesUMQty == 0 &&
        this.productDetails.data.HasPromotion
      ) {
        var newData = { SalesUMQty: 1 };
        var obj = { ...this.productDetails.data, ...newData };
        await SalesOrderV3Service.setSaleItemPrice(
          obj,
          obj,
          this.model.SalesOrderTypeCode,
          this.model.BranchCode,
          this.toolbarModel.CustomerCode,
          this.model.ExchangeRate,
          this.model.DateIn,
          this.model.RequiredByDate,
          false,
        ).then((res) => {
          this.productDetails.data = res;
        });
      }
      this.productDetails.model.itemCode = data.data.OriginItemCode;
      this.productDetails.model.salesUM = data.data.SalesUM;

      await this.getProductUnitOfMeasure(this.productDetails.data);

      if (this.productDetails.data.HasPrimaryImage) {
        let itemCode = data.data.OriginItemCode;
        await ApiService.get("/api/ProductPrimaryImage?itemCode=" + itemCode)
          .then((res) => {
            var src = "data:image;base64," + res.data.value;
            this.productDetails.imageURL = src;
          })
          .catch((err) => {
            if (err) this.showLoading(false);
          });
      }

      this.showLoading(false);
      this.productDetailsPopupVisible = true;
    },
    async getProductUnitOfMeasure(data) {
      var that = this;
      that.productDetails.hasPromotion = false;
      that.productDetails.model.promotionStartDate = null;
      that.productDetails.model.promotionEndDate = null;

      await SalesOrderProductDetails.getProductUnitOfMeasureByCode(
        data.OriginItemCode,
        this.model.BranchCode,
        data.SalesUM,
      )
        .then(async (result) => {
          if (
            data.HasPromotion &&
            data.PriceGroupCode != 0 &&
            data.PriceGroupCode != null
          ) {
            await PriceGroupService.getByCode(data.PriceGroupCode)
              .then((result1) => {
                that.productDetails.hasPromotion =
                  result1.data.value[0].DateControlled;
                that.productDetails.model.promotionStartDate =
                  result1.data.value[0].SellStartDate;
                that.productDetails.model.promotionEndDate =
                  result1.data.value[0].SellEndDate;
              })
              .catch((err) => {
                if (err) this.showLoading(false);
              });
          }

          this.productDetails.model.apn = result.data.value[0].Product.APN;
          this.productDetails.model.cartonQty =
            result.data.value[0].Product.PackSize;
          this.productDetails.model.standardSell =
            result.data.value[0].StandardSell;
          if (data.SalesUMQty == 0) {
            this.productDetails.model.customerSell =
              result.data.value[0].StandardSell;
          } else {
            this.productDetails.model.customerSell =
              this.productDetails.data.SalesUMPrice;
          }

          this.showLoading(false);
        })
        .catch((err) => {
          if (err) this.showLoading(false);
        });
    },

    // Available Stock

    stockLevelCallBack() {
      this.visibleStockLevels = !this.visibleStockLevels;
    },
    visibleStockLevel(item) {
      this.stockLevelItemData = {
        ItemCode: item.OriginItemCode,
        Description: item.Description,
      };
      this.visibleStockLevels = !this.visibleStockLevels;
    },

    // Filter side panel
    changeFilterPanelTab(e) {
      this.sidebarTabSelectedIndex = e.itemIndex;
    },
    setGridPanelFilter(filters, valueChanged) {
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;

      if (valueChanged != undefined || filters.length > 0) {
        this.forceRefresh = true;
        CommonStorage.saveItems(
          "filter-panel-" + this.getFilterPersistenceKey,
          JSON.stringify(filters), // , replacer
        );
        var finalFilter = [];
        if (this.getDefaultFilters) {
          if (filters.length > 0) {
            if (filters[filters.length - 1] != "and") filters.push("and");
            finalFilter = filters.concat(this.getDefaultFilters);
          } else finalFilter = this.getDefaultFilters;
        } else finalFilter = filters;

        if (finalFilter.length > 0) dataGrid.filter(finalFilter);
        else if (finalFilter.length == 0) dataGrid.clearFilter();

        if (filters.length == 0) {
          this.filterFormItems.forEach((element) => {
            if (element.filterDefaultValue) delete element.filterDefaultValue;
          });
          this.filterRefreshKey++;
        }
      }
      this.showLoading(false);
    },
    closeFilterPanel() {
      this.showRightContainer = !this.showRightContainer;
      this.sidebarTabSelectedIndex = 0;
      var item = document.getElementById("filter-btn");
      item.classList.remove("dx-state-invisible");

      setTimeout(() => {
        this.reSizeGrid();
      }, 50);
    },

    // Footer
    async setFooterItems(gridItems) {
      this.getFooterItems
        .filter((x) => (x.hasOwnProperty("visible") ? x.visible : true))
        .forEach((x) => {
          x.value = this.updateValue(x.dataField, gridItems);
          if (x.customCalc) x.value = x.customCalc(this.model, gridItems);
        });
      this.footerItems = this.getFooterItems;
      this.showFooter = true;
    },
    updateValue(dataField, items) {
      let result = 0;
      if (items.length > 0)
        items.forEach((x) => {
          if (x.CheckInclude || this.baseView.mode == "review")
            result += parseFloat(x[dataField]).roundNumber(4);
          else if (x.CheckInclude == undefined)
            result += parseFloat(x[dataField]).roundNumber(4);
        });

      return result;
    },
  },
  watch: {
    $route(to) {
      if (to.path == "/sales-order/create") {
        location.reload();
      }
    },
    customerCodeComputed: {
      immediate: true,
      handler: function (val) {
        var accountStatus = false;
        if (val) {
          accountStatus = true;
        }
        let toolbar = this.$refs["gc-form-header-toolbar"];
        if (toolbar) {
          setTimeout(() => {
            var items = toolbar.$refs["toolbar-header"].instance.option().items;
            items.forEach((element) => {
              if (element.options && element.options.icon == "more") {
                element.options.items.forEach((i) => {
                  if (i.menuText == "Account Status") {
                    i.disabled = !accountStatus;
                  }
                });
              }
            });
            toolbar.$refs["toolbar-header"].instance.option("items", items);
            toolbar.$refs["toolbar-header"].instance.repaint();
          }, 10);
        }
      },
    },
    getValidationSummaryVisible(value, prev_Value) {
      if (value == false && !this.model.CustomerCode) {
        let toolbar = this.$refs["gc-form-header-toolbar"];
        if (toolbar) {
          let headerToolBarForm =
            toolbar.$refs["toolbar-form"].$refs["gc-form-comp"].instance;
          setTimeout(() => {
            if (
              headerToolBarForm &&
              headerToolBarForm.getEditor("CustomerCode")
            )
              headerToolBarForm.getEditor("CustomerCode").instance().focus();
            headerToolBarForm.getEditor("CustomerCode").instance().open();
          }, 250);
        }
      }

      if (value == false && prev_Value == true) {
        setTimeout(() => {
          if (this.SelectedQTYCell != undefined && this.SelectedQTYCell != -1) {
            var dataGrid = this.$refs["dx-data-grid-comp"].instance;
            dataGrid.editCell(this.SelectedQTYCell, "SalesUMQty");
            setTimeout(() => {
              this.SelectedQTYCell = undefined;
            }, 100);
          }
        }, 50);
      }
    },
  },
};
</script>

<style lang="scss">
.touch-sales-order-layout {
  .dx-datagrid .dx-row-alt.dummy-product-row > td {
    background-color: unset !important;
  }
  .dx-scrollable-content {
    min-height: 100% !important;
  }
  .layout-body .dx-scrollable-wrapper .dx-scrollable-content {
    .dx-scrollview-content .content {
      //   padding: 10px;
      .touch-sales-order {
        background-color: white;
        // padding: 10px;
      }
    }

    .header-form-container {
      width: 100%;
      display: inline-flex;

      .header-form {
        width: calc((100% / 3) * 2);
        padding-right: 5px;
        margin-bottom: 0 !important;
      }

      .header-comments {
        width: calc((100% / 3) * 1);
        padding-left: 5px;
      }
    }

    .dx-datagrid .dx-row.dx-data-row.dx-row-lines .key-pad-focused-cell {
      background-color: #d1f7ffba !important;
    }

    // .dx-cell-modified {
    //   // background-color: unset !important;
    // }

    .advance-filter-panel {
      .dx-form .dx-scrollable-container {
        margin-left: 0;
        padding-left: 0;
        .dx-scrollable-content {
          .dx-layout-manager.dx-widget {
            padding-right: 0;
          }
        }
      }
    }
    .dx-icon-money::before {
      color: #20a4da;
    }

    .class-item-cell-template {
      cursor: pointer;
    }

    .gc-grid-footer-css-v2.t-so-foolter-h {
      position: absolute;
      bottom: 10px;
      width: calc(100% - 20px);
    }

    .dx-datagrid-rowsview {
      thead {
        display: none;
      }

      .dx-data-row.dx-row-lines
        .gc-editable-cell-color[role="gridcell"]:not(.cell-disabled) {
        background-color: #f8fdfe !important;
        // #d1f7ffba !important
      }
      // .edit-cell-textbox-field gc-editable-cell-color dx-editor-cell
      // edit-cell-textbox-field gc-editable-cell-color
      // .dx-row:not(.dx-row-alt){
      .dx-selection.dx-row:not(.dx-row-focused):not(.dx-row-alt) > td,
      .dx-selection.dx-row:not(.dx-row-focused):not(.dx-row-alt):hover > td {
        background-color: unset;
      }
      // }

      // .dx-row.dx-row-alt {
      .dx-selection.dx-row.dx-row-alt:not(.dx-row-focused)
        > td:not(.gc-editable-cell-color):not(.netprice-warn),
      .dx-selection.dx-row.dx-row-alt:not(.dx-row-focused):hover
        > td:not(.gc-editable-cell-color):not(.netprice-warn),
      .dx-selection.dx-row.dx-row-alt:not(.dx-row-focused) > td.cell-disabled,
      .dx-selection.dx-row.dx-row-alt:not(.dx-row-focused):hover
        > td.cell-disabled {
        background-color: #f5f5f5 !important;
        pointer-events: none;
      }
      // }

      .dx-data-row:not(.dx-row-alt).dx-state-hover:not(.dx-selection):not(
          .dx-row-inserted
        ):not(.dx-row-removed):not(.dx-edit-row):not(.dx-row-focused)
        > td:not(.dx-focused) {
        background-color: unset;
      }

      .dx-selection.dx-row.dx-row-alt:not(.dx-row-focused)
        > td.dx-cell-modified.netprice-warn,
      .dx-cell-modified.netprice-warn.dx-cell-focus-disabled {
        background-color: rgba(232, 56, 79, 0.1) !important;
        pointer-events: auto;
      }
    }
    .dx-datagrid
      .dx-datagrid-content
      .dx-datagrid-table
      .dx-row
      .dx-editor-cell {
      padding-left: 5px !important;
      padding-right: 5px !important;
    }
  }

  .css-v2.transaction-screen {
    background-color: unset !important;
  }

  .edit-cell-numberbox-field {
    .dx-numberbox.dx-texteditor:not(.dx-state-readonly)::after {
      content: "";
      border-bottom: unset !important;
      background-color: unset !important;
    }
    .dx-numberbox.dx-texteditor.dx-state-readonly {
      border-bottom-style: unset !important;
      border-bottom-width: unset !important;
    }
  }

  .qty-cell:not([role="columnheader"])[role="gridcell"] {
    border-left: 3px solid white !important;
    border-right: 3px solid white !important;
  }
  .dx-datagrid .dx-row-alt > td.dx-cell-modified {
    background-color: #f5f5f5 !important;
  }

  .pslip-status-cell {
    text-align: center !important;
    .pslip-status {
      font-size: 18px;
    }

    .grey {
      color: #c7c7c7;
    }

    .orange {
      color: #ffa500;
    }

    .green {
      color: #00c300;
    }
    .red {
      color: #e8384f;
    }
  }

  .dx-datagrid {
    .dx-row-alt .dx-cell-modified.netprice-warn,
    .dx-cell-modified.netprice-warn,
    .netprice-warn {
      background-color: rgba(232, 56, 79, 0.1) !important;
    }

    .dx-datagrid-rowsview
      .dx-selection.dx-row.dx-row-alt:not(.dx-row-focused)
      .netprice-warn[role="gridcell"]:not(.cell-disabled) {
      background-color: rgba(232, 56, 79, 0.1) !important;
    }
  }
}

.sales-order-layout.custom-auto-complete {
  display: flex;
  flex-direction: column;
}

.dx-device-tablet {
  .touch-sales-order-layout {
    .layout-body {
      .gc-header-toolbar .gc-form-header-toolbar {
        .view-toolbar.form-header-toolbar {
          .dx-toolbar-items-container {
            .dx-toolbar-before {
              padding-left: 12px;
            }
            .dx-toolbar-after {
              padding-right: 12px;
            }
          }
        }
      }
    }
  }
}

.popup-in-header {
  padding: 10px 10px 0 10px;
  > div {
    background-color: white;
    > div {
      padding: 10px;
    }
  }
}

.gc-popup-product-details {
  .product-details {
    max-width: 100%;
    max-height: 80vh;
    overflow: auto;
    position: relative;
    -ms-flex: 1 1 auto;
    flex: 1 1 auto;
    padding: 10px;

    .popup-in-header {
      padding: 0 !important;
      padding-bottom: 10px !important;
    }

    .details-row {
      // padding: 10px;
      background-color: white;
    }

    .row {
      align-items: center !important;
      display: flex;
      flex-wrap: wrap;

      .col-6 {
        flex: 0 0 50%;
        max-width: 50%;
        position: relative;
        width: 100%;
        min-height: 1px;
        padding-right: 15px;
        padding-left: 15px;

        input {
          text-align: right;
        }

        .product-primary-image-img {
          text-align: center;
          overflow: auto;

          .no-image {
            border: 1px solid #ece1e1;
            width: 140px;
            height: 140px;
            text-align: center;
            display: inline-flex;

            span {
              width: auto;
              margin: auto;
              display: block;
              float: none;
              max-width: 100%;
            }
          }
        }
      }
    }
  }
}

.toolbar-right-form {
  .dx-form {
    .dx-first-row {
      padding: 0 !important;
    }

    .available-stock-switch {
      .dx-field-item-content > div {
        margin-top: 0 !important;
      }
    }
  }
}

.exchange-rate {
  padding-right: 0 !important;
}

.sales-order-account-status {
  > .dx-popup-normal {
    border-radius: unset !important;
  }
}

.header-form-items {
  .dx-form-group {
    .dx-item > .dx-item-content > .dx-last-col {
      padding-bottom: unset !important;
    }
  }
}
</style>
